import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isLoggedIn, getUser, verifySession, getStudents, getTrainingDays, getTrainingDetails, getStudentExercises, getTrainingFull, getStudentTemplates, updateTraining, createTraining, moveTraining, deleteTraining, updateExercise, createExercise, getCurrentSubscription, getStudentStats, getExerciseProgress, getOverviewHeatmap, getCoachAlerts, getCoachTemplates, sendTemplateToStudent, scheduleProgramming, duplicateTrainingMultiple, getStudentScores, getTrainingPreviousPerfs, getStudentNote, saveStudentNote, sendMessage, getStudentExerciseOverview, getWeeklyReport, getCoachStudentCheckins, requestCheckin, getTemplateFolders, saveTemplateFolder, deleteTemplateFolder, assignTemplateToFolder, getLastExerciseSeries } from "../services/authService";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import CoachTopbar from "../components/coach/CoachTopbar";
import CoachSidebar from "../components/coach/CoachSidebar";
import CoachSearchModal from "../components/coach/CoachSearchModal";
import OverviewSkeleton from "../components/coach/OverviewSkeleton";
import StatsSkeleton from "../components/coach/StatsSkeleton";
import WeeklyReportSkeleton from "../components/coach/WeeklyReportSkeleton";
import Avatar from "../components/Avatar";
import TemplateFolderMenu from "../components/coach/TemplateFolderMenu";
import EditorShell from "../components/coach/editor/EditorShell";
import SeriesTable from "../components/coach/editor/SeriesTable";
import WarmupTable from "../components/coach/editor/WarmupTable";
import RestTimeField from "../components/coach/RestTimeField";
import TemplateSeriesControl from "../components/coach/editor/TemplateSeriesControl";
import CalendarPanel from "../components/coach/editor/panels/CalendarPanel";
import ExercisesPanel from "../components/coach/editor/panels/ExercisesPanel";
import TemplatesPanel from "../components/coach/editor/panels/TemplatesPanel";
import StatsPanel from "../components/coach/editor/panels/StatsPanel";
import { effectiveWeight, formatWeight } from "../utils/weight";
import ExerciseThumb from "../components/ExerciseThumb";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const MONTH_NAMES = [
	"Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin",
	"Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"
];

const DAY_NAMES = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Converts a Date (interpreted by its local Y/M/D components) to a timestamp at noon UTC.
// Storing calendar days at noon UTC keeps the chosen day stable regardless of the server's
// MySQL session timezone (works for any TZ between UTC-11 and UTC+11).
const toApiDateTimestamp = (date) =>
	Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);

// Hardcoded muscle labels (same order as in the mobile app)
const MUSCLE_LABELS = [
	"Pectoraux",
	"Epaules",
	"Dorsaux",
	"Trapezes",
	"Biceps",
	"Triceps",
	"Avant-bras",
	"Cou",
	"Fessiers",
	"Quadriceps",
	"Ischio-jambiers",
	"Mollets",
	"Abdominaux",
	"Lombaires",
	"Adducteurs",
	"Abducteurs"
];

// Helper function to get muscle names from muscles array
const getMuscleNames = (muscles) => {
	if (!muscles || muscles.length === 0) return [];
	return muscles
		.map((value, index) => (value === 1 ? MUSCLE_LABELS[index] : null))
		.filter(Boolean);
};

function CoachMode() {
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [students, setStudents] = useState([]);
	const [selectedStudent, setSelectedStudent] = useState(null);

	// Calendar state
	const [currentDate, setCurrentDate] = useState(new Date());
	const [trainingDays, setTrainingDays] = useState([]);
	const [loadingCalendar, setLoadingCalendar] = useState(false);
	const [selectedDay, setSelectedDay] = useState(null);
	const [trainingDetails, setTrainingDetails] = useState([]);
	const [loadingDetails, setLoadingDetails] = useState(false);

	// Exercises state
	const [exercises, setExercises] = useState([]);
	const [coachExercises, setCoachExercises] = useState([]);
	const [coachExercisesLoaded, setCoachExercisesLoaded] = useState(false);
	const [loadingExercises, setLoadingExercises] = useState(false);
	const [selectedExercise, setSelectedExercise] = useState(null);
	const [exerciseSearch, setExerciseSearch] = useState("");

	// Editor state
	const [editorTraining, setEditorTraining] = useState(null);
	const [loadingEditor, setLoadingEditor] = useState(false);
	const [editorMode, setEditorMode] = useState(null); // 'edit' or 'create'
	const [savingTraining, setSavingTraining] = useState(false);

	// Templates state
	const [templates, setTemplates] = useState([]);
	const [loadingTemplates, setLoadingTemplates] = useState(false);

	// Drag and drop state
	const [draggedExercise, setDraggedExercise] = useState(null);

	// Date picker modal state
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
	const [datePickerAction, setDatePickerAction] = useState(null); // 'addToCalendar', 'move', 'duplicate'
	const [selectedOverviewTraining, setSelectedOverviewTraining] = useState(null);

	// Template name modal state
	const [showTemplateNameModal, setShowTemplateNameModal] = useState(false);
	const [templateListTab, setTemplateListTab] = useState("student");
	const [templateSaveTarget, setTemplateSaveTarget] = useState("student");
	const [templateName, setTemplateName] = useState("");

	// Template folders (file explorer UI)
	const [templateFolders, setTemplateFolders] = useState([]);
	const [templateFolderAssoc, setTemplateFolderAssoc] = useState([]);
	const [activeFolderKey, setActiveFolderKey] = useState(null);
	const [newFolderName, setNewFolderName] = useState("");
	const [showNewFolderInput, setShowNewFolderInput] = useState(false);
	const [templateFoldersLoading, setTemplateFoldersLoading] = useState(false);

	// Exercise edit state
	const [exerciseEditMode, setExerciseEditMode] = useState(false);
	const [editingExerciseName, setEditingExerciseName] = useState("");
	const [editingExerciseDescription, setEditingExerciseDescription] = useState("");
	const [editingExerciseMuscles, setEditingExerciseMuscles] = useState([]);
	// Temps de repos par défaut en secondes (0 = pas défini, fallback 90s côté calcul durée)
	const [editingExerciseTempsRepos, setEditingExerciseTempsRepos] = useState(0);
	const [savingExercise, setSavingExercise] = useState(false);

	// Create exercise modal state
	const [showCreateExerciseModal, setShowCreateExerciseModal] = useState(false);
	const [newExerciseName, setNewExerciseName] = useState("");
	const [newExerciseDescription, setNewExerciseDescription] = useState("");
	const [newExerciseMuscles, setNewExerciseMuscles] = useState(MUSCLE_LABELS.map(() => 0));
	const [newExerciseTempsRepos, setNewExerciseTempsRepos] = useState(0);
	const [creatingExercise, setCreatingExercise] = useState(false);

	// Training comment modal state
	const [showCommentModal, setShowCommentModal] = useState(false);
	const [editingComment, setEditingComment] = useState("");

	// Subscription state
	const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);

	// Track if training has been modified
	const [trainingModified, setTrainingModified] = useState(false);

	// New shell navigation:
	// view ∈ "dashboard" | "weekly" | "templates" | "student"
	// studentTab (when view === "student") ∈ "stats" | "editor"  (notes/templates coming in phase B)
	const [view, setView] = useState("dashboard");
	const [studentTab, setStudentTab] = useState("stats");
	const [showSearch, setShowSearch] = useState(false);

	// Editor focus mode — active panel on the rail
	// editorPanel ∈ null | "templates" | "exercices" | "calendrier" | "stats"
	const [editorPanel, setEditorPanel] = useState(null);
	// Library mode: editing a coach-owned template (no student). The editor uses the
	// coach's own catalogue and the coach account as owner.
	const [libraryMode, setLibraryMode] = useState(false);
	// Tracks last save / modification time for the footer "Modifié il y a X" label
	const [editorLastModifiedAt, setEditorLastModifiedAt] = useState(null);
	// Custom loading message displayed by the editor body (templates, batch loads…)
	const [editorLoadingMessage, setEditorLoadingMessage] = useState("");

	// Derive the legacy `activeTab` from view + studentTab so existing JSX keeps working.
	const activeTab = (() => {
		if (libraryMode) return "editeur";
		if (view === "dashboard") return "vue-ensemble";
		if (view === "weekly") return "rapport-hebdo";
		if (view === "templates") return "coach-templates-global";
		if (view === "student") {
			if (studentTab === "editor") return "editeur";
			return "statistiques";
		}
		return "vue-ensemble";
	})();

	// Setter shim: lets the existing in-page handlers that called setActiveTab keep working.
	const setActiveTab = (legacy) => {
		if (legacy === "vue-ensemble") setView("dashboard");
		else if (legacy === "rapport-hebdo") setView("weekly");
		else if (legacy === "coach-templates") setView("templates");
		else if (legacy === "statistiques") { setView("student"); setStudentTab("stats"); }
		else if (legacy === "editeur") { setView("student"); setStudentTab("editor"); }
		else if (legacy === "rapport-eleve") { setView("student"); setStudentTab("stats"); }
	};

	// Multi-date duplication
	const [multiSelectMode, setMultiSelectMode] = useState(false);
	const [selectedDates, setSelectedDates] = useState([]);
	const [duplicateSourceTraining, setDuplicateSourceTraining] = useState(null);

	// Planification
	const [showPlanification, setShowPlanification] = useState(false);
	const [planSelectedTemplate, setPlanSelectedTemplate] = useState(null);
	const [planTemplateSource, setPlanTemplateSource] = useState("coach");
	const [coachTemplates, setCoachTemplates] = useState([]);
	const [planDays, setPlanDays] = useState([false, false, false, false, false, false, false]);
	const [planWeeks, setPlanWeeks] = useState(4);
	const [planStartDate, setPlanStartDate] = useState(new Date().toISOString().split('T')[0]);
	const [planSaveAsTemplate, setPlanSaveAsTemplate] = useState(false);
	const [planLoading, setPlanLoading] = useState(false);

	// Is the selected student actually the coach themselves?
	const isSelf = selectedStudent && user && selectedStudent.id === user.id;

	// Stats tab
	const [statsData, setStatsData] = useState(null);
	const [statsPeriod, setStatsPeriod] = useState(30);
	const [statsLoading, setStatsLoading] = useState(false);
	const [progressionAggregation, setProgressionAggregation] = useState("week");
	const [exerciseProgressData, setExerciseProgressData] = useState(null);
	const [selectedProgressExercise, setSelectedProgressExercise] = useState(null);
	const [exerciseProgressData2, setExerciseProgressData2] = useState(null);
	const [selectedProgressExercise2, setSelectedProgressExercise2] = useState(null);
	const [exerciseProgressPeriod2, setExerciseProgressPeriod2] = useState(90);

	// Notification banner
	const [notification, setNotification] = useState(null);
	const showNotif = (msg, type = 'success') => {
		setNotification({ msg, type });
		setTimeout(() => setNotification(null), 3500);
	};

	// Overview tab
	const [heatmapData, setHeatmapData] = useState(null);
	const [studentScores, setStudentScores] = useState(null);
	const [scoresSortBy, setScoresSortBy] = useState("regularity");
	const [scoresSortAsc, setScoresSortAsc] = useState(true);
	const [alertsData, setAlertsData] = useState(null);
	const [overviewLoading, setOverviewLoading] = useState(false);
	const [selectedHeatmapDay, setSelectedHeatmapDay] = useState(null);
	const [heatmapWeekOffset, setHeatmapWeekOffset] = useState(0);
	const [overviewFilterStudent, setOverviewFilterStudent] = useState(null);
	const [overviewPeriod, setOverviewPeriod] = useState(90);
	const [trainingDetailModal, setTrainingDetailModal] = useState(null); // { studentName, training, previousTraining }
	const [trainingDetailLoading, setTrainingDetailLoading] = useState(false);

	// Coach notes & goals per student
	const [coachNote, setCoachNote] = useState("");
	const [coachNoteGoal, setCoachNoteGoal] = useState(0);
	const [coachNoteLoading, setCoachNoteLoading] = useState(false);
	const [coachNoteSaving, setCoachNoteSaving] = useState(false);
	const [checkinStatus, setCheckinStatus] = useState("idle"); // idle | sending | sent | error

	// Feature 4 — Template banner
	const [loadedFromTemplate, setLoadedFromTemplate] = useState(false);
	const [loadedTemplateName, setLoadedTemplateName] = useState("");

	// Feature 6 — Broadcast message
	const [showBroadcast, setShowBroadcast] = useState(false);
	const [broadcastMessage, setBroadcastMessage] = useState("");

	// Feature 9 — Exercise overview per student
	const [exerciseOverview, setExerciseOverview] = useState(null);
	const [exerciseOverviewLoading, setExerciseOverviewLoading] = useState(false);

	// Feature 1 — Weekly report
	const [weeklyReport, setWeeklyReport] = useState(null);
	const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
	const [weeklyReportError, setWeeklyReportError] = useState(null);
	const [weekOffset, setWeekOffset] = useState(0);

	// Feature 5 — Student exportable report
	const [studentCheckins, setStudentCheckins] = useState(null);
	const [studentCheckinsLoading, setStudentCheckinsLoading] = useState(false);

	useEffect(() => {
		const checkSession = async () => {
			if (!isLoggedIn()) {
				navigate("/login");
				return;
			}

			const isValid = await verifySession();
			if (!isValid) {
				navigate("/login");
				return;
			}

			setUser(getUser());

			// Check subscription — dashboard requires Pro tier (idAbonnementType 4 or 6)
			const subResult = await getCurrentSubscription();
			if (subResult.success && subResult.subscription) {
				const proTierIds = [4, 5, 6]; // Pro Mensuel, Testeur, Pro Annuel
				if (!proTierIds.includes(subResult.subscription.idAbonnementType)) {
					setSubscriptionBlocked(true);
					setLoading(false);
					return;
				}
			} else {
				setSubscriptionBlocked(true);
				setLoading(false);
				return;
			}

			// Fetch students
			const result = await getStudents();
			if (result.success) {
				setStudents(result.students);
			}

			setLoading(false);
		};

		checkSession();
	}, [navigate]);

	// Fetch training days when student or month changes
	useEffect(() => {
		const fetchTrainingDays = async () => {
			if (!selectedStudent) {
				setTrainingDays([]);
				return;
			}

			setLoadingCalendar(true);
			setSelectedDay(null);
			setTrainingDetails([]);
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

			const result = await getTrainingDays(selectedStudent.id, year, month);
			if (result.success) {
				setTrainingDays(result.trainingDays);
			} else {
				setTrainingDays([]);
			}
			setLoadingCalendar(false);
		};

		fetchTrainingDays();
	}, [selectedStudent, currentDate]);

	// Fetch training details when a day is selected
	useEffect(() => {
		const fetchTrainingDetails = async () => {
			if (!selectedStudent || !selectedDay) {
				setTrainingDetails([]);
				return;
			}

			setLoadingDetails(true);
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth() + 1;

			const result = await getTrainingDetails(selectedStudent.id, year, month, selectedDay);
			if (result.success) {
				setTrainingDetails(result.trainings);
			} else {
				setTrainingDetails([]);
			}
			setLoadingDetails(false);
		};

		fetchTrainingDetails();
	}, [selectedStudent, selectedDay, currentDate]);

	// Fetch exercises when student changes
	useEffect(() => {
		const fetchExercises = async () => {
			if (!selectedStudent) {
				setExercises([]);
				return;
			}

			setLoadingExercises(true);
			const result = await getStudentExercises(selectedStudent.id);
			if (result.success) {
				setExercises(result.exercises);
			} else {
				setExercises([]);
			}
			setLoadingExercises(false);
		};

		fetchExercises();
	}, [selectedStudent]);

	// Fetch the coach's own exercise library + template folders once
	// (used by the "import from my library" flows in the editor panels).
	useEffect(() => {
		if (!user || !user.id || coachExercisesLoaded) return;
		getStudentExercises(user.id).then((res) => {
			if (res.success) setCoachExercises(res.exercises || []);
			setCoachExercisesLoaded(true);
		});
		getTemplateFolders().then((res) => {
			if (res.success) {
				setTemplateFolders(res.folders);
				setTemplateFolderAssoc(res.associations);
			}
		});
	}, [user, coachExercisesLoaded]);

	// Import an exercise from the coach's library into the selected student's catalogue.
	// Matches by name (trim + lowercase, strict). Does not add it to the current session (B2).
	const handleImportExerciseToStudent = async (coachExo) => {
		if (!selectedStudent) return { ok: false };
		const normalize = (s) => (s || "").trim().toLowerCase();
		const name = coachExo.nomExercice || coachExo.nom || "";
		const exists = (exercises || []).some((e) => normalize(e.nomExercice) === normalize(name));
		if (exists) {
			showNotif(`« ${name} » est déjà dans le catalogue de ${selectedStudent.prenom}`, "success");
			return { ok: true, already: true };
		}
		const created = await createExercise(
			selectedStudent.id,
			name,
			coachExo.comExercice || coachExo.description || "",
			coachExo.muscles || [],
		);
		if (!created.success) {
			showNotif(`Échec de l'import de « ${name} »`, "error");
			return { ok: false };
		}
		const newEntry = {
			id: created.id,
			nomExercice: name,
			comExercice: coachExo.comExercice || "",
			muscles: coachExo.muscles || [],
		};
		setExercises((prev) => [...prev, newEntry]);
		showNotif(`« ${name} » importé dans le catalogue de ${selectedStudent.prenom}`, "success");
		return { ok: true };
	};

	// Fetch templates when student changes
	useEffect(() => {
		const fetchTemplates = async () => {
			if (!selectedStudent) {
				setTemplates([]);
				setCoachTemplates([]);
				return;
			}

			setLoadingTemplates(true);
			const [studentRes, coachRes] = await Promise.all([
				getStudentTemplates(selectedStudent.id),
				getCoachTemplates()
			]);
			setTemplates(studentRes.success ? studentRes.templates : []);
			setCoachTemplates(coachRes.success ? (coachRes.data?.templates || []) : []);
			setLoadingTemplates(false);
		};

		fetchTemplates();
	}, [selectedStudent]);

	// Sidebar: pick a student → switch to student view (stats tab by default)
	const handleSelectStudentFromSidebar = (student) => {
		if (!student) return;
		const isSelfSel = user && student.id === user.id;
		const targetId = isSelfSel ? user.id : student.id;

		// Switching to a different student: silently discard any open training from the previous student
		// (no confirmation here — the editor will reopen empty for the new student).
		if (selectedStudent && selectedStudent.id !== targetId) {
			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			setLoadedFromTemplate(false);
			setLoadedTemplateName("");
			setEditorPanel(null);
		}

		const normalized = isSelfSel
			? { id: user.id, prenom: user.prenom, nom: user.nom, photoProfil: user.photoProfil }
			: student;
		setSelectedStudent(normalized);
		setSelectedDay(null);
		setTrainingDetails([]);
		if (isSelfSel) {
			setTemplateListTab("coach");
			setTemplateSaveTarget("coach");
			setPlanTemplateSource("coach");
		}
		if (multiSelectMode) cancelMultiDateDuplicate();
		setLibraryMode(false);
		setView("student");
		setStudentTab("stats");
	};

	// Sidebar: nav item click
	const handleSelectView = (next) => {
		if (multiSelectMode) cancelMultiDateDuplicate();
		setLibraryMode(false);
		setEditorTraining(null);
		setEditorMode(null);
		setTrainingModified(false);
		setEditorPanel(null);
		setView(next);
	};

	// Hover quick-actions on a student
	const handleStudentMessage = (student) => {
		if (!student || student.isSelf) return;
		window.dispatchEvent(new CustomEvent("open-messaging", {
			detail: {
				partnerId: student.id,
				partnerPrenom: student.prenom,
				partnerNom: student.nom,
				partnerPhoto: student.photoProfil || null,
			},
		}));
	};

	const handleStudentNote = (student) => {
		if (!student) return;
		handleSelectStudentFromSidebar(student);
		setStudentTab("notes");
	};

	const handleStudentPlan = (student) => {
		if (!student) return;
		handleSelectStudentFromSidebar(student);
		setStudentTab("editor");
		openPlanification();
	};

	// Fetch student scores once we have students, so the sidebar shows badges immediately.
	useEffect(() => {
		if (!user || subscriptionBlocked) return;
		if (studentScores || overviewLoading) return;
		(async () => {
			const res = await getStudentScores(overviewPeriod);
			if (res.success) setStudentScores(res.data?.students || []);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, subscriptionBlocked]);

	// ⌘K / Ctrl+K opens the search modal
	useEffect(() => {
		const onKey = (e) => {
			if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
				e.preventDefault();
				setShowSearch(true);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	// When entering a student view, drive overviewFilterStudent so the coach-note
	// fetch (and dependent panels) target that student.
	useEffect(() => {
		if (view === "student" && selectedStudent) {
			if (overviewFilterStudent !== selectedStudent.id) {
				setOverviewFilterStudent(selectedStudent.id);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [view, selectedStudent]);

	// Templates view: load coach templates + folders standalone (no student selected)
	useEffect(() => {
		if (view !== "templates") return;
		(async () => {
			setLoadingTemplates(true);
			const [tplRes, foldersRes] = await Promise.all([
				getCoachTemplates(),
				getTemplateFolders(),
			]);
			if (tplRes.success) setCoachTemplates(tplRes.data?.templates || []);
			if (foldersRes.success) {
				setTemplateFolders(foldersRes.folders);
				setTemplateFolderAssoc(foldersRes.associations);
			}
			setLoadingTemplates(false);
		})();
	}, [view]);

	const handleDayClick = (day) => {
		if (multiSelectMode) {
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth();
			const dateTs = Date.UTC(year, month, day, 12, 0, 0);
			toggleDateSelection(dateTs);
		} else {
			setSelectedDay(day);
		}
	};

	const handleExerciseClick = (exercise) => {
		setSelectedExercise(exercise);
	};

	const closeExerciseModal = () => {
		setSelectedExercise(null);
		setExerciseEditMode(false);
	};

	const startEditExercise = () => {
		if (!selectedExercise) return;
		setEditingExerciseName(selectedExercise.nomExercice || "");
		setEditingExerciseDescription(selectedExercise.comExercice || "");
		// Ensure muscles array has 16 elements (0 or 1 for each muscle)
		const muscles = selectedExercise.muscles || [];
		const normalizedMuscles = MUSCLE_LABELS.map((_, index) => muscles[index] || 0);
		setEditingExerciseMuscles(normalizedMuscles);
		setEditingExerciseTempsRepos(parseInt(selectedExercise.tempsRepos, 10) || 0);
		setExerciseEditMode(true);
	};

	const cancelEditExercise = () => {
		setExerciseEditMode(false);
	};

	const toggleMuscle = (index) => {
		const newMuscles = [...editingExerciseMuscles];
		newMuscles[index] = newMuscles[index] === 1 ? 0 : 1;
		setEditingExerciseMuscles(newMuscles);
	};

	const saveExerciseChanges = async () => {
		if (!selectedExercise || !selectedStudent) return;

		setSavingExercise(true);

		const result = await updateExercise(
			selectedStudent.id,
			selectedExercise.id,
			editingExerciseName,
			editingExerciseDescription,
			editingExerciseMuscles,
			editingExerciseTempsRepos
		);

		setSavingExercise(false);

		if (result.success) {
			// Update the exercise in local state
			const updatedExercises = exercises.map((ex) => {
				if (ex.id === selectedExercise.id) {
					return {
						...ex,
						nomExercice: editingExerciseName,
						comExercice: editingExerciseDescription,
						muscles: editingExerciseMuscles,
						tempsRepos: editingExerciseTempsRepos,
					};
				}
				return ex;
			});
			setExercises(updatedExercises);

			// Update selectedExercise to reflect changes
			setSelectedExercise({
				...selectedExercise,
				nomExercice: editingExerciseName,
				comExercice: editingExerciseDescription,
				muscles: editingExerciseMuscles,
				tempsRepos: editingExerciseTempsRepos,
			});

			setExerciseEditMode(false);
			showNotif("Exercice modifié avec succès");
		} else {
			showNotif("Erreur lors de la modification: " + result.error, 'error');
		}
	};

	const openCreateExerciseModal = () => {
		setNewExerciseName("");
		setNewExerciseDescription("");
		setNewExerciseMuscles(MUSCLE_LABELS.map(() => 0));
		setNewExerciseTempsRepos(0);
		setShowCreateExerciseModal(true);
	};

	const closeCreateExerciseModal = () => {
		setShowCreateExerciseModal(false);
	};

	const toggleNewExerciseMuscle = (index) => {
		const newMuscles = [...newExerciseMuscles];
		newMuscles[index] = newMuscles[index] === 1 ? 0 : 1;
		setNewExerciseMuscles(newMuscles);
	};

	const openCommentModal = () => {
		setEditingComment(editorTraining?.commentaire || "");
		setShowCommentModal(true);
	};

	const closeCommentModal = () => {
		setShowCommentModal(false);
	};

	const saveComment = () => {
		if (!editorTraining) return;
		setEditorTraining({
			...editorTraining,
			commentaire: editingComment
		});
		setTrainingModified(true);
		setShowCommentModal(false);
	};

	const handleCreateExercise = async () => {
		const targetUserId = libraryMode ? user?.id : selectedStudent?.id;
		if (!targetUserId || !newExerciseName.trim()) return;

		setCreatingExercise(true);

		const result = await createExercise(
			targetUserId,
			newExerciseName.trim(),
			newExerciseDescription,
			newExerciseMuscles,
			newExerciseTempsRepos
		);

		setCreatingExercise(false);

		if (result.success) {
			// Add the new exercise to local state
			const newExercise = {
				id: result.id,
				nomExercice: newExerciseName.trim(),
				comExercice: newExerciseDescription,
				muscles: newExerciseMuscles,
				tempsRepos: newExerciseTempsRepos,
			};
			const sortByName = (a, b) => a.nomExercice.localeCompare(b.nomExercice);
			if (libraryMode) {
				setCoachExercises([...coachExercises, newExercise].sort(sortByName));
			} else {
				setExercises([...exercises, newExercise].sort(sortByName));
			}
			setShowCreateExerciseModal(false);
			showNotif("Exercice créé avec succès");
		} else {
			showNotif(result.error, 'error');
		}
	};

	const handleAddToSession = () => {
		const now = Date.now();
		const newExercise = {
			id: -1, // New exercise, not yet saved
			localId: now, // Local identifier like in the mobile app
			nom: selectedExercise.nomExercice,
			idExercice: selectedExercise.id,
			supprime: 0,
			series: [
				{ id: -1, reps: 0, poids: 0, poidsDec: 0, rpe: 0 },
				{ id: -1, reps: 0, poids: 0, poidsDec: 0, rpe: 0 }
			]
		};

		if (!editorTraining) {
			// Create a new training if none is open
			setEditorTraining({
				id: null,
				nom: "Nouvelle séance",
				date: Date.now(),
				estTemplate: false,
				exercices: [newExercise]
			});
			setEditorMode('create');
		} else {
			// Add to existing training
			const updated = { ...editorTraining };
			updated.exercices.push(newExercise);
			setEditorTraining({ ...updated });
		}
		setTrainingModified(true);
		closeExerciseModal();
	};

	const openInEditor = async (trainingId, isTemplate = false) => {
		if (!selectedStudent) return;

		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}

		setLoadingEditor(true);
		const result = await getTrainingFull(selectedStudent.id, trainingId);
		if (result.success) {
			// Deep copy to make it editable
			const training = JSON.parse(JSON.stringify(result.training));

			// Add supprime and localId fields to each exercise
			training.exercices = training.exercices.map((ex) => ({
				...ex,
				supprime: 0,
				localId: ex.id // Use db id as localId for existing exercises
			}));

			// Mark if this is a template
			training.estTemplate = isTemplate;

			setEditorTraining(training);
			// Use 'editTemplate' for existing templates, 'edit' for trainings
			setEditorMode(isTemplate ? 'editTemplate' : 'edit');
			setTrainingModified(false);
			// Feature 4 — template banner: not shown here (editing template itself, not a copy)
			setLoadedFromTemplate(false);
			setLoadedTemplateName("");
		}
		setLoadingEditor(false);
	};

	// Feature 4 — Load a template as a new session (copy)
	// Works with both student templates (camelCase id/nom) and coach templates (PascalCase Id/Nom).
	// For coach templates: resolves each exercise against the student's catalogue by name
	// (case-insensitive + trim, strict equality), creating missing ones in the student catalogue.
	// All-or-nothing: if any creation fails, the operation aborts without touching the editor.
	// After resolution, pre-fills each series' weight with the student's last performed weight
	// on that exercise (per series position, fallback to last available); reps stay at 0.
	const openTemplateAsNewSession = async (template) => {
		if (!selectedStudent) return;
		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}
		const templateId = template.Id ?? template.id;
		const templateNom = template.Nom ?? template.nom ?? "Template";
		const isCoachTemplate = template.Id !== undefined;
		const ownerId = isCoachTemplate ? user.id : selectedStudent.id;

		setLoadingEditor(true);
		setEditorLoadingMessage("Chargement du template…");

		try {
			const result = await getTrainingFull(ownerId, templateId);
			if (!result.success) {
				showNotif("Erreur lors du chargement du template", "error");
				return;
			}

			const training = JSON.parse(JSON.stringify(result.training));
			const templateExos = (training.exercices || []);

			// Resolve coach template exercises against student catalogue.
			let studentCatalog = exercises || [];
			if (isCoachTemplate) {
				setEditorLoadingMessage("Préparation des exercices côté élève…");

				const normalize = (s) => (s || "").trim().toLowerCase();
				const catByName = new Map();
				studentCatalog.forEach((e) => {
					const k = normalize(e.nomExercice);
					if (k) catByName.set(k, e);
				});

				// Identify which exos need to be created (deduped by name).
				const toCreateByName = new Map();
				for (const ex of templateExos) {
					const name = ex.nom || ex.nomExercice;
					if (!name) continue;
					const k = normalize(name);
					if (!catByName.has(k) && !toCreateByName.has(k)) {
						toCreateByName.set(k, {
							nom: name,
							description: ex.comExercice || ex.description || "",
							muscles: ex.muscles || [],
						});
					}
				}

				const newCatalogEntries = [];
				if (toCreateByName.size > 0) {
					setEditorLoadingMessage(`Création de ${toCreateByName.size} exercice${toCreateByName.size > 1 ? "s" : ""} dans le catalogue de l'élève…`);

					for (const item of toCreateByName.values()) {
						const created = await createExercise(
							selectedStudent.id,
							item.nom,
							item.description,
							item.muscles,
						);
						if (!created.success) {
							showNotif(
								`Échec de la création de l'exercice « ${item.nom} » dans le catalogue de l'élève. La séance n'a pas été chargée.`,
								"error",
							);
							return; // abort all-or-nothing
						}
						newCatalogEntries.push({
							id: created.id,
							nomExercice: item.nom,
							comExercice: item.description,
							muscles: item.muscles,
						});
					}

					// Refresh local catalogue and rebuild map.
					studentCatalog = [...studentCatalog, ...newCatalogEntries];
					setExercises(studentCatalog);
					newCatalogEntries.forEach((e) => catByName.set(normalize(e.nomExercice), e));
				}

				// Now remap each template exo's idExercice to student catalogue id.
				for (const ex of templateExos) {
					const name = ex.nom || ex.nomExercice;
					const ref = catByName.get(normalize(name));
					if (ref) ex.idExercice = ref.id;
				}
			}

			// Fetch last-perf series for each exo (unique student-side ids).
			setEditorLoadingMessage("Récupération des dernières charges de l'élève…");
			const uniqueIds = Array.from(
				new Set(templateExos.map((ex) => ex.idExercice).filter((v) => v != null && v > 0)),
			);
			let lastByExoId = new Map();
			if (uniqueIds.length > 0) {
				const lastRes = await getLastExerciseSeries(selectedStudent.id, uniqueIds);
				if (lastRes.success) {
					(lastRes.data || []).forEach((entry) => {
						if (entry?.exerciseId != null) lastByExoId.set(entry.exerciseId, entry.series || []);
					});
				}
			}

			// Build the new session: same structure as template, weights from last-perf, reps = 0.
			training.exercices = templateExos.map((ex, exIdx) => {
				const lastSeries = lastByExoId.get(ex.idExercice) || [];
				const templateSeries = ex.series || [];
				const newSeries = templateSeries.map((templateSerie, sIdx) => {
					let poids = 0;
					let poidsDec = 0;
					if (lastSeries.length > 0) {
						const matched = lastSeries[sIdx] || lastSeries[lastSeries.length - 1]; // B2 fallback
						if (matched) {
							poids = matched.poids ?? 0;
							poidsDec = matched.poidsDec ?? 0;
						}
					}
					return {
						id: -1,
						reps: 0,
						poids,
						poidsDec,
						rpe: 0,
						ordre: sIdx,
					};
				});
				return {
					...ex,
					supprime: 0,
					localId: Date.now() + exIdx, // unique numeric local id
					id: -1,
					series: newSeries.length > 0 ? newSeries : [{ id: -1, reps: 0, poids: 0, poidsDec: 0, rpe: 0, ordre: 0 }],
				};
			});

			training.id = null;
			training.estTemplate = false;
			training.date = Date.now();
			training.nom = templateNom;

			setEditorTraining(training);
			setEditorMode("create");
			setTrainingModified(true);
			setLoadedFromTemplate(true);
			setLoadedTemplateName(templateNom);
		} catch (err) {
			console.error("openTemplateAsNewSession error:", err);
			showNotif("Erreur inattendue lors du chargement du template", "error");
		} finally {
			setLoadingEditor(false);
			setEditorLoadingMessage("");
		}
	};

	// Resolve a list of template exercises against the selected student's catalogue.
	// Matches by name (trim + lowercase, strict), creates missing ones. All-or-nothing.
	// Returns { ok: true, exos } (exos with student-side idExercice) or { ok: false, error }.
	const resolveExosForStudent = async (templateExos) => {
		const normalize = (s) => (s || "").trim().toLowerCase();
		let studentCatalog = exercises || [];
		const catByName = new Map();
		studentCatalog.forEach((e) => {
			const k = normalize(e.nomExercice);
			if (k) catByName.set(k, e);
		});

		const toCreateByName = new Map();
		for (const ex of templateExos) {
			const name = ex.nom || ex.nomExercice;
			if (!name) continue;
			const k = normalize(name);
			if (!catByName.has(k) && !toCreateByName.has(k)) {
				toCreateByName.set(k, {
					nom: name,
					description: ex.comExercice || ex.description || "",
					muscles: ex.muscles || [],
				});
			}
		}

		const newEntries = [];
		for (const item of toCreateByName.values()) {
			const created = await createExercise(selectedStudent.id, item.nom, item.description, item.muscles);
			if (!created.success) {
				return { ok: false, error: `Échec de la création de l'exercice « ${item.nom} »` };
			}
			newEntries.push({ id: created.id, nomExercice: item.nom, comExercice: item.description, muscles: item.muscles });
		}
		if (newEntries.length > 0) {
			studentCatalog = [...studentCatalog, ...newEntries];
			setExercises(studentCatalog);
			newEntries.forEach((e) => catByName.set(normalize(e.nomExercice), e));
		}

		const resolved = templateExos.map((ex) => {
			const name = ex.nom || ex.nomExercice;
			const ref = catByName.get(normalize(name));
			return { ...ex, idExercice: ref ? ref.id : ex.idExercice };
		});
		return { ok: true, exos: resolved };
	};

	// Import one of the coach's templates into the selected student's template library.
	// Resolves exercises against the student catalogue (creating missing), then creates a
	// student-side template keeping the coach template's prescribed weights/reps.
	const handleImportTemplateToStudent = async (coachTemplate) => {
		if (!selectedStudent) return { ok: false };
		const normalize = (s) => (s || "").trim().toLowerCase();
		const templateId = coachTemplate.Id ?? coachTemplate.id;
		const templateNom = coachTemplate.Nom ?? coachTemplate.nom ?? "Template";

		const exists = (templates || []).some((t) => normalize(t.nom ?? t.Nom) === normalize(templateNom));
		if (exists) {
			showNotif(`Un template « ${templateNom} » existe déjà chez ${selectedStudent.prenom}`, "success");
			return { ok: true, already: true };
		}

		const full = await getTrainingFull(user.id, templateId);
		if (!full.success) {
			showNotif("Erreur lors du chargement du template", "error");
			return { ok: false };
		}
		const templateExos = full.training.exercices || [];

		const resolved = await resolveExosForStudent(templateExos);
		if (!resolved.ok) {
			showNotif(`${resolved.error}. Import annulé.`, "error");
			return { ok: false };
		}

		let ordre = 0;
		const exercicesForApi = resolved.exos.map((ex) => ({
			Id: -1,
			LocalId: Date.now() + ordre,
			IdExercice: ex.idExercice,
			Supprime: 0,
			Ordre: ordre++,
			ListSerie: (ex.series || []).map((s) => ({ reps: s.reps || 0, poids: s.poids || 0, poidsDec: s.poidsDec || 0, rpe: s.rpe || 0 })),
			ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({ reps: w.reps || 0, poids: w.poids || 0, poidsDec: w.poidsDec || 0 })),
		}));

		const created = await createTraining(selectedStudent.id, templateNom, Date.now(), true, exercicesForApi);
		if (!created.success) {
			showNotif("Erreur lors de l'import du template", "error");
			return { ok: false };
		}

		const tpl = await getStudentTemplates(selectedStudent.id);
		if (tpl.success) setTemplates(tpl.templates);
		showNotif(`Template « ${templateNom} » importé dans la bibliothèque de ${selectedStudent.prenom}`, "success");
		return { ok: true };
	};

	const createNewTraining = () => {
		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}

		setEditorTraining({
			id: null,
			nom: "Nouvelle séance",
			date: Date.now(),
			estTemplate: false,
			exercices: []
		});
		setEditorMode('create');
		setTrainingModified(false);
		// Feature 4 — reset template banner
		setLoadedFromTemplate(false);
		setLoadedTemplateName("");
	};

	const removeExercice = (exerciceIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };

		// Get the actual exercise (accounting for hidden deleted ones)
		const visibleExercises = updated.exercices.filter((ex) => ex.supprime !== 1);
		const exerciseToRemove = visibleExercises[exerciceIndex];

		// Find its real index in the full array
		const realIndex = updated.exercices.findIndex(
			(ex) => (ex.localId && ex.localId === exerciseToRemove.localId) || (ex.id > 0 && ex.id === exerciseToRemove.id)
		);

		if (realIndex !== -1) {
			// Mark as deleted instead of removing
			updated.exercices[realIndex].supprime = 1;
			setEditorTraining({ ...updated });
			setTrainingModified(true);
		}
	};

	// True when the drag was started from a superset HEADER handle ≡: in that case
	// we move the whole group together. When false, a member's own card handle is
	// dragging it — we reorder only that single exo (and lock it within the group).
	const [draggedAsGroup, setDraggedAsGroup] = useState(false);

	const handleDragStart = (e, visibleIndex) => {
		setDraggedExercise(visibleIndex);
		setDraggedAsGroup(false);
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleGroupDragStart = (e, firstMemberIdx) => {
		setDraggedExercise(firstMemberIdx);
		setDraggedAsGroup(true);
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragOver = (e, visibleIndex) => {
		e.preventDefault();
		if (draggedExercise === null || draggedExercise === visibleIndex) return;
		// "Add to superset" mode locks the drag onto a single target — reorder is disabled.
		if (addingToSuperset !== null) return;

		const updated = { ...editorTraining };
		const visibleExercises = updated.exercices.filter((ex) => ex.supprime !== 1);
		const deletedExercises = updated.exercices.filter((ex) => ex.supprime === 1);

		const draggedExo = visibleExercises[draggedExercise];
		const targetExo = visibleExercises[visibleIndex];
		const draggedGroup = draggedExo.supersetGroup || 0;
		const targetGroup = targetExo.supersetGroup || 0;

		const groupRange = (arr, grp) => {
			let first = -1, last = -1;
			for (let i = 0; i < arr.length; i++) {
				if ((arr[i].supersetGroup || 0) === grp) {
					if (first === -1) first = i;
					last = i;
				}
			}
			return [first, last];
		};

		// --- Case A: dragging an ENTIRE superset (header handle) ---
		if (draggedAsGroup && draggedGroup > 0) {
			if (draggedGroup === targetGroup) return; // dropping on own member is a no-op
			const members = visibleExercises.filter((ex) => (ex.supersetGroup || 0) === draggedGroup);
			const others = visibleExercises.filter((ex) => (ex.supersetGroup || 0) !== draggedGroup);
			const [draggedFirst] = groupRange(visibleExercises, draggedGroup);
			const dragUp = visibleIndex < draggedFirst;

			let insertAt;
			if (targetGroup > 0) {
				const [tFirst, tLast] = groupRange(others, targetGroup);
				insertAt = dragUp ? tFirst : tLast + 1;
			} else {
				const targetIdxInOthers = others.indexOf(targetExo);
				insertAt = dragUp ? targetIdxInOthers : targetIdxInOthers + 1;
			}

			const newVisible = [
				...others.slice(0, insertAt),
				...members,
				...others.slice(insertAt),
			];

			updated.exercices = [...newVisible, ...deletedExercises];
			setEditorTraining({ ...updated });
			const newFirstMemberIdx = newVisible.findIndex((ex) => (ex.supersetGroup || 0) === draggedGroup);
			setDraggedExercise(newFirstMemberIdx);
			setTrainingModified(true);
			return;
		}

		// --- Case B: dragging a SINGLE member of a superset (member card handle) ---
		// Locked inside the group: reorder freely with siblings of the SAME group; ignore
		// any target outside the group (use the detach button to extract instead).
		if (draggedGroup > 0) {
			if (targetGroup !== draggedGroup) return;
			const item = visibleExercises[draggedExercise];
			visibleExercises.splice(draggedExercise, 1);
			visibleExercises.splice(visibleIndex, 0, item);
			updated.exercices = [...visibleExercises, ...deletedExercises];
			setEditorTraining({ ...updated });
			setDraggedExercise(visibleIndex);
			setTrainingModified(true);
			return;
		}

		// --- Case C: dragging a STANDALONE exo ---
		// If target is a member of a superset, snap to that group's boundary (reorder
		// BEFORE or AFTER the whole group, never inside). Adding to a superset only
		// happens through the explicit add-mode picker.
		let actualTargetIdx = visibleIndex;
		if (targetGroup > 0) {
			const [tFirst, tLast] = groupRange(visibleExercises, targetGroup);
			const dragUp = draggedExercise > visibleIndex;
			actualTargetIdx = dragUp ? tFirst : tLast + 1;
			if (draggedExercise < actualTargetIdx) actualTargetIdx--;
			if (actualTargetIdx === draggedExercise) return;
		}

		const draggedItem = visibleExercises[draggedExercise];
		visibleExercises.splice(draggedExercise, 1);
		visibleExercises.splice(actualTargetIdx, 0, draggedItem);

		updated.exercices = [...visibleExercises, ...deletedExercises];
		setEditorTraining({ ...updated });
		setDraggedExercise(actualTargetIdx);
		setTrainingModified(true);
	};

	const handleDragEnd = () => {
		setDraggedExercise(null);
		setDraggedAsGroup(false);
	};

	// Pending (empty) supersets — visible UI placeholders that exist only in React
	// state until the user drops at least one exo into them. The DB only stores
	// supersetGroup on each exo, so an empty group has no persisted home.
	const [pendingSupersets, setPendingSupersets] = useState([]);

	// Group id of the superset currently in "add exercises" mode. When non-null,
	// drag-and-drop only targets THIS superset (drop = add to group) and reorder
	// around the supersets is disabled. When null, drag behaves as a normal
	// reorder and never adds exos to any superset.
	const [addingToSuperset, setAddingToSuperset] = useState(null);
	// Visible indices of exos picked so far, in click order. Drained on confirm.
	const [pendingAdditions, setPendingAdditions] = useState([]);

	const toggleAddingToSuperset = (groupId) => {
		setAddingToSuperset((prev) => {
			const next = prev === groupId ? null : groupId;
			if (next == null) setPendingAdditions([]);
			return next;
		});
	};

	const togglePendingAddition = (visibleIndex) => {
		setPendingAdditions((prev) => {
			const idx = prev.indexOf(visibleIndex);
			if (idx >= 0) return prev.filter((i) => i !== visibleIndex);
			return [...prev, visibleIndex];
		});
	};

	// Move every picked exo into the target superset, preserving the click order,
	// inserting them right after the last existing member of the group.
	const confirmAddToSuperset = (groupId) => {
		if (pendingAdditions.length === 0) return;
		const orderIndices = pendingAdditions;
		setEditorTraining((prev) => {
			const all = [...(prev.exercices || [])];
			const visible = all.filter((e) => e.supprime !== 1);
			const deleted = all.filter((e) => e.supprime === 1);

			const keyOf = (e) => (e.id != null && e.id > 0 ? "id-" + e.id : "local-" + e.localId);
			const toAdd = orderIndices
				.map((idx) => visible[idx])
				.filter(Boolean)
				.map((e) => ({ ...e, supersetGroup: groupId }));
			const addKeys = new Set(toAdd.map(keyOf));

			const remaining = visible.filter((e) => !addKeys.has(keyOf(e)));

			let lastMember = -1;
			remaining.forEach((e, i) => { if ((e.supersetGroup || 0) === groupId) lastMember = i; });
			const insertPos = lastMember === -1 ? remaining.length : lastMember + 1;

			const newVisible = [
				...remaining.slice(0, insertPos),
				...toAdd,
				...remaining.slice(insertPos),
			];

			return { ...prev, exercices: [...newVisible, ...deleted] };
		});
		setPendingAdditions([]);
		setPendingSupersets((prev) => prev.filter((g) => g !== groupId));
		setAddingToSuperset(null);
		setTrainingModified(true);
	};

	const createSuperset = () => {
		const exos = (editorTraining?.exercices || []);
		const maxGroup = Math.max(0, ...exos.map((e) => e.supersetGroup || 0), ...pendingSupersets);
		setPendingSupersets((prev) => [...prev, maxGroup + 1]);
	};

	const removeEmptySuperset = (groupId) => {
		setPendingSupersets((prev) => prev.filter((g) => g !== groupId));
	};

	// Drop a dragged exo into a superset block: assigns the group and moves the
	// exo so it sits adjacent to the other group members in `exercices` (the
	// renderer collapses consecutive same-group exos into a single block).
	const dropExoIntoSuperset = (groupId) => {
		if (draggedExercise === null) return;
		const draggedIdx = draggedExercise;
		setEditorTraining((prev) => {
			const all = [...(prev.exercices || [])];
			const visible = all.filter((e) => e.supprime !== 1);
			const deleted = all.filter((e) => e.supprime === 1);
			if (draggedIdx < 0 || draggedIdx >= visible.length) return prev;
			const dragged = { ...visible[draggedIdx], supersetGroup: groupId };

			// Find the insertion position: right after the last existing member of this group
			// (excluding the dragged one, which we're about to remove from its old slot).
			let lastMember = -1;
			for (let i = 0; i < visible.length; i++) {
				if (i === draggedIdx) continue;
				if ((visible[i].supersetGroup || 0) === groupId) lastMember = i;
			}
			let insertPos = lastMember === -1 ? visible.length : lastMember + 1;

			// Remove dragged from current position
			visible.splice(draggedIdx, 1);
			// Adjust insertion target if it sat past the dragged index
			if (draggedIdx < insertPos) insertPos--;
			visible.splice(insertPos, 0, dragged);

			return { ...prev, exercices: [...visible, ...deleted] };
		});
		setDraggedExercise(null);
		setPendingSupersets((prev) => prev.filter((g) => g !== groupId));
		setAddingToSuperset(null);
		setTrainingModified(true);
	};

	// Remove an exo from its superset (set supersetGroup back to 0) without
	// removing the exo itself from the training.
	const removeExoFromSuperset = (visibleIndex) => {
		setEditorTraining((prev) => {
			const all = [...(prev.exercices || [])];
			const visible = all.filter((e) => e.supprime !== 1);
			const deleted = all.filter((e) => e.supprime === 1);
			if (visibleIndex < 0 || visibleIndex >= visible.length) return prev;
			visible[visibleIndex] = { ...visible[visibleIndex], supersetGroup: 0 };
			return { ...prev, exercices: [...visible, ...deleted] };
		});
		setTrainingModified(true);
	};

	// Add an exercise from the exercises panel to the current editor session.
	// Creates a new training if none is open yet (auto-create mode).
	const addExerciseToEditor = (exercise) => {
		const exName = exercise.nomExercice || exercise.nom || "Exercice";
		const now = Date.now();
		const newEntry = {
			id: -1,                         // -1 = not saved yet
			localId: now,                   // numeric long-like local id, matches mobile app
			nom: exName,
			idExercice: exercise.id,
			supprime: 0,
			supersetGroup: 0,
			series: [
				{ id: -1, reps: 0, poids: 0, poidsDec: 0, rpe: 0 },
				{ id: -1, reps: 0, poids: 0, poidsDec: 0, rpe: 0 },
			],
		};

		if (!editorTraining) {
			setEditorTraining({
				id: null,
				nom: "Nouvelle séance",
				date: Date.now(),
				estTemplate: false,
				exercices: [newEntry],
			});
			setEditorMode("create");
			setTrainingModified(true);
			return;
		}

		const updated = { ...editorTraining };
		updated.exercices = [...(updated.exercices || []), newEntry];
		setEditorTraining(updated);
		setTrainingModified(true);
	};

	const handleDeleteTraining = async () => {
		if (!editorTraining || !selectedStudent) return;

		const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer cet entraînement ?");
		if (!confirmed) return;

		setSavingTraining(true);
		const result = await deleteTraining(selectedStudent.id, editorTraining.id);
		setSavingTraining(false);

		if (!result.success) {
			showNotif("Erreur lors de la suppression: " + result.error, 'error');
			return;
		}

		// Refresh calendar markers
		const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
		if (daysResult.success) setTrainingDays(daysResult.trainingDays);

		// Refresh training details for the currently selected day
		if (selectedDay) {
			const detailsResult = await getTrainingDetails(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1, selectedDay);
			if (detailsResult.success) setTrainingDetails(detailsResult.trainings);
		}

		setEditorTraining(null);
		setEditorMode(null);
		setTrainingModified(false);
		setLoadedFromTemplate(false);
		setLoadedTemplateName("");
	};

	const handleDeleteTemplate = async () => {
		if (!editorTraining || !selectedStudent) return;

		const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer ce template ?");
		if (!confirmed) return;

		setSavingTraining(true);
		const result = await deleteTraining(selectedStudent.id, editorTraining.id);
		setSavingTraining(false);

		if (!result.success) {
			showNotif("Erreur lors de la suppression: " + result.error, 'error');
			return;
		}

		// Refresh templates list
		const templatesResult = await getStudentTemplates(selectedStudent.id);
		if (templatesResult.success) setTemplates(templatesResult.templates);

		setEditorTraining(null);
		setEditorMode(null);
		setTrainingModified(false);
	};

	// ===== Library mode: create/edit coach-owned templates (no student) =====
	const refreshCoachTemplates = async () => {
		const coachRes = await getCoachTemplates();
		if (coachRes.success) setCoachTemplates(coachRes.data?.templates || []);
	};

	const exitLibraryEditor = () => {
		setEditorTraining(null);
		setEditorMode(null);
		setTrainingModified(false);
		setLoadedFromTemplate(false);
		setLoadedTemplateName("");
		setEditorPanel(null);
		setLibraryMode(false);
		setView("templates");
	};

	const openNewLibraryTemplate = () => {
		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}
		setSelectedStudent(null);
		setLibraryMode(true);
		setEditorTraining({ id: -1, nom: "", date: null, estTemplate: true, exercices: [], commentaire: "" });
		setEditorMode("createTemplate");
		setTrainingModified(false);
		setLoadedFromTemplate(false);
		setLoadedTemplateName("");
		setEditorPanel(null);
	};

	const openLibraryTemplate = async (tplId) => {
		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}
		setSelectedStudent(null);
		setLibraryMode(true);
		setEditorPanel(null);
		setEditorMode("editTemplate");
		setLoadingEditor(true);
		const result = await getTrainingFull(user.id, tplId);
		if (result.success) {
			const training = JSON.parse(JSON.stringify(result.training));
			training.exercices = (training.exercices || []).map((ex) => ({ ...ex, supprime: 0, localId: ex.id }));
			training.estTemplate = true;
			setEditorTraining(training);
			setTrainingModified(false);
			setLoadedFromTemplate(false);
			setLoadedTemplateName("");
		} else {
			showNotif("Erreur lors du chargement du template", "error");
			setLibraryMode(false);
		}
		setLoadingEditor(false);
	};

	// Load an existing coach template's content into the current library editor.
	// Replaces the current content (does not merge).
	const loadCoachTemplateIntoEditor = async (tpl) => {
		const tplId = tpl.Id ?? tpl.id;
		if (!tplId || !user) return;

		const hasContent = ((editorTraining && editorTraining.exercices) || []).some((ex) => ex.supprime !== 1);
		if (hasContent) {
			const ok = window.confirm("Remplacer le contenu actuel par ce template ?");
			if (!ok) return;
		}

		setEditorPanel(null);
		setLoadingEditor(true);
		const result = await getTrainingFull(user.id, tplId);
		setLoadingEditor(false);
		if (!result.success) {
			showNotif("Erreur lors du chargement du template", "error");
			return;
		}
		const loaded = JSON.parse(JSON.stringify(result.training));
		const newEntries = (loaded.exercices || [])
			.filter((ex) => ex.supprime !== 1)
			.map((ex, i) => ({ ...ex, id: -1, localId: Date.now() + i, supprime: 0 }));
		setEditorTraining((prev) => {
			// Keep already-persisted exercises as deletions so the backend removes them;
			// drop never-saved (id -1) ones outright.
			const deletedOld = ((prev && prev.exercices) || [])
				.filter((ex) => ex.id != null && ex.id > 0)
				.map((ex) => ({ ...ex, supprime: 1 }));
			return {
				...prev,
				nom: prev && prev.nom && prev.nom.trim() ? prev.nom : (loaded.nom || ""),
				exercices: [...deletedOld, ...newEntries],
			};
		});
		setTrainingModified(true);
	};

	const handleSaveLibraryTemplate = async () => {
		if (!editorTraining || !user) return;
		if (!editorTraining.nom || !editorTraining.nom.trim()) {
			showNotif("Donne un nom au template avant d'enregistrer", "error");
			return;
		}

		setSavingTraining(true);
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices.map((ex) => ({
			Id: ex.id,
			LocalId: ex.localId || Date.now(),
			IdExercice: ex.idExercice,
			Supprime: ex.supprime || 0,
			Ordre: ex.supprime === 1 ? -1 : ordre++,
			SupersetGroup: ex.supersetGroup || 0,
			ListSerie: ex.series.map((s) => ({ reps: s.reps, poids: s.poids, poidsDec: s.poidsDec || 0, rpe: s.rpe || 0 })),
			ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({ reps: w.reps || 0, poids: w.poids || 0, poidsDec: w.poidsDec || 0 })),
		}));

		let result;
		let savedId = editorTraining.id;
		if (editorTraining.id == null || editorTraining.id === -1) {
			result = await createTraining(user.id, editorTraining.nom.trim(), Date.now(), true, exercicesForApi);
			if (result.success) savedId = result.trainingId;
		} else {
			result = await updateTraining(user.id, editorTraining.id, editorTraining.nom.trim(), editorTraining.date || Date.now(), true, exercicesForApi, editorTraining.commentaire || "");
		}
		setSavingTraining(false);

		if (result.success) {
			await refreshCoachTemplates();
			const fullResult = await getTrainingFull(user.id, savedId);
			if (fullResult.success) {
				const fresh = JSON.parse(JSON.stringify(fullResult.training));
				fresh.exercices = (fresh.exercices || []).map((ex) => ({ ...ex, supprime: 0, localId: ex.id }));
				fresh.estTemplate = true;
				setEditorTraining(fresh);
			}
			setEditorMode("editTemplate");
			setTrainingModified(false);
			setEditorLastModifiedAt(Date.now());
			showNotif("Template enregistré dans ta bibliothèque");
		} else {
			showNotif("Erreur lors de l'enregistrement: " + result.error, "error");
		}
	};

	const handleDeleteLibraryTemplate = async () => {
		if (!editorTraining || !user || editorTraining.id == null || editorTraining.id === -1) return;
		const confirmed = window.confirm("Supprimer ce template de ta bibliothèque ?");
		if (!confirmed) return;
		setSavingTraining(true);
		const result = await deleteTraining(user.id, editorTraining.id);
		setSavingTraining(false);
		if (!result.success) {
			showNotif("Erreur lors de la suppression: " + result.error, "error");
			return;
		}
		await refreshCoachTemplates();
		exitLibraryEditor();
	};

	const handleSaveTemplate = async () => {
		if (!editorTraining || !selectedStudent) return;

		setSavingTraining(true);

		// Transform data to API format with IDs and supprime status
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices.map((ex) => {
			const exercice = {
				Id: ex.id, // Database ID (-1 for new)
				LocalId: ex.localId || Date.now(), // Local identifier (fallback to timestamp if missing)
				IdExercice: ex.idExercice, // Referentiel exercise ID
				Supprime: ex.supprime || 0,
				Ordre: ex.supprime === 1 ? -1 : ordre++,
				SupersetGroup: ex.supersetGroup || 0,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					poidsDec: s.poidsDec || 0,
					rpe: s.rpe || 0,
				})),
				ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({
					reps: w.reps || 0,
					poids: w.poids || 0,
					poidsDec: w.poidsDec || 0,
				}))
			};
			return exercice;
		});

		const result = await updateTraining(
			selectedStudent.id,
			editorTraining.id,
			editorTraining.nom,
			editorTraining.date,
			true, // EstTemplate = true
			exercicesForApi,
			editorTraining.commentaire || ""
		);

		setSavingTraining(false);

		if (result.success) {
			// Refresh templates list
			const templatesResult = await getStudentTemplates(selectedStudent.id);
			if (templatesResult.success) {
				setTemplates(templatesResult.templates);
			}
			// Reload the saved template fresh so ids are real for subsequent edits.
			const fullResult = await getTrainingFull(selectedStudent.id, editorTraining.id);
			if (fullResult.success) {
				const fresh = JSON.parse(JSON.stringify(fullResult.training));
				fresh.exercices = (fresh.exercices || []).map((ex) => ({ ...ex, supprime: 0, localId: ex.id }));
				fresh.estTemplate = true;
				setEditorTraining(fresh);
			}
			setTrainingModified(false);
			setEditorLastModifiedAt(Date.now());
			showNotif("Template enregistré avec succès");
		} else {
			showNotif("Erreur lors de l'enregistrement: " + result.error, 'error');
		}
	};

	const handleSaveTraining = async () => {
		if (!editorTraining || !selectedStudent) return;

		setSavingTraining(true);

		// Transform data to API format with IDs and supprime status
		// Calculate ordre based on visible exercises order
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices.map((ex) => {
			const exercice = {
				Id: ex.id, // Database ID (-1 for new)
				LocalId: ex.localId || Date.now(), // Local identifier (fallback to timestamp if missing)
				IdExercice: ex.idExercice, // Referentiel exercise ID
				Supprime: ex.supprime || 0,
				Ordre: ex.supprime === 1 ? -1 : ordre++, // Only increment ordre for non-deleted
				SupersetGroup: ex.supersetGroup || 0,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					poidsDec: s.poidsDec || 0,
					rpe: s.rpe || 0,
				})),
				ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({
					reps: w.reps || 0,
					poids: w.poids || 0,
					poidsDec: w.poidsDec || 0,
				}))
			};
			return exercice;
		});

		const isNew = !editorTraining.id || editorTraining.id < 0;
		let result;
		let newId = editorTraining.id;
		if (isNew) {
			result = await createTraining(
				selectedStudent.id,
				editorTraining.nom,
				editorTraining.date,
				editorTraining.estTemplate || false,
				exercicesForApi,
			);
			if (result.success) newId = result.trainingId;
		} else {
			result = await updateTraining(
				selectedStudent.id,
				editorTraining.id,
				editorTraining.nom,
				editorTraining.date,
				editorTraining.estTemplate || false,
				exercicesForApi,
				editorTraining.commentaire || "",
			);
		}

		setSavingTraining(false);

		if (result.success) {
			// Refresh calendar markers (a new session adds a dot)
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) setTrainingDays(daysResult.trainingDays);

			// Refresh training details if we're viewing the same day
			if (selectedDay) {
				const year = currentDate.getFullYear();
				const month = currentDate.getMonth() + 1;
				const detailsResult = await getTrainingDetails(selectedStudent.id, year, month, selectedDay);
				if (detailsResult.success) {
					setTrainingDetails(detailsResult.trainings);
				}
			}

			// Reload the saved session fresh so series/exercise ids are real for subsequent edits.
			const fullResult = await getTrainingFull(selectedStudent.id, newId);
			if (fullResult.success) {
				const fresh = JSON.parse(JSON.stringify(fullResult.training));
				fresh.exercices = (fresh.exercices || []).map((ex) => ({ ...ex, supprime: 0, localId: ex.id }));
				fresh.estTemplate = false;
				setEditorTraining(fresh);
			} else {
				setEditorTraining((prev) => (prev ? { ...prev, id: newId } : prev));
			}
			setEditorMode("edit");
			setTrainingModified(false);
			setLoadedFromTemplate(false);
			setLoadedTemplateName("");
			setEditorLastModifiedAt(Date.now());
			showNotif("Entraînement enregistré avec succès");
		} else {
			showNotif("Erreur lors de l'enregistrement: " + result.error, 'error');
		}
	};

	const openDatePickerModal = (action, overviewTraining = null) => {
		setSelectedCalendarDate(new Date());
		setDatePickerAction(action);
		setSelectedOverviewTraining(overviewTraining);
		setShowDatePicker(true);
	};

	const closeDatePickerModal = () => {
		setShowDatePicker(false);
		setDatePickerAction(null);
		setSelectedOverviewTraining(null);
	};

	const handleAddToCalendar = async () => {
		if (!editorTraining || !selectedStudent) return;

		setSavingTraining(true);
		setShowDatePicker(false);

		// Convert selected date to timestamp (noon UTC to avoid TZ day-shift)
		const dateTimestamp = toApiDateTimestamp(selectedCalendarDate);

		// Transform data to API format - filter out deleted exercises
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices
			.filter((ex) => ex.supprime !== 1)
			.map((ex) => ({
				Id: -1, // New exercise
				LocalId: ex.localId || Date.now(),
				IdExercice: ex.idExercice,
				Supprime: 0,
				Ordre: ordre++,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					poidsDec: s.poidsDec || 0,
					rpe: s.rpe || 0,
				})),
				ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({
					reps: w.reps || 0,
					poids: w.poids || 0,
					poidsDec: w.poidsDec || 0,
				}))
			}));

		const result = await createTraining(
			selectedStudent.id,
			editorTraining.nom,
			dateTimestamp,
			false, // EstTemplate = false
			exercicesForApi
		);

		setSavingTraining(false);

		if (result.success) {
			// Check if the selected date is in the current month view
			const selectedYear = selectedCalendarDate.getFullYear();
			const selectedMonth = selectedCalendarDate.getMonth();
			const currentYear = currentDate.getFullYear();
			const currentMonth = currentDate.getMonth();

			if (selectedYear === currentYear && selectedMonth === currentMonth) {
				// Refresh training days
				const daysResult = await getTrainingDays(selectedStudent.id, selectedYear, selectedMonth + 1);
				if (daysResult.success) {
					setTrainingDays(daysResult.trainingDays);
				}
			}

			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			showNotif("Entraînement ajouté au calendrier avec succès");
		} else {
			showNotif("Erreur lors de l'enregistrement: " + result.error, 'error');
		}
	};

	const handleMoveTraining = async () => {
		if (!selectedOverviewTraining || !selectedStudent) return;

		setSavingTraining(true);
		setShowDatePicker(false);

		const dateTimestamp = toApiDateTimestamp(selectedCalendarDate);

		const result = await moveTraining(
			selectedStudent.id,
			selectedOverviewTraining.id,
			dateTimestamp
		);

		setSavingTraining(false);
		setSelectedOverviewTraining(null);
		setDatePickerAction(null);

		if (result.success) {
			// Refresh training days for the current month view
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) {
				setTrainingDays(daysResult.trainingDays);
			}

			// Clear selected day and details since the training moved
			setSelectedDay(null);
			setTrainingDetails([]);

			showNotif("Entraînement déplacé avec succès");
		} else {
			showNotif("Erreur lors du déplacement: " + result.error, 'error');
		}
	};

	const handleDuplicateTraining = async () => {
		if (!selectedOverviewTraining || !selectedStudent) return;

		setSavingTraining(true);
		setShowDatePicker(false);

		// Fetch full training data
		const fullResult = await getTrainingFull(selectedStudent.id, selectedOverviewTraining.id);
		if (!fullResult.success) {
			setSavingTraining(false);
			showNotif("Erreur lors de la récupération de l'entraînement", 'error');
			return;
		}

		const training = fullResult.training;
		const dateTimestamp = toApiDateTimestamp(selectedCalendarDate);

		// Create new exercises with new LocalIds (similar to duplicateSeance in the app)
		const baseTime = Date.now();
		const exercicesForApi = training.exercices.map((ex, index) => ({
			Id: -1, // New exercise
			LocalId: baseTime + index, // New unique LocalId for each exercise
			IdExercice: ex.idExercice, // Keep the referentiel exercise ID
			Supprime: 0,
			Ordre: index,
			ListSerie: ex.series.map((s) => ({
				reps: 0, // Reset reps to 0 like in the app
				poids: s.poids,
				poidsDec: s.poidsDec || 0,
				rpe: 0,
			}))
		}));

		const result = await createTraining(
			selectedStudent.id,
			training.nom,
			dateTimestamp,
			false, // EstTemplate = false
			exercicesForApi
		);

		setSavingTraining(false);
		setSelectedOverviewTraining(null);
		setDatePickerAction(null);

		if (result.success) {
			// Check if the selected date is in the current month view
			const selectedYear = selectedCalendarDate.getFullYear();
			const selectedMonth = selectedCalendarDate.getMonth();
			const currentYear = currentDate.getFullYear();
			const currentMonth = currentDate.getMonth();

			if (selectedYear === currentYear && selectedMonth === currentMonth) {
				// Refresh training days
				const daysResult = await getTrainingDays(selectedStudent.id, selectedYear, selectedMonth + 1);
				if (daysResult.success) {
					setTrainingDays(daysResult.trainingDays);
				}
			}

			showNotif("Entraînement dupliqué avec succès");
		} else {
			showNotif("Erreur lors de la duplication: " + result.error, 'error');
		}
	};

	const handleDatePickerConfirm = () => {
		if (datePickerAction === 'addToCalendar') {
			handleAddToCalendar();
		} else if (datePickerAction === 'move') {
			handleMoveTraining();
		} else if (datePickerAction === 'duplicate') {
			handleDuplicateTraining();
		}
	};

	const openTemplateNameModal = () => {
		if (!editorTraining || !selectedStudent) return;
		setTemplateName(editorTraining.nom || "");
		setShowTemplateNameModal(true);
	};

	const closeTemplateNameModal = () => {
		setShowTemplateNameModal(false);
		setTemplateName("");
	};

	const handleSaveAsTemplate = async () => {
		if (!editorTraining || !selectedStudent || !templateName.trim()) return;

		setSavingTraining(true);
		setShowTemplateNameModal(false);

		const isCoach = templateSaveTarget === "coach";
		const targetUserId = isCoach ? user.id : selectedStudent.id;

		// Transform data to API format - filter out deleted exercises
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices
			.filter((ex) => ex.supprime !== 1)
			.map((ex) => ({
				Id: -1, // New exercise
				LocalId: ex.localId || Date.now(),
				IdExercice: ex.idExercice,
				Supprime: 0,
				Ordre: ordre++,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					poidsDec: s.poidsDec || 0,
					rpe: s.rpe || 0,
				})),
				ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({
					reps: w.reps || 0,
					poids: w.poids || 0,
					poidsDec: w.poidsDec || 0,
				}))
			}));

		const result = await createTraining(
			targetUserId,
			templateName.trim(),
			editorTraining.date || Date.now(),
			true, // EstTemplate = true
			exercicesForApi
		);

		setSavingTraining(false);
		setTemplateName("");

		if (result.success) {
			// Refresh the relevant templates list
			if (isCoach) {
				const coachRes = await getCoachTemplates();
				if (coachRes.success) setCoachTemplates(coachRes.data?.templates || []);
			} else {
				const templatesResult = await getStudentTemplates(selectedStudent.id);
				if (templatesResult.success) setTemplates(templatesResult.templates);
			}
			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			showNotif("Template enregistré avec succès");
		} else {
			showNotif("Erreur lors de l'enregistrement: " + result.error, 'error');
		}
	};

	// Helper to get real index from visible index
	const getRealExerciceIndex = (visibleIndex) => {
		const visibleExercises = editorTraining.exercices.filter((ex) => ex.supprime !== 1);
		const exercise = visibleExercises[visibleIndex];
		return editorTraining.exercices.findIndex(
			(ex) => (ex.localId && ex.localId === exercise.localId) || (ex.id > 0 && ex.id === exercise.id)
		);
	};

	const addSerie = (visibleIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const lastSerie = updated.exercices[realIndex].series.slice(-1)[0];
		const newSerie = {
			id: -1, // New serie, not yet saved
			reps: lastSerie ? lastSerie.reps : 10,
			poids: lastSerie ? lastSerie.poids : 0,
			poidsDec: lastSerie ? (lastSerie.poidsDec || 0) : 0,
			rpe: lastSerie ? lastSerie.rpe || 0 : 0
		};
		updated.exercices[realIndex].series.push(newSerie);
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const removeSerie = (visibleIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		if (updated.exercices[realIndex].series.length > 1) {
			updated.exercices[realIndex].series.pop();
			setEditorTraining({ ...updated });
			setTrainingModified(true);
		}
	};

	const updateSerieReps = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		updated.exercices[realIndex].series[serieIndex].reps = parseInt(value) || 0;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const updateSeriePoids = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		// Keep the integer part only (decimal handled separately via poidsDec).
		updated.exercices[realIndex].series[serieIndex].poids = parseInt(value) || 0;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	// Decimal part of the weight: 0, 25, 50 or 75.
	const updateSeriePoidsDec = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const allowed = [0, 25, 50, 75];
		const dec = allowed.includes(parseInt(value)) ? parseInt(value) : 0;
		updated.exercices[realIndex].series[serieIndex].poidsDec = dec;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const updateSerieRpe = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		updated.exercices[realIndex].series[serieIndex].rpe = parseFloat(value) || 0;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	// ─── Séries d'échauffement (warmupSeries) ───
	// Modélisées séparément côté DB (gn_serie_echauffement). Pas de RPE.
	// Exclues des records, de la progression et du calcul de volume.
	const ensureWarmups = (exo) => {
		if (!Array.isArray(exo.warmupSeries)) exo.warmupSeries = [];
	};

	const addWarmup = (visibleIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const exo = updated.exercices[realIndex];
		ensureWarmups(exo);
		exo.warmupSeries = [...exo.warmupSeries, { id: -1, reps: 0, poids: 0, poidsDec: 0 }];
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const removeWarmup = (visibleIndex, warmupIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const exo = updated.exercices[realIndex];
		ensureWarmups(exo);
		exo.warmupSeries = exo.warmupSeries.filter((_, i) => i !== warmupIndex);
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const updateWarmupField = (visibleIndex, warmupIndex, field, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const exo = updated.exercices[realIndex];
		ensureWarmups(exo);
		const w = { ...exo.warmupSeries[warmupIndex] };
		if (field === "reps" || field === "poids" || field === "poidsDec") {
			w[field] = parseInt(value, 10) || 0;
			if (field === "poids") w.poids = parseFloat(value) || 0;
		}
		exo.warmupSeries = exo.warmupSeries.map((it, i) => (i === warmupIndex ? w : it));
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	// Remove a single serie by its index (the editor needs this for the per-row trash button).
	const removeSerieAt = (visibleIndex, serieIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const series = updated.exercices[realIndex].series;
		if (series.length <= 1) return; // keep at least one serie
		series.splice(serieIndex, 1);
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const duplicateSerie = (visibleIndex, serieIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const src = updated.exercices[realIndex].series[serieIndex];
		if (!src) return;
		updated.exercices[realIndex].series.splice(serieIndex + 1, 0, {
			id: -1,
			reps: src.reps || 0,
			poids: src.poids || 0,
			poidsDec: src.poidsDec || 0,
			rpe: src.rpe || 0,
		});
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const goToPreviousMonth = () => {
		setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
	};

	const goToNextMonth = () => {
		setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
	};

	const getDaysInMonth = (year, month) => {
		return new Date(year, month + 1, 0).getDate();
	};

	const getFirstDayOfMonth = (year, month) => {
		const day = new Date(year, month, 1).getDay();
		// Convert Sunday (0) to 7, so Monday is 1
		return day === 0 ? 7 : day;
	};

	// ─── Assistance exercise resolver ─────────────────────────
	// Un exercice d'assistance (Machine Assisted Pull-up, Adducteurs, etc.)
	// utilise un contrepoids ou un dispositif qui allège la résistance.
	// Progresser dessus = utiliser MOINS de poids (moins d'assistance), et
	// non pas plus. On inverse donc la comparaison poids côté progression /
	// régression pour ne pas contredire l'effort réel de l'élève.
	const isAssistanceExercise = (exo) => {
		if (!exo) return false;
		const ref =
			(exo.idExercice != null && exercises.find((e) => e.id === exo.idExercice)) ||
			exercises.find((e) => (e.nomExercice || "").trim().toLowerCase() === (exo.nom || exo.nomExercice || "").trim().toLowerCase());
		return !!(ref && ref.estAssistance);
	};

	// ========== Multi-date duplication ==========
	const startMultiDateDuplicate = (training) => {
		setMultiSelectMode(true);
		setDuplicateSourceTraining(training);
		setSelectedDates([]);
	};

	const cancelMultiDateDuplicate = () => {
		setMultiSelectMode(false);
		setDuplicateSourceTraining(null);
		setSelectedDates([]);
	};

	const toggleDateSelection = (dateTimestamp) => {
		setSelectedDates(prev =>
			prev.includes(dateTimestamp)
				? prev.filter(d => d !== dateTimestamp)
				: [...prev, dateTimestamp]
		);
	};

	const confirmMultiDateDuplicate = async () => {
		if (!duplicateSourceTraining || !selectedStudent || selectedDates.length === 0) return;
		setSavingTraining(true);
		const result = await duplicateTrainingMultiple(selectedStudent.id, duplicateSourceTraining.id, selectedDates);
		setSavingTraining(false);
		cancelMultiDateDuplicate();
		if (result.success) {
			// Refresh calendar
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) setTrainingDays(daysResult.trainingDays);
			showNotif(`Entraînement dupliqué sur ${selectedDates.length} date(s)`);
		} else {
			showNotif("Erreur: " + result.error, 'error');
		}
	};

	// ========== Planification ==========
	const openPlanification = async () => {
		setShowPlanification(true);
		setPlanSelectedTemplate(null);
		setPlanDays([false, false, false, false, false, false, false]);
		setPlanWeeks(4);
		setPlanStartDate(new Date().toISOString().split('T')[0]);
		setPlanSaveAsTemplate(false);
		// Fetch coach templates
		const result = await getCoachTemplates();
		if (result.success) setCoachTemplates(result.data?.templates || []);
	};

	const closePlanification = () => {
		setShowPlanification(false);
		setPlanSelectedTemplate(null);
	};

	const getPlanDates = () => {
		const dates = [];
		const start = new Date(planStartDate);
		// Find the Monday of the start date's week (ISO: Monday=0)
		const startDayOfWeek = (start.getDay() + 6) % 7; // 0=Mon, 6=Sun
		const weekStart = new Date(start);
		weekStart.setDate(start.getDate() - startDayOfWeek);

		// We need planWeeks full weeks of selected days FROM the start date
		// Iterate enough weeks to guarantee planWeeks occurrences
		let collected = 0;
		for (let week = 0; collected < planWeeks * 7 && week < planWeeks + 1; week++) {
			for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
				if (planDays[dayIndex]) {
					const date = new Date(weekStart);
					date.setDate(weekStart.getDate() + week * 7 + dayIndex);
					if (date >= start) {
						dates.push(toApiDateTimestamp(date));
					}
				}
			}
			collected++;
		}

		// Keep only the first planWeeks occurrences per selected day
		// Count how many selected days per week
		const selectedDaysCount = planDays.filter(Boolean).length;
		const maxDates = planWeeks * selectedDaysCount;
		dates.sort((a, b) => a - b);
		return dates.slice(0, maxDates);
	};

	const confirmPlanification = async () => {
		if (!selectedStudent || !planSelectedTemplate) return;
		const dates = getPlanDates();
		if (dates.length === 0) { showNotif("Aucune date sélectionnée", 'error'); return; }

		setPlanLoading(true);
		const isCoachTemplate = planTemplateSource === "coach";

		const payload = {
			studentId: selectedStudent.id,
			dates,
			saveAsStudentTemplate: planSaveAsTemplate
		};

		if (isCoachTemplate) {
			payload.templateName = planSelectedTemplate.nom;
			payload.exercises = planSelectedTemplate.exercices.map((ex, i) => ({
				nomExercice: ex.nom || ex.nomExercice,
				ordre: i,
				supersetGroup: ex.supersetGroup || 0,
				series: ex.series.map((s, j) => ({ ordre: j, reps: s.reps || 0, poids: s.poids || 0, poidsDec: s.poidsDec || 0, rpe: s.rpe || 0 }))
			}));
		} else {
			payload.templateId = planSelectedTemplate.id;
		}

		const result = await scheduleProgramming(selectedStudent.id, payload);
		setPlanLoading(false);

		if (result.success) {
			closePlanification();
			// Refresh calendar
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) setTrainingDays(daysResult.trainingDays);
			showNotif(`${dates.length} séances planifiées avec succès`);
		} else {
			showNotif("Erreur: " + result.error, 'error');
		}
	};

	// ========== Send template ==========
	const handleSendTemplate = async (template) => {
		if (!selectedStudent) return;
		// Support both camelCase (student templates) and PascalCase (coach templates)
		const templateNom = template.nom || template.Nom || "";
		const templateId = template.id || template.Id;
		const confirmed = window.confirm(`Envoyer le template "${templateNom}" à ${selectedStudent.prenom} ${selectedStudent.nom} ?`);
		if (!confirmed) return;

		// Fetch full template data — for coach templates, use coach's own id
		const ownerId = (template.Id !== undefined) ? user.id : selectedStudent.id;
		const fullResult = await getTrainingFull(ownerId, templateId);
		if (!fullResult.success) { showNotif("Erreur lors de la récupération du template", 'error'); return; }

		const exercisesPayload = fullResult.training.exercices.map((ex, i) => ({
			nomExercice: ex.nom || ex.nomExercice,
			ordre: i,
			supersetGroup: ex.supersetGroup || 0,
			series: ex.series.map((s, j) => ({ ordre: j, reps: s.reps || 0, poids: s.poids || 0, rpe: s.rpe || 0 }))
		}));

		const result = await sendTemplateToStudent(selectedStudent.id, templateNom, exercisesPayload);
		if (result.success) {
			// Refresh templates
			const tResult = await getStudentTemplates(selectedStudent.id);
			if (tResult.success) setTemplates(tResult.templates);
			showNotif("Template envoyé avec succès");
		} else {
			showNotif("Erreur: " + result.error, 'error');
		}
	};

	// ========== Template folders ==========
	useEffect(() => {
		if (templateListTab !== "coach") return;
		setTemplateFoldersLoading(true);
		getTemplateFolders().then(res => {
			if (res.success) {
				setTemplateFolders(res.folders);
				setTemplateFolderAssoc(res.associations);
			}
		}).finally(() => setTemplateFoldersLoading(false));
	}, [templateListTab]);

	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) return;
		const key = Date.now();
		const res = await saveTemplateFolder(key, newFolderName.trim());
		if (res.success) {
			setTemplateFolders(prev => [...prev, res.folder]);
			setActiveFolderKey(key);
		}
		setNewFolderName("");
		setShowNewFolderInput(false);
	};

	const handleDeleteFolder = async (key) => {
		if (!window.confirm("Supprimer ce dossier ? Les templates ne seront pas supprimés.")) return;
		const res = await deleteTemplateFolder(key);
		if (res.success) {
			setTemplateFolders(prev => prev.map(f => f.Key === key ? { ...f, Supprime: true } : f));
			setTemplateFolderAssoc(prev => prev.map(a => a.FolderKey === key ? { ...a, Supprime: true } : a));
			if (activeFolderKey === key) setActiveFolderKey(null);
		}
	};

	const handleToggleFolderAssign = async (template, folderKey, assign) => {
		const templateKey = template.Key ?? template.key;
		const res = await assignTemplateToFolder(folderKey, templateKey, assign);
		if (!res.success) {
			showNotif("Erreur lors du rangement dans le dossier", "error");
			return;
		}
		// Defensive matching: backend returns camelCase, locally-added rows may differ.
		const matches = (a) =>
			(a.FolderKey ?? a.folderKey) === folderKey &&
			(a.TemplateKey ?? a.templateKey) === templateKey;
		setTemplateFolderAssoc(prev => {
			const existing = prev.find(matches);
			if (existing) {
				return prev.map(a => matches(a) ? { ...a, supprime: !assign, Supprime: !assign } : a);
			}
			if (assign) {
				return [...prev, { folderKey, templateKey, log: Date.now(), supprime: false }];
			}
			return prev;
		});
	};

	// ========== Stats tab ==========
	const fetchStudentStats = async (period) => {
		if (!selectedStudent) return;
		setStatsLoading(true);
		const result = await getStudentStats(selectedStudent.id, period);
		if (result.success) setStatsData(result.data);
		else setStatsData(null);
		setStatsLoading(false);
	};

	const fetchExerciseProgressData = async (exerciseId, period) => {
		if (!selectedStudent || !exerciseId) return;
		const result = await getExerciseProgress(selectedStudent.id, exerciseId, period);
		if (result.success) setExerciseProgressData(result.data);
		else setExerciseProgressData(null);
	};

	// Fetch stats when switching to stats tab or changing student/period
	useEffect(() => {
		if (activeTab === "statistiques" && selectedStudent) {
			fetchStudentStats(statsPeriod);
		}
	}, [activeTab, selectedStudent, statsPeriod]);

	// Reset the per-exercise progression view whenever the focused student changes.
	// Otherwise the previously-loaded chart sticks around and shows data from the
	// previous student under the new student's name.
	useEffect(() => {
		setSelectedProgressExercise(null);
		setExerciseProgressData(null);
		setSelectedProgressExercise2(null);
		setExerciseProgressData2(null);
	}, [selectedStudent?.id]);

	// Fetch exercise progress when exercise selection or page period changes
	useEffect(() => {
		if (selectedProgressExercise) {
			fetchExerciseProgressData(selectedProgressExercise, statsPeriod);
		} else {
			setExerciseProgressData(null);
		}
	}, [selectedProgressExercise, statsPeriod]);

	const fetchExerciseProgressData2 = async (exerciseId, period) => {
		const result = await getExerciseProgress(selectedStudent.id, exerciseId, period);
		if (result.success) setExerciseProgressData2(result.data);
		else setExerciseProgressData2(null);
	};

	useEffect(() => {
		if (selectedProgressExercise2) {
			fetchExerciseProgressData2(selectedProgressExercise2, exerciseProgressPeriod2);
		} else {
			setExerciseProgressData2(null);
		}
	}, [selectedProgressExercise2, exerciseProgressPeriod2]);

	// ========== Overview tab ==========
	const fetchOverviewData = async () => {
		setOverviewLoading(true);
		const [heatRes, scoresRes, alertsRes] = await Promise.all([
			getOverviewHeatmap(overviewPeriod),
			getStudentScores(overviewPeriod),
			getCoachAlerts()
		]);
		if (heatRes.success) setHeatmapData(heatRes.data);
		console.log("scoresRes:", JSON.stringify(scoresRes).substring(0, 500));
		if (scoresRes.success) setStudentScores(scoresRes.data?.students || []);
		if (alertsRes.success) setAlertsData(alertsRes.data?.alerts || alertsRes.alerts || alertsRes.data || null);
		setOverviewLoading(false);
	};

	useEffect(() => {
		if (activeTab === "vue-ensemble") {
			fetchOverviewData();
		}
		if (activeTab === "statistiques" && !studentScores) {
			getStudentScores(overviewPeriod).then(res => { if (res.success) setStudentScores(res.data?.students || []); });
		}
	}, [activeTab, overviewPeriod]);

	// Load coach note when a student is selected in the overview tab
	useEffect(() => {
		if (overviewFilterStudent == null) {
			setCoachNote("");
			setCoachNoteGoal(0);
			return;
		}
		const fetchNote = async () => {
			setCoachNoteLoading(true);
			const res = await getStudentNote(overviewFilterStudent);
			if (res.success) {
				setCoachNote(res.note ?? "");
				setCoachNoteGoal(res.goalSessionsPerWeek ?? 0);
			}
			setCoachNoteLoading(false);
		};
		fetchNote();
	}, [overviewFilterStudent]);

	// Feature 9 — Load exercise overview when a student is selected in vue-ensemble
	useEffect(() => {
		if (overviewFilterStudent && activeTab === "vue-ensemble") {
			setExerciseOverviewLoading(true);
			getStudentExerciseOverview(overviewFilterStudent, 30)
				.then(res => {
					setExerciseOverview(res.success ? res.exercises : null);
					setExerciseOverviewLoading(false);
				});
		} else {
			setExerciseOverview(null);
		}
	}, [overviewFilterStudent, activeTab]);

	// Feature 1 — Load weekly report
	useEffect(() => {
		if (activeTab === "rapport-hebdo") {
			setWeeklyReportLoading(true);
			setWeeklyReportError(null);
			getWeeklyReport(weekOffset).then(res => {
				if (res.success) {
					setWeeklyReport(res.data);
					setWeeklyReportError(null);
				} else {
					setWeeklyReport(null);
					setWeeklyReportError(res.error || "Échec de chargement");
				}
				setWeeklyReportLoading(false);
			});
		}
	}, [activeTab, weekOffset]);

	// Feature 5 — Load student checkins for rapport-eleve tab
	useEffect(() => {
		if (activeTab === "rapport-eleve" && selectedStudent) {
			if (!statsData) fetchStudentStats(90);
			setStudentCheckinsLoading(true);
			getCoachStudentCheckins(selectedStudent.id, 8).then(res => {
				setStudentCheckins(res.success ? res.checkins : []);
				setStudentCheckinsLoading(false);
			});
		}
	}, [activeTab, selectedStudent]);

	const handleSaveCoachNote = async () => {
		if (overviewFilterStudent == null) return;
		setCoachNoteSaving(true);
		await saveStudentNote(overviewFilterStudent, coachNote, coachNoteGoal);
		setCoachNoteSaving(false);
	};

	// Feature 6 — Broadcast message to all students
	const handleBroadcastSend = async () => {
		if (!broadcastMessage.trim()) return;
		let count = 0;
		for (const student of (studentScores || [])) {
			const res = await sendMessage(student.id, broadcastMessage.trim());
			if (res && res.success) count++;
		}
		setShowBroadcast(false);
		setBroadcastMessage("");
		showNotif(`Message envoyé à ${count} élève${count > 1 ? "s" : ""} ✓`, "success");
	};

	const openTrainingDetailModal = async (studentId, studentName, dateStr) => {
		setTrainingDetailLoading(true);
		setTrainingDetailModal({ studentName, trainings: [], previousTraining: null });

		const dateParts = dateStr.split("-");
		const d = { year: parseInt(dateParts[0]), month: parseInt(dateParts[1]), day: parseInt(dateParts[2]) };
		const detailRes = await getTrainingDetails(studentId, d.year, d.month, d.day);
		if (!detailRes.success || !detailRes.trainings || detailRes.trainings.length === 0) {
			setTrainingDetailModal(null);
			setTrainingDetailLoading(false);
			return;
		}

		// Fetch all trainings for that day in parallel
		const fullResults = await Promise.all(
			detailRes.trainings.map(t => getTrainingFull(studentId, t.id))
		);
		const trainings = fullResults.filter(r => r.success).map(r => r.training);
		if (trainings.length === 0) {
			setTrainingDetailModal(null);
			setTrainingDetailLoading(false);
			return;
		}

		// Fetch previous performances per exercise for each training (one query each)
		const prevPerfsResults = await Promise.all(
			trainings.map(t => getTrainingPreviousPerfs(studentId, t.id))
		);
		// Merge all results into one map: idExercice -> series[]
		const previousPerfs = {};
		prevPerfsResults.forEach(r => {
			if (r.success && r.data) {
				Object.entries(r.data).forEach(([idEx, series]) => {
					if (!previousPerfs[idEx]) previousPerfs[idEx] = series;
				});
			}
		});

		setTrainingDetailModal({ studentName, trainings, previousPerfs });
		setTrainingDetailLoading(false);
	};

	const renderCalendar = () => {
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();
		const daysInMonth = getDaysInMonth(year, month);
		const firstDay = getFirstDayOfMonth(year, month);

		const days = [];

		// Empty cells for days before the first day of the month
		for (let i = 1; i < firstDay; i++) {
			days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
		}

		// Days of the month
		for (let day = 1; day <= daysInMonth; day++) {
			const hasTraining = trainingDays.includes(day);
			const isToday =
				new Date().getDate() === day &&
				new Date().getMonth() === month &&
				new Date().getFullYear() === year;
			const isSelected = selectedDay === day;
			const dateTs = Date.UTC(year, month, day, 12, 0, 0);
			const isMultiSelected = multiSelectMode && selectedDates.includes(dateTs);

			days.push(
				<div
					key={day}
					className={`calendar-day ${hasTraining ? "has-training" : ""} ${isToday ? "today" : ""} ${isSelected && !multiSelectMode ? "selected" : ""} ${isMultiSelected ? "multi-selected" : ""} ${multiSelectMode ? "multi-mode" : ""}`}
					onClick={() => handleDayClick(day)}
				>
					<span className="day-number">{day}</span>
					{hasTraining && !multiSelectMode && <span className="training-dot"></span>}
					{isMultiSelected && <span className="multi-check">&#10003;</span>}
				</div>
			);
		}

		return days;
	};

	if (loading) {
		return (
			<div className="page">
				<div className="coach-container">
					<p className="coach-loading">Chargement...</p>
				</div>
			</div>
		);
	}

	if (subscriptionBlocked) {
		return (
			<div className="page">
				<div className="coach-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center", padding: "40px" }}>
					<div style={{ fontSize: "48px", marginBottom: "20px" }}>🔒</div>
					<h2 style={{ color: "#F87B1B", marginBottom: "15px", fontSize: "24px" }}>Abonnement Pro requis</h2>
					<p style={{ color: "#b0b0b0", maxWidth: "500px", lineHeight: "1.6", marginBottom: "30px" }}>
						Le Dashboard Coach est réservé aux abonnés Pro. Passez à l'offre Pro pour gérer vos élèves depuis le web.
					</p>
					<button
						className="coach-btn-primary"
						onClick={() => navigate("/pro")}
						style={{ padding: "12px 30px", fontSize: "16px" }}
					>
						Voir les abonnements
					</button>
				</div>
			</div>
		);
	}

	// Lookup id → exercice pour retrouver le picto dans l'éditeur de séance
	// (où seul l'idExercice est stocké sur chaque exo de la séance).
	const exerciseById = new Map();
	exercises.forEach((e) => { if (e?.id != null) exerciseById.set(e.id, e); });

	return (
		<div className="page">
			{/* Notification banner */}
			{notification && (
				<div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:notification.type==='success'?'#30a49a':'#e74c3c',color:'white',padding:'12px 24px',borderRadius:12,fontWeight:600,boxShadow:'0 4px 20px rgba(0,0,0,0.3)'}}>
					{notification.msg}
				</div>
			)}

			<div className="coach-shell">
				<CoachTopbar
					user={user}
					onOpenSearch={() => setShowSearch(true)}
					alertsCount={Array.isArray(alertsData) ? alertsData.length : 0}
				/>
				<div className="coach-shell-body">
					<CoachSidebar
						view={view}
						onSelectView={handleSelectView}
						students={students}
						selectedStudent={selectedStudent}
						user={user}
						studentScores={studentScores}
						onSelectStudent={handleSelectStudentFromSidebar}
						onStudentMessage={handleStudentMessage}
						onStudentNote={handleStudentNote}
						onStudentPlan={handleStudentPlan}
					/>
					<div className="coach-workspace">

					{/* Workspace header: contextual breadcrumb + internal tabs (student view) */}
					<div className="coach-workspace-header">
						<div className="coach-workspace-crumb">
							{view === "dashboard" && <span>Dashboard</span>}
							{view === "weekly" && <span>Rapport hebdo</span>}
							{view === "templates" && <span>Templates</span>}
							{view === "student" && selectedStudent && (
								<>
									<span className="coach-workspace-crumb-context">Élèves ›</span>
									<span>{selectedStudent.prenom} {selectedStudent.nom}</span>
								</>
							)}
						</div>
						{view === "student" && selectedStudent && (
							<div className="coach-workspace-header-actions">
								{studentTab === "stats" && (
									<>
										<div className="stats-period-selector">
											<button className={`stats-period-btn ${statsPeriod === 15 ? "active" : ""}`} onClick={() => setStatsPeriod(15)}>15 jours</button>
											<button className={`stats-period-btn ${statsPeriod === 30 ? "active" : ""}`} onClick={() => setStatsPeriod(30)}>30 jours</button>
											<button className={`stats-period-btn ${statsPeriod === 90 ? "active" : ""}`} onClick={() => setStatsPeriod(90)}>90 jours</button>
										</div>
										<button
											className="stats-period-btn"
											onClick={() => window.print()}
											title="Exporter en PDF"
										>
											Exporter PDF
										</button>
									</>
								)}
								<div className="coach-workspace-tabs">
									<button className={`coach-workspace-tab ${studentTab === "stats" ? "active" : ""}`} onClick={() => setStudentTab("stats")}>Statistiques</button>
									<button className={`coach-workspace-tab ${studentTab === "editor" ? "active" : ""}`} onClick={() => { if (multiSelectMode) cancelMultiDateDuplicate(); setStudentTab("editor"); }}>Éditeur</button>
									{!isSelf && (
										<button className={`coach-workspace-tab ${studentTab === "notes" ? "active" : ""}`} onClick={() => setStudentTab("notes")}>Notes</button>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Templates view — file explorer with folders */}
					{view === "templates" && !libraryMode && (
						<div className="coach-templates-view">
							<div className="coach-templates-folders">
								<div className="coach-templates-folders-header">Dossiers</div>
								<div
									onClick={() => setActiveFolderKey(null)}
									className={`coach-templates-folder ${activeFolderKey === null ? "active" : ""}`}
								>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}>
										<rect x="3" y="3" width="18" height="18" rx="2"/>
										<line x1="3" y1="9" x2="21" y2="9"/>
									</svg>
									<span className="coach-templates-folder-name">Tous</span>
									<span className="coach-templates-folder-count">{coachTemplates.length}</span>
								</div>

								{templateFolders.filter(f => !(f.Supprime ?? f.supprime)).map(folder => {
									const folderKey = folder.Key ?? folder.key;
									const folderName = folder.NomDossier ?? folder.nomDossier ?? "Dossier";
									const count = templateFolderAssoc.filter(a => {
										const aSupprime = a.Supprime ?? a.supprime;
										const aFolderKey = a.FolderKey ?? a.folderKey;
										return !aSupprime && aFolderKey === folderKey;
									}).length;
									const isActive = activeFolderKey === folderKey;
									return (
										<div
											key={folderKey}
											onClick={() => setActiveFolderKey(folderKey)}
											className={`coach-templates-folder ${isActive ? "active" : ""}`}
										>
											<svg width="14" height="14" viewBox="0 0 24 24" fill={isActive ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: isActive ? 1 : 0.6 }}>
												<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
											</svg>
											<span className="coach-templates-folder-name">{folderName}</span>
											<span className="coach-templates-folder-count">{count}</span>
											<button
												onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folderKey); }}
												className="coach-templates-folder-delete"
												title="Supprimer le dossier"
											>×</button>
										</div>
									);
								})}

								<div className="coach-templates-folder-new">
									{showNewFolderInput ? (
										<div className="coach-templates-folder-new-form">
											<input
												autoFocus
												value={newFolderName}
												onChange={e => setNewFolderName(e.target.value)}
												onKeyDown={e => {
													if (e.key === "Enter") handleCreateFolder();
													if (e.key === "Escape") { setShowNewFolderInput(false); setNewFolderName(""); }
												}}
												placeholder="Nom du dossier"
												className="coach-templates-folder-new-input"
											/>
											<div className="coach-templates-folder-new-buttons">
												<button onClick={handleCreateFolder} className="coach-templates-folder-new-ok">Créer</button>
												<button onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); }} className="coach-templates-folder-new-cancel">Annuler</button>
											</div>
										</div>
									) : (
										<button onClick={() => setShowNewFolderInput(true)} className="coach-templates-folder-new-btn">
											<span style={{ fontSize: "1rem", lineHeight: 1 }}>+</span> Nouveau dossier
										</button>
									)}
								</div>
							</div>

							<div className="coach-templates-list">
								<div className="coach-templates-listhead">
									<div className="coach-templates-breadcrumb">
										<span>Mes templates</span>
										{activeFolderKey !== null && (() => {
											const f = templateFolders.find(x => (x.Key ?? x.key) === activeFolderKey);
											return (
												<>
													<span className="coach-templates-breadcrumb-sep">›</span>
													<span className="coach-templates-breadcrumb-active">{f ? (f.NomDossier ?? f.nomDossier ?? "Dossier") : ""}</span>
												</>
											);
										})()}
									</div>
									<button className="coach-templates-new-btn" onClick={openNewLibraryTemplate}>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
											<line x1="12" y1="5" x2="12" y2="19" />
											<line x1="5" y1="12" x2="19" y2="12" />
										</svg>
										Nouveau template
									</button>
								</div>

								<div className="coach-templates-grid">
									{loadingTemplates || templateFoldersLoading ? (
										<div className="coach-templates-empty">Chargement…</div>
									) : (() => {
										const displayed = activeFolderKey === null
											? coachTemplates
											: coachTemplates.filter(t => templateFolderAssoc.some(a => {
												const aSupprime = a.Supprime ?? a.supprime;
												const aFolderKey = a.FolderKey ?? a.folderKey;
												const aTemplateKey = a.TemplateKey ?? a.templateKey;
												const tKey = t.Key ?? t.key;
												return !aSupprime && aFolderKey === activeFolderKey && aTemplateKey === tKey;
											}));

										if (displayed.length === 0) return (
											<div className="coach-templates-empty">
												{activeFolderKey === null ? "Aucun template" : "Aucun template dans ce dossier"}
											</div>
										);

										// Keys of non-deleted folders only — used to ignore associations
										// pointing to folders that were since deleted.
										const validFolderKeys = new Set(
											(templateFolders || [])
												.filter(f => !(f.Supprime ?? f.supprime))
												.map(f => (f.Key ?? f.key))
										);

										return displayed.map(template => {
											const tplId = template.Id ?? template.id;
											const tplKey = template.Key ?? template.key;
											const tplNom = template.Nom ?? template.nom ?? "Sans nom";
											const tplExercises = template.ListExercice ?? template.listExercice ?? template.exercices ?? [];
											const tplFolderKeys = new Set(
												(templateFolderAssoc || [])
													.filter(a => !(a.Supprime ?? a.supprime) && (a.TemplateKey ?? a.templateKey) === tplKey)
													.map(a => (a.FolderKey ?? a.folderKey))
													.filter(fk => validFolderKeys.has(fk))
											);
											const totalSeries = tplExercises.reduce((sum, ex) => {
												if (typeof ex === "string") return sum;
												const series = ex.ListSerie ?? ex.listSerie ?? ex.series ?? [];
												return sum + (Array.isArray(series) ? series.length : 0);
											}, 0);
											return (
												<div key={tplId} className="coach-templates-card">
													<TemplateFolderMenu
														folders={templateFolders.filter(f => !(f.Supprime ?? f.supprime))}
														activeFolderKeys={tplFolderKeys}
														onToggle={(folderKey, assign) => handleToggleFolderAssign({ ...template, Key: tplKey }, folderKey, assign)}
													/>
													<div className="coach-templates-card-main" onClick={() => openLibraryTemplate(tplId)}>
														<div className="coach-templates-card-header">
															<div className="coach-templates-card-name">{tplNom}</div>
															<div className="coach-templates-card-stats">
																{tplExercises.length} exo{tplExercises.length > 1 ? "s" : ""}
																{totalSeries > 0 && ` · ${totalSeries} sér.`}
															</div>
														</div>
														{tplExercises.length > 0 && (
															<ul className="coach-templates-card-exercises">
																{tplExercises.slice(0, 6).map((ex, i) => {
																	if (typeof ex === "string") {
																		return <li key={i}><span className="ex-name">{ex}</span></li>;
																	}
																	const exName = ex.NomExercice ?? ex.nomExercice ?? ex.nom ?? "";
																	const exSeries = ex.ListSerie ?? ex.listSerie ?? ex.series ?? [];
																	const seriesCount = Array.isArray(exSeries) ? exSeries.length : 0;
																	return (
																		<li key={i}>
																			<span className="ex-name">{exName}</span>
																			{seriesCount > 0 && <span className="ex-series">× {seriesCount}</span>}
																		</li>
																	);
																})}
																{tplExercises.length > 6 && (
																	<li className="more">+ {tplExercises.length - 6} autre{tplExercises.length - 6 > 1 ? "s" : ""}</li>
																)}
															</ul>
														)}
													</div>
												</div>
											);
										});
									})()}
								</div>
							</div>
						</div>
					)}

					{/* Student Notes view */}
					{view === "student" && studentTab === "notes" && selectedStudent && !isSelf && (
						<div className="coach-notes-view">
							<div className="coach-notes-card">
								<h3 className="coach-notes-title">
									Note privée
									<span className="coach-notes-subtitle">non visible par l'élève</span>
								</h3>
								{coachNoteLoading ? (
									<div className="coach-notes-loading">Chargement…</div>
								) : (
									<>
										<textarea
											value={coachNote}
											onChange={(e) => setCoachNote(e.target.value)}
											rows={18}
											placeholder="Observations, points à travailler, historique médical…"
											className="coach-notes-textarea"
										/>

										<div className="coach-notes-goal-row">
											<label className="coach-notes-goal-label">Objectif séances/semaine</label>
											<select
												value={coachNoteGoal}
												onChange={(e) => setCoachNoteGoal(parseInt(e.target.value))}
												className="coach-notes-goal-select"
											>
												<option value={0}>Désactivé</option>
												{[1,2,3,4,5,6,7].map(n => (
													<option key={n} value={n}>{n} séance{n > 1 ? "s" : ""}</option>
												))}
											</select>
										</div>

										{coachNoteGoal > 0 && (() => {
											const scoreRow = (studentScores || []).find(st => st.id === selectedStudent.id);
											const weeklySessions = scoreRow?.weeklySessions || [];
											const currentWeekCount = weeklySessions.length > 0 ? weeklySessions[weeklySessions.length - 1] : 0;
											const pct = Math.min(100, Math.round((currentWeekCount / coachNoteGoal) * 100));
											const barColor = pct >= 100 ? "#4ade80" : pct >= 60 ? "#F87B1B" : "#ef4444";
											return (
												<div className="coach-notes-progress">
													<div className="coach-notes-progress-label">
														Progression cette semaine : <strong>{currentWeekCount}/{coachNoteGoal}</strong>
													</div>
													<div className="coach-notes-progress-bar">
														<div className="coach-notes-progress-fill" style={{ width: `${pct}%`, background: barColor }} />
													</div>
												</div>
											);
										})()}

										<div className="coach-notes-actions">
											<button
												className={`coach-notes-checkin-btn coach-notes-checkin-btn-${checkinStatus}`}
												disabled={checkinStatus === "sending" || checkinStatus === "sent"}
												onClick={async () => {
													setCheckinStatus("sending");
													const res = await requestCheckin(selectedStudent.id);
													if (res.success) {
														setCheckinStatus("sent");
														showNotif("Notification de check-in envoyée");
														setTimeout(() => setCheckinStatus("idle"), 3500);
													} else {
														setCheckinStatus("error");
														showNotif(res.message || "Erreur lors de l'envoi", "error");
														setTimeout(() => setCheckinStatus("idle"), 4500);
													}
												}}
											>
												{checkinStatus === "sending" && "Envoi…"}
												{checkinStatus === "sent" && "✓ Check-in demandé"}
												{checkinStatus === "error" && "✗ Échec de l'envoi"}
												{checkinStatus === "idle" && "Demander un check-in"}
											</button>
											<button
												className="coach-notes-save-btn"
												onClick={handleSaveCoachNote}
												disabled={coachNoteSaving}
											>
												{coachNoteSaving ? "Sauvegarde…" : "Sauvegarder"}
											</button>
										</div>
									</>
								)}
							</div>
						</div>
					)}

			{/* Multi-date confirmation bar */}
			{multiSelectMode && (
				<div className="multi-date-bar">
					<span>Duplication multi-dates: {selectedDates.length} date(s) sélectionnée(s)</span>
					<div className="multi-date-bar-buttons">
						<button className="multi-date-bar-btn cancel" onClick={cancelMultiDateDuplicate}>Annuler</button>
						<button className="multi-date-bar-btn confirm" onClick={confirmMultiDateDuplicate} disabled={selectedDates.length === 0 || savingTraining}>
							{savingTraining ? "Duplication..." : "Confirmer"}
						</button>
					</div>
				</div>
			)}

			{/* ===== TAB: Editeur (focus mode) ===== */}
			{activeTab === "editeur" && (
				<EditorShell
					user={user}
					studentName={libraryMode ? null : (selectedStudent ? `${selectedStudent.prenom} ${selectedStudent.nom}` : null)}
					trainingName={editorTraining?.nom || (editorTraining ? "Sans titre" : null)}
					isTemplate={libraryMode || editorMode === "editTemplate"}
					editorMode={editorMode}
					lastModifiedAt={editorLastModifiedAt}
					savingTraining={savingTraining}
					canDelete={libraryMode ? (!!editorTraining && editorTraining.id != null && editorTraining.id !== -1) : ((editorMode === "edit" || editorMode === "editTemplate") && !!editorTraining)}
					canSave={!!editorTraining}
					hasTraining={!!editorTraining}
					hasActions={!libraryMode}
					navItems={libraryMode ? [{ key: "templates", label: "Templates" }, { key: "exercices", label: "Exercices" }] : undefined}
					onExit={() => {
						if (editorTraining && trainingModified) {
							const confirmed = window.confirm("Une séance est en cours d'édition. Quitter l'éditeur la fermera sans sauvegarder. Continuer ?");
							if (!confirmed) return;
						}
						if (libraryMode) {
							exitLibraryEditor();
							return;
						}
						setEditorTraining(null);
						setEditorMode(null);
						setTrainingModified(false);
						setLoadedFromTemplate(false);
						setLoadedTemplateName("");
						setEditorPanel(null);
						if (selectedStudent) {
							setStudentTab("stats");
						} else {
							setView("dashboard");
						}
					}}
					onOpenSearch={() => setShowSearch(true)}
					onDupliquer={() => editorTraining && openDatePickerModal('duplicate', editorTraining)}
					onPlanifier={openPlanification}
					onSave={libraryMode ? handleSaveLibraryTemplate : (editorMode === "editTemplate" ? handleSaveTemplate : handleSaveTraining)}
					onSaveAsTemplate={libraryMode ? undefined : openTemplateNameModal}
					onDelete={libraryMode ? handleDeleteLibraryTemplate : (editorMode === "editTemplate" ? handleDeleteTemplate : handleDeleteTraining)}
					onCancel={() => {
						if (trainingModified) {
							const msg = editorMode === "create"
								? "Annuler la création de cette séance ? Les changements seront perdus."
								: "Fermer sans sauvegarder ? Les modifications non enregistrées seront perdues.";
							const confirmed = window.confirm(msg);
							if (!confirmed) return;
						}
						if (libraryMode) {
							exitLibraryEditor();
							return;
						}
						setEditorTraining(null);
						setEditorMode(null);
						setTrainingModified(false);
						setLoadedFromTemplate(false);
						setLoadedTemplateName("");
					}}
					activePanel={editorPanel}
					setActivePanel={setEditorPanel}
					panels={{
						calendrier: (
							<CalendarPanel
								currentDate={currentDate}
								trainingDays={trainingDays}
								selectedDay={selectedDay}
								loading={loadingCalendar}
								dayTrainings={trainingDetails}
								dayTrainingsLoading={loadingDetails}
								onPrevMonth={goToPreviousMonth}
								onNextMonth={goToNextMonth}
								onDayClick={(day) => setSelectedDay(day)}
								onSelectTraining={(t) => {
									openInEditor(t.id, false);
									setEditorPanel(null);
								}}
							/>
						),
						exercices: (
							<ExercisesPanel
								exercises={libraryMode ? coachExercises : exercises}
								loading={loadingExercises}
								editorTraining={editorTraining}
								studentName={selectedStudent?.prenom}
								coachExercises={coachExercises}
								onAddExercise={addExerciseToEditor}
								onCreateNew={() => setShowCreateExerciseModal(true)}
								onImportFromLibrary={libraryMode ? undefined : handleImportExerciseToStudent}
								libraryMode={libraryMode}
							/>
						),
						templates: (
							<TemplatesPanel
								studentTemplates={templates}
								coachTemplates={coachTemplates}
								templateFolders={templateFolders}
								templateFolderAssoc={templateFolderAssoc}
								studentName={selectedStudent?.prenom}
								loading={loadingTemplates}
								libraryMode={libraryMode}
								onOpenTemplate={(t) => {
									if (libraryMode) {
										// Library mode: load the chosen coach template into the current template editor.
										loadCoachTemplateIntoEditor(t);
										return;
									}
									// Student mode: load it as a new session in the editor.
									openTemplateAsNewSession(t);
									setEditorPanel(null);
								}}
								onImportTemplate={handleImportTemplateToStudent}
							/>
						),
						stats: (
							<StatsPanel
								studentId={selectedStudent?.id}
								editorTraining={editorTraining}
							/>
						),
					}}
				>
					<div className="editor-shell-content">
						{(() => {
							if (loadingEditor) {
								return (
									<div className="editor-blank">
										<div className="editor-blank-dots" aria-label="Chargement">
											<span /><span /><span />
										</div>
										{editorLoadingMessage && (
											<div className="editor-blank-loading-msg">{editorLoadingMessage}</div>
										)}
									</div>
								);
							}

							if (!editorTraining) {
								return (
									<div className="editor-blank">
										<svg className="editor-blank-illu" viewBox="0 0 140 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
											<rect x="0.75" y="22.75" width="6" height="14.5" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
											<rect x="10.75" y="13.75" width="11" height="32.5" rx="2.5" stroke="currentColor" strokeWidth="1.25" />
											<line x1="22" y1="30" x2="118" y2="30" stroke="currentColor" strokeWidth="1.25" />
											<rect x="118.25" y="13.75" width="11" height="32.5" rx="2.5" stroke="currentColor" strokeWidth="1.25" />
											<rect x="133.25" y="22.75" width="6" height="14.5" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
											<line x1="35" y1="30" x2="105" y2="30" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 3" opacity="0.5" />
										</svg>
										<div className="editor-blank-text">
											<h2 className="editor-blank-title">Aucune séance ouverte</h2>
											<p className="editor-blank-hint">
												Pars d'un template, ouvre une séance depuis le calendrier
												ou crée-en une vide.
											</p>
										</div>
										<div className="editor-blank-actions">
											{selectedStudent && (
												<button className="editor-blank-action primary" onClick={createNewTraining}>
													<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<line x1="12" y1="5" x2="12" y2="19" />
														<line x1="5" y1="12" x2="19" y2="12" />
													</svg>
													Nouvelle séance
												</button>
											)}
											<button className="editor-blank-action" onClick={() => setEditorPanel("templates")}>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
													<rect x="3" y="3" width="7" height="7" rx="1" />
													<rect x="14" y="3" width="7" height="7" rx="1" />
													<rect x="3" y="14" width="7" height="7" rx="1" />
													<rect x="14" y="14" width="7" height="7" rx="1" />
												</svg>
												Charger un template
											</button>
											<button className="editor-blank-action" onClick={() => setEditorPanel("calendrier")}>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
													<rect x="3" y="4" width="18" height="18" rx="2" />
													<line x1="16" y1="2" x2="16" y2="6" />
													<line x1="8" y1="2" x2="8" y2="6" />
													<line x1="3" y1="10" x2="21" y2="10" />
												</svg>
												Ouvrir le calendrier
											</button>
										</div>
									</div>
								);
							}

							// Compute KPIs
							const visibleExos = (editorTraining.exercices || []).filter((e) => e.supprime !== 1);
							const totalSeries = visibleExos.reduce((s, ex) => s + ((ex.series || []).length), 0);
							const totalTonnage = visibleExos.reduce(
								(sum, ex) =>
									sum +
									(ex.series || []).reduce(
										(s, sr) => s + effectiveWeight(sr.poids, sr.poidsDec) * (parseInt(sr.reps) || 0),
										0
									),
								0
							);
							const rpeValues = visibleExos.flatMap((ex) =>
								(ex.series || []).map((s) => parseFloat(s.rpe) || 0)
							).filter((r) => r > 0);
							const avgRpe = rpeValues.length > 0
								? rpeValues.reduce((a, b) => a + b, 0) / rpeValues.length
								: null;

							// Estimated duration: per exercise series count × (work + rest) using catalog rest time when available.
							// Fallback: match by name; otherwise 90 s rest. Per-series work time fixed at 30 s.
							const exoById = new Map();
							const exoByName = new Map();
							(exercises || []).forEach((e) => {
								if (e.id != null) exoById.set(e.id, e);
								const k = (e.nomExercice || "").trim().toLowerCase();
								if (k) exoByName.set(k, e);
							});
							const WORK_SEC = 30;
							const DEFAULT_REST_SEC = 90;
							const estDurationSec = visibleExos.reduce((total, ex) => {
								const ref =
									(ex.idExercice != null && exoById.get(ex.idExercice)) ||
									exoByName.get((ex.nom || "").trim().toLowerCase());
								const restSec = ref && ref.tempsRepos > 0 ? ref.tempsRepos : DEFAULT_REST_SEC;
								const nSer = (ex.series || []).length;
								return total + nSer * (WORK_SEC + restSec);
							}, 0);
							const estDuration = Math.round(estDurationSec / 60);

							const formatTonnage = (kg) => {
								if (kg <= 0) return "0";
								if (kg < 1000) return Math.round(kg).toLocaleString("fr-FR");
								return `${(kg / 1000).toFixed(1).replace(".", ",")}t`;
							};
							const formatDuration = (min) => {
								if (!min || min <= 0) return "—";
								if (min < 60) return `${Math.round(min)}'`;
								const h = Math.floor(min / 60);
								const m = Math.round(min % 60);
								return m > 0 ? `${h}h ${String(m).padStart(2, "0")}'` : `${h}h`;
							};

							const dateLabel = editorMode === "edit" && editorTraining.date
								? new Date(editorTraining.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
								: null;

							return (
								<>
									{loadedFromTemplate && (
										<div className="editor-template-banner">
											<span className="editor-template-banner-tag">Copie de template</span>
											<span>
												Cette séance est dérivée de <strong>« {loadedTemplateName} »</strong>. Les modifications n'affectent pas le modèle original.
											</span>
										</div>
									)}

									<header className="editor-session-header">
										<div className="editor-session-eyebrow">
											{libraryMode ? (
												<span className="editor-session-eyebrow-name">Ma bibliothèque</span>
											) : selectedStudent && (
												<span className="editor-session-eyebrow-name">
													{selectedStudent.prenom} {selectedStudent.nom}
												</span>
											)}
											<span className="editor-session-eyebrow-dot" aria-hidden="true">·</span>
											<span className="editor-session-eyebrow-kind">
												{libraryMode || editorMode === "editTemplate" || editorMode === "createTemplate"
													? "Template"
													: editorMode === "create" ? "Nouvelle séance" : "Séance"}
											</span>
										</div>
										{libraryMode ? (
											<input
												className="editor-session-title editor-session-title-input"
												value={editorTraining.nom || ""}
												placeholder="Nom du template"
												onChange={(e) => {
													setEditorTraining((prev) => ({ ...prev, nom: e.target.value }));
													setTrainingModified(true);
												}}
											/>
										) : (
											<h1 className="editor-session-title">{editorTraining.nom || "Sans titre"}</h1>
										)}
										<div className="editor-session-meta">
											{libraryMode ? (
												<span className="editor-session-meta-muted">Modèle réutilisable — assignable à plusieurs élèves</span>
											) : dateLabel ? (
												<span className="editor-session-date">
													<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<rect x="3" y="4" width="18" height="18" rx="2" />
														<line x1="16" y1="2" x2="16" y2="6" />
														<line x1="8" y1="2" x2="8" y2="6" />
														<line x1="3" y1="10" x2="21" y2="10" />
													</svg>
													{dateLabel}
												</span>
											) : editorMode === "editTemplate" ? (
												<span className="editor-session-meta-muted">Modèle réutilisable</span>
											) : (
												<button className="editor-session-date-cta" onClick={openPlanification}>
													<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
														<rect x="3" y="4" width="18" height="18" rx="2" />
														<line x1="16" y1="2" x2="16" y2="6" />
														<line x1="8" y1="2" x2="8" y2="6" />
														<line x1="3" y1="10" x2="21" y2="10" />
													</svg>
													Planifier cette séance
												</button>
											)}
										</div>
									</header>

									{/* Retour de séance de l'élève (lecture seule) : humeur + retour libre
									    renseignés en fin de séance côté app. Uniquement pour les vraies
									    séances (pas les templates). */}
									{!libraryMode && editorTraining.estTemplate !== true &&
									 editorTraining.noteSeance != null && (() => {
										const moodEmojis = ["", "😓", "😔", "😐", "😊", "💪"];
										const moodLabels = ["", "Très difficile", "Difficile", "Moyenne", "Bonne", "Excellente"];
										const idx = Math.max(1, Math.min(5, editorTraining.noteSeance));
										return (
											<div className="editor-mood-banner">
												<div className="editor-mood-emoji">{moodEmojis[idx]}</div>
												<div className="editor-mood-content">
													<div className="editor-mood-label">
														Humeur de {selectedStudent?.prenom || "l'élève"} : <strong>{moodLabels[idx]}</strong>
													</div>
													{editorTraining.retourSeance && editorTraining.retourSeance.trim().length > 0 && (
														<div className="editor-mood-retour">« {editorTraining.retourSeance} »</div>
													)}
												</div>
											</div>
										);
									})()}

									<dl className="editor-session-kpis">
										<div className="editor-kpi">
											<dt className="editor-kpi-label">Exos</dt>
											<dd className="editor-kpi-value">{visibleExos.length}</dd>
										</div>
										<div className="editor-kpi">
											<dt className="editor-kpi-label">Séries</dt>
											<dd className="editor-kpi-value">{totalSeries}</dd>
										</div>
										{!libraryMode && (
											<>
												<div className="editor-kpi">
													<dt className="editor-kpi-label">Tonnage</dt>
													<dd className="editor-kpi-value editor-kpi-value-accent">{formatTonnage(totalTonnage)}</dd>
												</div>
												<div className="editor-kpi">
													<dt className="editor-kpi-label">RPE moy.</dt>
													<dd className="editor-kpi-value">{avgRpe != null ? avgRpe.toFixed(1).replace(".", ",") : "—"}</dd>
												</div>
												<div className="editor-kpi" title="Estimation basée sur le temps de repos configuré par exercice (+ 30s d'exécution par série)">
													<dt className="editor-kpi-label">Durée estimée</dt>
													<dd className="editor-kpi-value">{formatDuration(estDuration)}</dd>
												</div>
											</>
										)}
									</dl>

									{visibleExos.length === 0 ? (
										<div className="editor-empty-exercices">
											<svg className="editor-empty-exercices-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
												<rect x="2" y="11" width="2" height="2" rx="0.5" />
												<rect x="4.5" y="9" width="2" height="6" rx="0.5" />
												<line x1="7" y1="12" x2="17" y2="12" />
												<rect x="17.5" y="9" width="2" height="6" rx="0.5" />
												<rect x="20" y="11" width="2" height="2" rx="0.5" />
											</svg>
											<h3 className="editor-empty-exercices-title">Aucun exercice pour l'instant</h3>
											<p className="editor-empty-exercices-hint">
												Ajoute un exercice depuis le panneau <strong>Exercices</strong> (icône <code>≡</code> à gauche) ou charge un <strong>template</strong> existant.
											</p>
											<div className="editor-empty-exercices-actions">
												<button className="editor-blank-action primary" onClick={() => setEditorPanel("exercices")}>
													<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<line x1="12" y1="5" x2="12" y2="19" />
														<line x1="5" y1="12" x2="19" y2="12" />
													</svg>
													Ajouter un exercice
												</button>
												<button className="editor-blank-action" onClick={() => setEditorPanel("templates")}>
													<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<rect x="3" y="3" width="7" height="7" rx="1" />
														<rect x="14" y="3" width="7" height="7" rx="1" />
														<rect x="3" y="14" width="7" height="7" rx="1" />
														<rect x="14" y="14" width="7" height="7" rx="1" />
													</svg>
													Charger un template
												</button>
											</div>
										</div>
									) : (
										<>
											<div className={`editor-exercises ${libraryMode ? "is-template" : ""}`}>
												{(() => {
													// Group consecutive exos sharing the same supersetGroup into render blocks.
													// Pending (empty) supersets are appended at the end as drop placeholders.
													const blocks = [];
													const seenGroups = new Set();
													let i = 0;
													while (i < visibleExos.length) {
														const ex = visibleExos[i];
														const grp = ex.supersetGroup || 0;
														if (grp === 0) {
															blocks.push({ type: "standalone", exo: ex, visibleIndex: i });
															i++;
														} else {
															const members = [{ exo: ex, visibleIndex: i }];
															let j = i + 1;
															while (j < visibleExos.length && (visibleExos[j].supersetGroup || 0) === grp) {
																members.push({ exo: visibleExos[j], visibleIndex: j });
																j++;
															}
															blocks.push({ type: "superset", groupId: grp, members });
															seenGroups.add(grp);
															i = j;
														}
													}
													for (const grpId of pendingSupersets) {
														if (!seenGroups.has(grpId)) {
															blocks.push({ type: "superset", groupId: grpId, members: [] });
														}
													}

													const renderExoCard = ({ exo, visibleIndex, inSuperset }) => {
														const tonnage = (exo.series || []).reduce(
															(sum, s) => sum + effectiveWeight(s.poids, s.poidsDec) * (parseInt(s.reps) || 0),
															0
														);
														const seriesCount = (exo.series || []).length;
														return (
															<div
																key={exo.localId || exo.id}
																className={`editor-exercise-card ${draggedExercise === visibleIndex ? "dragging" : ""}`}
																draggable
																onDragStart={(e) => handleDragStart(e, visibleIndex)}
																onDragOver={(e) => handleDragOver(e, visibleIndex)}
																onDragEnd={handleDragEnd}
															>
																<div className={`editor-exercise-card-header ${libraryMode ? "is-template" : ""}`}>
																	<span className="editor-exercise-card-drag" title="Glisser pour réordonner / dans un superset">≡</span>
																	<span className="editor-exercise-card-num">{visibleIndex + 1}</span>
																	<ExerciseThumb exercise={exerciseById.get(exo.idExercice) || { nomExercice: exo.nom }} size={72} />
																	<div className="editor-exercise-card-title-wrap">
																		<span className="editor-exercise-card-title">{exo.nom}</span>
																		{!libraryMode && (
																			<span className="editor-exercise-card-stats">
																				{seriesCount} série{seriesCount > 1 ? "s" : ""}
																				{tonnage > 0 && ` · ${tonnage.toLocaleString("fr-FR")} kg-rep`}
																			</span>
																		)}
																	</div>
																	{libraryMode && (
																		<TemplateSeriesControl
																			count={seriesCount}
																			onAdd={() => addSerie(visibleIndex)}
																			onRemove={() => removeSerieAt(visibleIndex, seriesCount - 1)}
																		/>
																	)}
																	{inSuperset && (
																		<button
																			className="editor-exercise-ss-detach"
																			onClick={() => removeExoFromSuperset(visibleIndex)}
																			title="Sortir du superset"
																			aria-label="Sortir du superset"
																		>
																			⛓✕
																		</button>
																	)}
																	<button
																		className="editor-exercise-card-action"
																		onClick={() => removeExercice(visibleIndex)}
																	title="Retirer cet exercice"
																	aria-label="Retirer cet exercice"
																>
																	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
																		<line x1="18" y1="6" x2="6" y2="18" />
																		<line x1="6" y1="6" x2="18" y2="18" />
																	</svg>
																</button>
															</div>
															{!libraryMode && (
																<>
																	<WarmupTable
																		warmups={exo.warmupSeries || []}
																		onUpdateReps={(wIdx, value) => updateWarmupField(visibleIndex, wIdx, "reps", value)}
																		onUpdatePoids={(wIdx, value) => updateWarmupField(visibleIndex, wIdx, "poids", value)}
																		onUpdatePoidsDec={(wIdx, value) => updateWarmupField(visibleIndex, wIdx, "poidsDec", value)}
																		onAddWarmup={() => addWarmup(visibleIndex)}
																		onRemoveWarmup={(wIdx) => removeWarmup(visibleIndex, wIdx)}
																	/>
																	<SeriesTable
																		series={exo.series || []}
																		onUpdateReps={(serieIndex, value) => updateSerieReps(visibleIndex, serieIndex, value)}
																		onUpdatePoids={(serieIndex, value) => updateSeriePoids(visibleIndex, serieIndex, value)}
																		onUpdatePoidsDec={(serieIndex, value) => updateSeriePoidsDec(visibleIndex, serieIndex, value)}
																		onUpdateRpe={(serieIndex, value) => updateSerieRpe(visibleIndex, serieIndex, value)}
																		onAddSerie={() => addSerie(visibleIndex)}
																		onRemoveSerie={(serieIndex) => removeSerieAt(visibleIndex, serieIndex)}
																		onDuplicateSerie={(serieIndex) => duplicateSerie(visibleIndex, serieIndex)}
																	/>
																</>
															)}
														</div>
													);
												};

												return blocks.map((block) => {
													if (block.type === "standalone") {
														return renderExoCard({ exo: block.exo, visibleIndex: block.visibleIndex, inSuperset: false });
													}
													const colorIdx = (block.groupId - 1) % 5;
													const firstMemberIdx = block.members.length > 0 ? block.members[0].visibleIndex : null;
													const draggedExoNow = draggedExercise != null ? visibleExos[draggedExercise] : null;
													const draggedGroupNow = draggedExoNow ? (draggedExoNow.supersetGroup || 0) : 0;
													return (
														<div
															key={"ss-" + block.groupId}
															className={`editor-superset-block ss-grp-${colorIdx} ${block.members.length === 0 ? "is-empty" : ""} ${draggedGroupNow === block.groupId ? "is-dragging" : ""} ${addingToSuperset === block.groupId ? "is-add-mode" : ""} ${addingToSuperset != null && addingToSuperset !== block.groupId ? "is-dim" : ""}`}

														>
															<div className="editor-superset-header">
																{firstMemberIdx != null && (
																	<span
																		className="editor-superset-drag"
																		title="Glisser pour déplacer le superset"
																		draggable
																		onDragStart={(e) => handleGroupDragStart(e, firstMemberIdx)}
																		onDragEnd={handleDragEnd}
																	>
																		≡
																	</span>
																)}
																<span className="editor-superset-label">⛓ SUPERSET</span>
																<button
																	className={`editor-superset-add ${addingToSuperset === block.groupId ? "is-active" : ""}`}
																	onClick={() => toggleAddingToSuperset(block.groupId)}
																	title={addingToSuperset === block.groupId ? "Sortir du mode ajout" : "Ajouter des exercices à ce superset"}
																>
																	{addingToSuperset === block.groupId ? "× Annuler ajout" : "+ Ajouter"}
																</button>
																<button
																	className="editor-superset-remove"
																	onClick={() => {
block.members.forEach((m) => removeExoFromSuperset(m.visibleIndex));
removeEmptySuperset(block.groupId);
																	}}
																	title="Délier ce superset"
																>
																	Délier
																</button>
															</div>
															<div className="editor-superset-slots">
																{block.members.map((m) => (
																	<div key={m.exo.localId || m.exo.id} className="editor-superset-slot is-filled">
{renderExoCard({ exo: m.exo, visibleIndex: m.visibleIndex, inSuperset: true })}
																	</div>
																))}
																{block.members.length === 0 && addingToSuperset !== block.groupId && (
																	<div className="editor-superset-slot is-empty">
																		<span className="editor-superset-slot-hint">Clique sur + Ajouter pour intégrer des exos</span>
																	</div>
																)}
															</div>
															{addingToSuperset === block.groupId && (
																<div className="editor-superset-add-panel">
																	<div className="editor-superset-add-panel-title">Choisis les exos à intégrer (clique dans l'ordre voulu)</div>
																	<div className="editor-superset-add-list">
																		{visibleExos.map((exo, vIdx) => {
																			if ((exo.supersetGroup || 0) !== 0) return null;
																			const order = pendingAdditions.indexOf(vIdx);
																			const isSelected = order >= 0;
																			return (
																				<button
																					key={exo.localId || exo.id}
																					type="button"
																					className={`editor-superset-add-item ${isSelected ? "is-selected" : ""}`}
																					onClick={() => togglePendingAddition(vIdx)}
																				>
																					<span className="editor-superset-add-item-mark">{isSelected ? (order + 1) : "+"}</span>
																					<span className="editor-superset-add-item-name">{exo.nom}</span>
																				</button>
																			);
																		})}
																		{visibleExos.every((exo) => (exo.supersetGroup || 0) !== 0) && (
																			<div className="editor-superset-add-empty">Aucun exo standalone disponible — ajoute d'abord des exercices à la séance</div>
																		)}
																	</div>
																	<div className="editor-superset-add-actions">
																		<button
																			type="button"
																			className="editor-superset-add-cancel"
																			onClick={() => { setAddingToSuperset(null); setPendingAdditions([]); }}
																		>
																			Annuler
																		</button>
																		<button
																			type="button"
																			className="editor-superset-add-confirm"
																			disabled={pendingAdditions.length === 0}
																			onClick={() => confirmAddToSuperset(block.groupId)}
																		>
																			{pendingAdditions.length === 0 ? "Ajouter" : "Ajouter " + pendingAdditions.length + " exo" + (pendingAdditions.length > 1 ? "s" : "")}
																		</button>
																	</div>
																</div>
															)}
														</div>
													);
												});
											})()}
											</div>
											<div className="editor-shell-footer-row">
												<button
													className="editor-shell-add-ex"
													onClick={() => setEditorPanel("exercices")}
												>
													+ Ajouter un exercice
												</button>
												<button
													className="editor-shell-add-ss"
													onClick={createSuperset}
												>
													+ Créer un superset
												</button>
											</div>
										</>
									)}
								</>
							);
						})()}
					</div>
				</EditorShell>
			)}

			{/* ===== TAB: Editeur (legacy 4-col, kept as fallback during Phase 1) — DISABLED ===== */}
			{false && activeTab === "editeur" && (
			<div className="coach-layout">
				{/* Column 1: Student & Calendar */}
				<div className="coach-column-left">
					<div className="coach-panel coach-panel-calendar">
						<h3 className="coach-panel-title">Calendrier</h3>
						<div className="calendar-container">
							<div className="calendar-header">
								<button className="calendar-nav-btn" onClick={goToPreviousMonth}>
									&lt;
								</button>
								<span className="calendar-month-year">
									{MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
								</span>
								<button className="calendar-nav-btn" onClick={goToNextMonth}>
									&gt;
								</button>
							</div>
							<div className="calendar-weekdays">
								{DAY_NAMES.map((day) => (
									<div key={day} className="calendar-weekday">{day}</div>
								))}
							</div>
							<div className="calendar-grid">
								{loadingCalendar ? (
									<div className="calendar-loading">Chargement...</div>
								) : (
									renderCalendar()
								)}
							</div>
							{!selectedStudent && (
								<p className="calendar-hint">Sélectionnez un élève pour voir son calendrier</p>
							)}
						</div>

						{/* Training Overview */}
						{selectedDay && (
							<div className="training-overview">
								<div className="training-overview-title">
									Entraînement du {selectedDay} {MONTH_NAMES[currentDate.getMonth()]}
								</div>
								{loadingDetails ? (
									<div className="training-overview-empty">Chargement...</div>
								) : trainingDetails.length === 0 ? (
									<div className="training-overview-empty">Aucun entraînement</div>
								) : (
									trainingDetails.map((training) => (
										<div key={training.id} className="training-overview-item">
											<div className="training-overview-item-name">
												{training.nom || "Entraînement"}
											</div>
											<div className="training-overview-exercises">
												{training.exercices && training.exercices.map((exercice, index) => (
													<div key={index} className="training-overview-exercise">
														{exercice.nom} ({exercice.nbSeries})
													</div>
												))}
											</div>
											<div className="training-overview-buttons">
												<button
													className="training-overview-btn"
													onClick={() => openInEditor(training.id)}
												>
													Ouvrir dans l'éditeur
												</button>
												<button
													className="training-overview-btn training-overview-btn-secondary"
													onClick={() => openDatePickerModal('duplicate', training)}
												>
													Dupliquer
												</button>
												<button
													className="training-overview-btn training-overview-btn-secondary"
													onClick={() => startMultiDateDuplicate(training)}
												>
													Multi-dates
												</button>
												<button
													className="training-overview-btn training-overview-btn-secondary"
													onClick={() => openDatePickerModal('move', training)}
												>
													Deplacer
												</button>
											</div>
										</div>
									))
								)}
							</div>
						)}
					</div>
				</div>

				{/* Column 2: Exercises */}
				<div className="coach-column-exercises">
					<div className="coach-panel coach-panel-exercises">
						<h3 className="coach-panel-title">Liste des exercices</h3>
						<div className="exercise-search">
							<input
								type="text"
								className="exercise-search-input"
								placeholder="Rechercher un exercice, un muscle..."
								value={exerciseSearch}
								onChange={(e) => setExerciseSearch(e.target.value)}
							/>
							{selectedStudent && (
								<button
									className="exercise-add-btn"
									onClick={openCreateExerciseModal}
									title="Ajouter un exercice"
								>
									+
								</button>
							)}
						</div>
						<div className="exercise-list">
							{!selectedStudent ? (
								<p className="exercise-list-hint">Sélectionnez un élève pour voir ses exercices</p>
							) : loadingExercises ? (
								<p className="exercise-list-loading">Chargement...</p>
							) : exercises.length === 0 ? (
								<p className="exercise-list-empty">Aucun exercice</p>
							) : (
								exercises
									.filter((exercise) => {
										const searchLower = exerciseSearch.toLowerCase();
										// Match by exercise name
										if (exercise.nomExercice.toLowerCase().includes(searchLower)) {
											return true;
										}
										// Match by muscle name
										const muscleNames = getMuscleNames(exercise.muscles);
										return muscleNames.some((muscle) => muscle.toLowerCase().includes(searchLower));
									})
									.map((exercise) => {
										const muscleNames = getMuscleNames(exercise.muscles);
										return (
											<div
												key={exercise.id}
												className="exercise-item exercise-item-withthumb"
												onClick={() => handleExerciseClick(exercise)}
											>
												<ExerciseThumb exercise={exercise} size={96} />
												<div className="exercise-item-info">
													<div className="exercise-item-name">{exercise.nomExercice}</div>
													{muscleNames.length > 0 && (
														<div className="exercise-item-muscles">
															{muscleNames.join(", ")}
														</div>
													)}
												</div>
											</div>
										);
									})
							)}
						</div>
					</div>
				</div>

				{/* Column 3: Session Editor */}
				<div className="coach-column-middle">
					<div className="coach-panel coach-panel-editor">
						<div className="coach-panel-header">
							<div className="coach-panel-title-container">
								<h3 className="coach-panel-title">Éditeur de séances</h3>
								{editorTraining && (
									<span className="coach-panel-subtitle">
										{editorMode === 'edit'
											? `Séance du ${new Date(editorTraining.date).toLocaleDateString('fr-FR')}`
											: editorMode === 'editTemplate'
											? `Template - ${editorTraining.nom}`
											: 'Nouvelle séance'}
									</span>
								)}
							</div>
							<div className="editor-header-buttons">
								{selectedStudent && (
									<button className="editor-create-btn" onClick={createNewTraining}>
										Créer
									</button>
								)}
								<button className="editor-reset-btn" onClick={() => {
									if (editorTraining && trainingModified) {
										const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
										if (!confirmed) return;
									}
									setEditorTraining(null);
									setTrainingModified(false);
								}}>
									Réinitialiser
								</button>
							</div>
						</div>
						<div className="editor-content">
							{loadingEditor ? (
								<p className="editor-loading">Chargement...</p>
							) : !editorTraining ? (
								<p className="editor-empty">Sélectionnez un entraînement pour l'éditer</p>
							) : (
								<>
									{/* Feature 4 — Template copy banner */}
									{loadedFromTemplate && (
										<div style={{
											background: "rgba(48,164,154,0.1)",
											border: "1px solid #30a49a",
											borderRadius: "8px",
											padding: "8px 12px",
											marginBottom: "12px",
											color: "rgba(255,255,255,0.7)",
											fontSize: "13px",
										}}>
											ℹ️ Cette séance est une copie du template "{loadedTemplateName}". Les modifications n'affectent pas le modèle original.
										</div>
									)}
									<div className="editor-exercises">
										{editorTraining.exercices
											.filter((ex) => ex.supprime !== 1)
											.map((exercice, exIndex) => (
											<div
												key={exercice.localId || exercice.id}
												className={`editor-exercise ${draggedExercise === exIndex ? 'dragging' : ''}`}
												draggable
												onDragStart={(e) => handleDragStart(e, exIndex)}
												onDragOver={(e) => handleDragOver(e, exIndex)}
												onDragEnd={handleDragEnd}
											>
												<div className="editor-exercise-header">
													<div className="editor-exercise-left">
														<span className="editor-drag-handle">&#9776;</span>
														<ExerciseThumb exercise={exerciseById.get(exercice.idExercice) || { nomExercice: exercice.nom }} size={72} />
														<span className="editor-exercise-name">{exercice.nom}</span>
													</div>
													<div className="editor-exercise-right">
														<div className="editor-exercise-series-control">
															<button
																className="editor-btn-small"
																onClick={() => removeSerie(exIndex)}
															>-</button>
															<span className="editor-series-count">{exercice.series.length} Series</span>
															<button
																className="editor-btn-small"
																onClick={() => addSerie(exIndex)}
															>+</button>
														</div>
														<button
															className="editor-remove-btn"
															onClick={() => removeExercice(exIndex)}
														>&times;</button>
													</div>
												</div>
												<div className="editor-series-list">
													{exercice.series.map((serie, serieIndex) => (
														<div key={serie.id || serieIndex} className="editor-serie">
															<div className="editor-serie-main">
																<input
																	type="number"
																	className="editor-serie-input"
																	value={serie.reps}
																	onChange={(e) => updateSerieReps(exIndex, serieIndex, e.target.value)}
																/>
																<span className="editor-serie-x">X</span>
																<input
																	type="number"
																	className="editor-serie-input"
																	value={serie.poids}
																	onChange={(e) => updateSeriePoids(exIndex, serieIndex, e.target.value)}
																/>
																<span className="editor-serie-unit">kg</span>
															</div>
															<div className="editor-serie-rpe-row">
																<span className="editor-serie-rpe-label">RPE</span>
																<input
																	type="number"
																	className="editor-serie-input editor-serie-input-rpe"
																	value={serie.rpe || 0}
																	min={0}
																	max={10}
																	onChange={(e) => updateSerieRpe(exIndex, serieIndex, e.target.value)}
																/>
															</div>
														</div>
													))}
												</div>
											</div>
										))}
									</div>
									<div className="editor-footer">
										<div className="editor-buttons">
											{editorMode === 'edit' ? (
												<>
													<button
														className="editor-btn editor-btn-save"
														onClick={handleSaveTraining}
														disabled={savingTraining}
													>
														{savingTraining ? "Enregistrement..." : "Enregistrer"}
													</button>
													<button className="editor-btn editor-btn-delete" onClick={handleDeleteTraining}>Supprimer</button>
												</>
											) : editorMode === 'editTemplate' ? (
												<>
													<button
														className="editor-btn editor-btn-save"
														onClick={handleSaveTemplate}
														disabled={savingTraining}
													>
														{savingTraining ? "Enregistrement..." : "Enregistrer"}
													</button>
													<button className="editor-btn editor-btn-delete" onClick={handleDeleteTemplate}>Supprimer</button>
													<button
														className="editor-btn"
														onClick={openTemplateNameModal}
														disabled={savingTraining}
													>
														Enregistrer comme template
													</button>
													<button
														className="editor-btn"
														onClick={() => openDatePickerModal('addToCalendar')}
														disabled={savingTraining}
													>
														Ajouter au calendrier
													</button>
												</>
											) : (
												<>
													<button
														className="editor-btn"
														onClick={openTemplateNameModal}
														disabled={savingTraining}
													>
														{savingTraining ? "Enregistrement..." : "Enregistrer comme template"}
													</button>
													<button
														className="editor-btn"
														onClick={() => openDatePickerModal('addToCalendar')}
														disabled={savingTraining}
													>
														Ajouter au calendrier
													</button>
												</>
											)}
										</div>
										<button
											className="editor-note-btn"
											onClick={openCommentModal}
											title={editorTraining?.commentaire ? "Modifier la note" : "Ajouter une note"}
										>
											{editorTraining?.commentaire ? "📝" : "📋"}
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>

				{/* Column 4: Templates */}
				<div className="coach-column-right">
					<div className="coach-panel coach-panel-templates">
						<div className="templates-header">
							<h3 className="coach-panel-title">Templates</h3>
							{selectedStudent && (
								<button className="coach-btn-planification" onClick={openPlanification}>Planifier</button>
							)}
						</div>
						{selectedStudent && !isSelf && (
							<div className="template-list-tabs">
								<button className={`template-list-tab ${templateListTab === "student" ? "active" : ""}`} onClick={() => setTemplateListTab("student")}>{selectedStudent.prenom}</button>
								<button className={`template-list-tab ${templateListTab === "coach" ? "active" : ""}`} onClick={() => setTemplateListTab("coach")}>Mes templates</button>
							</div>
						)}

						{/* ===== ONGLET COACH : explorateur de fichiers ===== */}
						{(templateListTab === "coach" || isSelf) && (
							<div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>

								{/* Sidebar dossiers */}
								<div style={{
									width: 140, minWidth: 140, flexShrink: 0,
									borderRight: "1px solid rgba(255,255,255,0.08)",
									display: "flex", flexDirection: "column",
									background: "rgba(0,0,0,0.15)"
								}}>
									<div style={{ padding: "10px 10px 6px", fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
										Dossiers
									</div>

									{/* Item "Tous" */}
									<div
										onClick={() => setActiveFolderKey(null)}
										style={{
											padding: "7px 10px", cursor: "pointer", fontSize: "0.85rem",
											display: "flex", alignItems: "center", gap: 6,
											background: activeFolderKey === null ? "rgba(48,164,154,0.15)" : "transparent",
											color: activeFolderKey === null ? "#30a49a" : "rgba(255,255,255,0.75)",
											borderLeft: activeFolderKey === null ? "3px solid #30a49a" : "3px solid transparent",
										}}
									>
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/></svg>
										<span>Tous</span>
										<span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "rgba(255,255,255,0.35)" }}>{coachTemplates.length}</span>
									</div>

									{/* Liste des dossiers */}
									{templateFolders.filter(f => !f.Supprime).map(folder => {
										const count = templateFolderAssoc.filter(a => !a.Supprime && a.FolderKey === folder.Key).length;
										const isActive = activeFolderKey === folder.Key;
										return (
											<div key={folder.Key}
												onClick={() => setActiveFolderKey(folder.Key)}
												style={{
													padding: "7px 10px 7px 7px", cursor: "pointer", fontSize: "0.85rem",
													display: "flex", alignItems: "center", gap: 5,
													background: isActive ? "rgba(48,164,154,0.15)" : "transparent",
													color: isActive ? "#30a49a" : "rgba(255,255,255,0.75)",
													borderLeft: isActive ? "3px solid #30a49a" : "3px solid transparent",
												}}
											>
												<svg width="13" height="13" viewBox="0 0 24 24" fill={isActive ? "#30a49a" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: isActive ? 1 : 0.6 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
												<span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{folder.NomDossier}</span>
												<span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)" }}>{count}</span>
												<button
													onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.Key); }}
													style={{ background: "none", border: "none", color: "rgba(255,255,255,0.25)", cursor: "pointer", padding: "0 2px", fontSize: "0.75rem", lineHeight: 1, flexShrink: 0 }}
													title="Supprimer le dossier"
												>✕</button>
											</div>
										);
									})}

									{/* Nouveau dossier */}
									<div style={{ marginTop: "auto", padding: "8px 8px 10px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
										{showNewFolderInput ? (
											<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
												<input
													autoFocus
													value={newFolderName}
													onChange={e => setNewFolderName(e.target.value)}
													onKeyDown={e => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") { setShowNewFolderInput(false); setNewFolderName(""); } }}
													placeholder="Nom du dossier"
													style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 4, color: "#fff", padding: "4px 6px", fontSize: "0.8rem", outline: "none", width: "100%", boxSizing: "border-box" }}
												/>
												<div style={{ display: "flex", gap: 4 }}>
													<button onClick={handleCreateFolder} style={{ flex: 1, background: "#30a49a", border: "none", borderRadius: 4, color: "#fff", padding: "3px 0", fontSize: "0.75rem", cursor: "pointer" }}>✓</button>
													<button onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); }} style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 4, color: "rgba(255,255,255,0.6)", padding: "3px 0", fontSize: "0.75rem", cursor: "pointer" }}>✕</button>
												</div>
											</div>
										) : (
											<button
												onClick={() => setShowNewFolderInput(true)}
												style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: 5, color: "rgba(255,255,255,0.5)", padding: "5px 4px", fontSize: "0.78rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
											>
												<span>+</span> Dossier
											</button>
										)}
									</div>
								</div>

								{/* Zone templates */}
								<div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", minWidth: 0 }}>
									{/* Breadcrumb */}
									<div style={{ padding: "8px 12px 4px", fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", display: "flex", alignItems: "center", gap: 4, borderBottom: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
										<span>Mes templates</span>
										{activeFolderKey !== null && (
											<>
												<span>›</span>
												<span style={{ color: "#30a49a" }}>{templateFolders.find(f => f.Key === activeFolderKey)?.NomDossier}</span>
											</>
										)}
									</div>

									{/* Liste filtrée */}
									<div style={{ flex: 1, padding: "4px 0" }}>
										{loadingTemplates || templateFoldersLoading ? (
											<p className="template-list-loading">Chargement...</p>
										) : (() => {
											const displayedTemplates = activeFolderKey === null
												? coachTemplates
												: coachTemplates.filter(t => templateFolderAssoc.some(a => !a.Supprime && a.FolderKey === activeFolderKey && a.TemplateKey === t.Key));

											if (displayedTemplates.length === 0) return (
												<div style={{ padding: "20px 12px", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "0.82rem" }}>
													{activeFolderKey === null ? "Aucun template" : "Aucun template dans ce dossier"}
												</div>
											);

											return displayedTemplates.map(template => {
												const inFolder = activeFolderKey !== null && templateFolderAssoc.some(a => !a.Supprime && a.FolderKey === activeFolderKey && a.TemplateKey === template.Key);
												return (
													<div key={template.Id} className="template-item">
														<div className="template-item-header" onClick={() => openInEditor(template.Id, true)}>
															<div className="template-item-icon">&#9776;</div>
															<div className="template-item-info">
																<div className="template-item-name">{template.Nom}</div>
																{template.ListExercice && template.ListExercice.length > 0 && (
																	<div className="template-item-exercises">
																		{template.ListExercice.slice(0, 3).map(ex => ex.NomExercice).join(' • ')}
																		{template.ListExercice.length > 3 ? ` +${template.ListExercice.length - 3}` : ''}
																	</div>
																)}
															</div>
														</div>
														<div className="template-item-actions">
															{!isSelf && (
																<button className="template-action-btn" onClick={() => handleSendTemplate(template)} title="Envoyer à l'élève">↗</button>
															)}
															{activeFolderKey !== null && (
																<button
																	className={`template-folder-toggle ${inFolder ? "in" : ""}`}
																	title={inFolder ? "Retirer du dossier" : "Ajouter au dossier"}
																	onClick={() => handleToggleFolderAssign(template, activeFolderKey, !inFolder)}
																>
																	{inFolder ? "−" : "+"}
																</button>
															)}
														</div>
													</div>
												);
											});
										})()}
									</div>
								</div>
							</div>
						)}

						{/* ===== ONGLET ÉTUDIANT : liste simple ===== */}
						{templateListTab === "student" && !isSelf && (
							<div className="template-list">
								{!selectedStudent ? (
									<p className="template-list-hint">Sélectionnez un élève pour voir ses templates</p>
								) : loadingTemplates ? (
									<p className="template-list-loading">Chargement...</p>
								) : templates.length === 0 ? (
									<p className="template-list-empty">Aucun template</p>
								) : (
									templates.map((template) => (
										<div key={template.id} className="template-item">
											<div className="template-item-header" onClick={() => openInEditor(template.id, true)}>
												<div className="template-item-icon">&#9776;</div>
												<div className="template-item-info">
													<div className="template-item-name">{template.nom}</div>
													{template.exercices && template.exercices.length > 0 && (
														<div className="template-item-exercises">
															{template.exercices.join(' • ')}
														</div>
													)}
												</div>
											</div>
											<div className="template-item-actions">
												<button className="template-action-btn" onClick={(e) => { e.stopPropagation(); openTemplateAsNewSession(template); }} title="Copier comme nouvelle séance">Copier</button>
											</div>
										</div>
									))
								)}
							</div>
						)}

					</div>
				</div>
			</div>
			)}

			{/* ===== TAB: Statistiques ===== */}
			{activeTab === "statistiques" && selectedStudent && studentTab === "stats" && (
			<div className="coach-stats-tab">

				{statsLoading ? (
					<StatsSkeleton />
				) : !statsData ? (
					<div className="stats-empty">Aucune donnée disponible</div>
				) : (
					<>
						{/* Summary cards */}
						{(() => {
							const regScore = statsData.regularityScore;
							const progScore = statsData.progressionScore;
							const regColor = regScore < 0 ? "#fff" : regScore >= 75 ? "#4ade80" : regScore >= 60 ? "#facc15" : regScore >= 50 ? "#F87B1B" : "#ef4444";
							const progColor = progScore < 0 ? "#fff" : progScore >= 50 ? "#4ade80" : progScore >= 35 ? "#facc15" : progScore >= 20 ? "#F87B1B" : "#ef4444";
							const regrScore = statsData.regressionScore;
							const regrColor = regrScore < 0 ? "#fff" : regrScore <= 10 ? "#4ade80" : regrScore <= 25 ? "#facc15" : regrScore <= 40 ? "#F87B1B" : "#ef4444";
							const sessPerWeek = statsData.averageSessionsPerWeek;
							const sessColor = sessPerWeek >= 4 ? "#4ade80" : sessPerWeek >= 3 ? "#4ade80" : sessPerWeek >= 2 ? "#facc15" : sessPerWeek >= 1 ? "#F87B1B" : "#ef4444";
							const daysSince = statsData.daysSinceLastTraining;
							const daysColor = daysSince <= 2 ? "#4ade80" : daysSince <= 4 ? "#facc15" : daysSince <= 7 ? "#F87B1B" : "#ef4444";
							return (
								<div className="stats-summary-cards">
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: "#fff" }}>{statsData.totalTrainings}</div>
										<div className="stats-card-label">Entraînements</div>
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: regColor }}>{regScore >= 0 ? regScore + "%" : "—"}</div>
										<div className="stats-card-label">Régularité</div>
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: progColor }}>{progScore >= 0 ? progScore + "%" : "—"}</div>
										<div className="stats-card-label">Progression</div>
										{progScore >= 0 && (
											<div className="stats-card-detail">{statsData.progressionSetsProgressed}/{statsData.progressionSetsAnalyzed} séries</div>
										)}
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: regrColor }}>{regrScore >= 0 ? regrScore + "%" : "—"}</div>
										<div className="stats-card-label">Régression</div>
										{regrScore >= 0 && (
											<div className="stats-card-detail">{statsData.regressionSetsRegressed}/{statsData.regressionSetsAnalyzed} séries</div>
										)}
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: sessColor }}>{sessPerWeek?.toFixed(1)}</div>
										<div className="stats-card-label">Séances / semaine</div>
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: daysColor }}>{daysSince}</div>
										<div className="stats-card-label">Jours depuis dernier entr.</div>
									</div>
								</div>
							);
						})()}

						{/* Progression / regression histogram (toggle semaine ↔ séance) */}
						{(() => {
							const sessionSeries = statsData.sessionProgression;
							const weekSeries = statsData.weeklyProgression;
							const sessionAvailable = Array.isArray(sessionSeries) && sessionSeries.length > 0;
							const weekAvailable = Array.isArray(weekSeries) && weekSeries.length > 0;
							if (!sessionAvailable && !weekAvailable) return null;

							const effectiveMode = progressionAggregation === "session" && sessionAvailable
								? "session"
								: "week";
							const series = effectiveMode === "session" ? sessionSeries : weekSeries;
							const labelKey = effectiveMode === "session" ? "sessionLabel" : "weekLabel";

							return (
								<div className="stats-weekly-histogram">
									<div className="stats-weekly-histogram-head">
										<h3>Progression / régression par {effectiveMode === "session" ? "séance" : "semaine"}</h3>
										<div className="stats-prog-agg-toggle" role="tablist" aria-label="Agrégation">
											<button
												type="button"
												role="tab"
												aria-selected={effectiveMode === "week"}
												className={effectiveMode === "week" ? "active" : ""}
												onClick={() => setProgressionAggregation("week")}
												disabled={!weekAvailable}
											>
												Semaine
											</button>
											<button
												type="button"
												role="tab"
												aria-selected={effectiveMode === "session"}
												className={effectiveMode === "session" ? "active" : ""}
												onClick={() => setProgressionAggregation("session")}
												disabled={!sessionAvailable}
												title={!sessionAvailable ? "Donnée non encore disponible" : undefined}
											>
												Séance
											</button>
										</div>
									</div>
									<div className="stats-weekly-histogram-chart">
										<Bar
											data={{
												labels: series.map((w) => w[labelKey]),
												datasets: [
													{
														label: "Progression",
														data: series.map((w) => w.progressionPercentage),
														backgroundColor: "#4ade80",
														stack: "wk",
														maxBarThickness: 40,
													},
													{
														label: "Stagnation",
														data: series.map((w) => Math.max(0, 100 - w.progressionPercentage - w.regressionPercentage)),
														backgroundColor: "rgba(255,255,255,0.16)",
														stack: "wk",
														maxBarThickness: 40,
													},
													{
														label: "Régression",
														data: series.map((w) => w.regressionPercentage),
														backgroundColor: "#ef4444",
														stack: "wk",
														maxBarThickness: 40,
													},
												],
											}}
											options={{
												responsive: true,
												maintainAspectRatio: false,
												plugins: {
													legend: { labels: { color: "#b0b0b0", boxWidth: 12, boxHeight: 12 } },
													tooltip: {
														callbacks: {
															afterBody: (items) => {
																const idx = items[0]?.dataIndex;
																const w = series[idx];
																return w ? `${w.setsAnalyzed} série${w.setsAnalyzed > 1 ? "s" : ""} analysée${w.setsAnalyzed > 1 ? "s" : ""}` : "";
															},
															label: (ctx) => `${ctx.dataset.label} : ${ctx.parsed.y}%`,
														},
													},
												},
												scales: {
													y: {
														stacked: true,
														beginAtZero: true,
														max: 100,
														ticks: { color: "#b0b0b0", callback: (v) => v + "%" },
														grid: { color: "rgba(255,255,255,0.05)" },
													},
													x: { stacked: true, ticks: { color: "#b0b0b0" }, grid: { display: false } },
												},
											}}
										/>
									</div>
								</div>
							);
						})()}


{/* Bodyweight evolution */}
						{statsData.bodyweightHistory && statsData.bodyweightHistory.length > 0 && (
							<div className="stats-weekly-histogram">
								<h3>Évolution du poids de corps</h3>
								<div className="stats-weekly-histogram-chart">
									<Line
										data={{
											datasets: [
												{
													label: "Poids de corps",
													data: [...statsData.bodyweightHistory]
														.sort((a, b) => a.date - b.date)
														.map((b) => ({ x: b.date, y: b.poids })),
													borderColor: "#30a49a",
													backgroundColor: "rgba(48, 164, 154, 0.12)",
													tension: 0.3,
													fill: true,
													pointBackgroundColor: "#30a49a",
													pointRadius: 3,
												},
											],
										}}
										options={{
											responsive: true,
											maintainAspectRatio: false,
											plugins: {
												legend: { display: false },
												tooltip: {
													callbacks: {
														title: (items) => (items[0] ? new Date(items[0].parsed.x).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""),
														label: (ctx) => `${Number(ctx.parsed.y).toFixed(1).replace(".", ",")} kg`,
													},
												},
											},
											scales: {
												y: { ticks: { color: "#b0b0b0", callback: (v) => Number(v).toFixed(1).replace(".", ",") + " kg" }, grid: { color: "rgba(255,255,255,0.05)" } },
												x: {
													type: "linear",
													ticks: {
														color: "#b0b0b0",
														maxRotation: 0,
														autoSkip: true,
														maxTicksLimit: 8,
														callback: (value) => new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
													},
													grid: { display: false },
												},
											},
										}}
									/>
								</div>
							</div>
						)}

												{/* Bottom row: progressions/regressions + exercise progress */}
						<div className="stats-charts-row">
						<div className="stats-prog-regr-row">
						{/* Progressions list */}
						{statsData.progressions && statsData.progressions.length > 0 && (
							<div className="stats-progressions">
								<h3>Progressions récentes</h3>
								<div className="progressions-list">
									{statsData.progressions.map((p, i) => {
										const isWeightProg = p.poids > p.dernierPoids;
										const isRepsProg = p.reps > p.derniereReps;
										let detail;
										if (isWeightProg && isRepsProg) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg | ${p.derniereReps} → ${p.reps} reps`;
										} else if (isWeightProg) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg (${p.reps} reps)`;
										} else {
											detail = `${p.derniereReps} → ${p.reps} reps @ ${p.poids}kg`;
										}
										const percent = isWeightProg
											? ((p.poids - p.dernierPoids) / p.dernierPoids * 100)
											: ((p.reps - p.derniereReps) / p.derniereReps * 100);
										const date = p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "";
										return (
										<div key={i} className="progression-item">
											<span className="progression-name">{p.nomExercice} — <span style={{opacity: 0.5, fontSize: "0.85em"}}>Série {p.ordre + 1}</span></span>
											<span className="progression-detail">{detail}</span>
											<span className="progression-date">{date}</span>
											<span className="progression-percent positive">+{percent.toFixed(1)}%</span>
										</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Regressions list */}
						{statsData.regressions && statsData.regressions.length > 0 && (
							<div className="stats-progressions stats-regressions">
								<h3>Régressions récentes</h3>
								<div className="progressions-list">
									{statsData.regressions.map((p, i) => {
										const isWeightRegr = p.poids < p.dernierPoids;
										const isRepsRegr = p.reps < p.derniereReps;
										let detail;
										if (isWeightRegr && isRepsRegr) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg | ${p.derniereReps} → ${p.reps} reps`;
										} else if (isWeightRegr) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg (${p.reps} reps)`;
										} else {
											detail = `${p.derniereReps} → ${p.reps} reps @ ${p.poids}kg`;
										}
										const percent = isWeightRegr
											? ((p.poids - p.dernierPoids) / p.dernierPoids * 100)
											: ((p.reps - p.derniereReps) / p.derniereReps * 100);
										const date = p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "";
										return (
										<div key={i} className="progression-item">
											<span className="progression-name">{p.nomExercice} — <span style={{opacity: 0.5, fontSize: "0.85em"}}>Série {p.ordre + 1}</span></span>
											<span className="progression-detail">{detail}</span>
											<span className="progression-date">{date}</span>
											<span className="progression-percent negative">{percent.toFixed(1)}%</span>
										</div>
										);
									})}
								</div>
							</div>
						)}
						</div>

						{/* Exercise progress chart */}
						<div className="stats-exercise-compare">
							{[
								{ data: exerciseProgressData, selected: selectedProgressExercise, setSelected: setSelectedProgressExercise }
							].map((slot, slotIdx) => (
								<div key={slotIdx} className="stats-exercise-progress">
									<h3>Progression par exercice</h3>
									<div className="exercise-progress-controls">
										<select
											className="coach-student-select"
											value={slot.selected || ""}
											onChange={(e) => slot.setSelected(e.target.value ? parseInt(e.target.value) : null)}
										>
											<option value="">-- Choisir un exercice --</option>
											{exercises.map(ex => (
												<option key={ex.id} value={ex.id}>{ex.nomExercice}</option>
											))}
										</select>
									</div>
									{(() => {
											const hasData = slot.data && ((slot.data.sessionProgression && slot.data.sessionProgression.length > 0) || (slot.data.rmProgression && slot.data.rmProgression.length > 0));
											if (!hasData) return slot.selected ? <div className="stats-empty">Aucune donnée pour cet exercice</div> : null;
											// Both charts share the same time axis: period start → today. Even if the
											// student hasn't trained recently, the right-hand side stays blank rather
											// than truncating the axis at the last data point.
											const nowMs = Date.now();
											const periodStartMs = nowMs - statsPeriod * 24 * 60 * 60 * 1000;
											const barThickness = statsPeriod <= 15 ? 14 : statsPeriod <= 30 ? 9 : 4;
											const xScaleShared = {
												type: "linear",
												min: periodStartMs,
												max: nowMs,
												ticks: {
													color: "#b0b0b0",
													maxRotation: 0,
													autoSkip: true,
													maxTicksLimit: 8,
													callback: (value) => new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
												},
												grid: { display: false },
											};
											const sessionPts = slot.data.sessionProgression || [];
											return (
												<>
													{sessionPts.length > 0 && (
														<div className="stats-chart-container">
															<div className="stats-weekly-histogram-chart">
																<Bar
																	data={{
																		datasets: [
																			{ label: "Progression", data: sessionPts.map((p) => ({ x: p.date, y: p.progressionPercentage })), backgroundColor: "#4ade80", stack: "sess", barThickness },
																			{ label: "Stagnation", data: sessionPts.map((p) => ({ x: p.date, y: Math.max(0, 100 - p.progressionPercentage - p.regressionPercentage) })), backgroundColor: "rgba(255,255,255,0.16)", stack: "sess", barThickness },
																			{ label: "Régression", data: sessionPts.map((p) => ({ x: p.date, y: p.regressionPercentage })), backgroundColor: "#ef4444", stack: "sess", barThickness },
																		],
																	}}
																	options={{
																		responsive: true,
																		maintainAspectRatio: false,
																		plugins: {
																			legend: { labels: { color: "#b0b0b0", boxWidth: 12, boxHeight: 12 } },
																			tooltip: {
																				callbacks: {
																					title: (items) => items[0] ? new Date(items[0].parsed.x).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "",
																					afterBody: (items) => {
																						const ms = items[0]?.parsed.x;
																						const p = sessionPts.find((s) => s.date === ms);
																						return p ? p.setsAnalyzed + ' série(s) analysée(s)' : '';
																					},
																					label: (ctx) => ctx.dataset.label + ' : ' + ctx.parsed.y + '%',
																				},
																			},
																		},
																		scales: {
																			y: { stacked: true, beginAtZero: true, max: 100, ticks: { color: "#b0b0b0", callback: (v) => v + '%' }, grid: { color: "rgba(255,255,255,0.05)" } },
																			x: { ...xScaleShared, stacked: true, offset: false },
																		},
																	}}
																/>
															</div>
														</div>
													)}
													{slot.data.rmProgression && slot.data.rmProgression.length > 0 && (
														<div className="stats-chart-container" style={{ marginTop: 14 }}>
															<h3>Évolution des Rep Max</h3>
															<div className="stats-weekly-histogram-chart">
																{(() => {
																	const pts = slot.data.rmProgression;
																	const series = [
																		{ key: "rm1", label: "1RM", color: "#F87B1B" },
																		{ key: "rm3", label: "3RM", color: "#60a5fa" },
																		{ key: "rm5", label: "5RM", color: "#30a49a" },
																		{ key: "rm8", label: "8RM", color: "#a78bfa" },
																		{ key: "rm10", label: "10RM", color: "#facc15" },
																	];
																	const visible = series.filter((s) => pts.some((p) => p[s.key] > 0));
																	const datasets = visible.map((s) => ({
																		label: s.label,
																		data: pts.map((p) => ({ x: p.date, y: p[s.key] > 0 ? p[s.key] : null })),
																		borderColor: s.color,
																		backgroundColor: s.color,
																		tension: 0.25,
																		spanGaps: true,
																		pointRadius: 2,
																		pointHoverRadius: 5,
																		borderWidth: 2,
																	}));
																	// Dashed "1RM estime" (Epley): cumulative max of weight x (1 + reps/30).
																	// Hidden when it never exceeds the real 1RM (no clutter).
																	const hasEst = pts.some((p) => p.rmEst1 > 0 && p.rmEst1 > (p.rm1 || 0));
																	if (hasEst) {
																		datasets.push({
																			label: "1RM estimé (Epley)",
																			data: pts.map((p) => ({ x: p.date, y: p.rmEst1 > 0 ? p.rmEst1 : null })),
																			borderColor: "#F87B1B",
																			backgroundColor: "transparent",
																			borderDash: [5, 4],
																			borderWidth: 2,
																			tension: 0.25,
																			spanGaps: true,
																			pointRadius: 0,
																			pointHoverRadius: 4,
																		});
																	}
																	return (
																		<Line
																			data={{ datasets }}
																			options={{
																				responsive: true,
																				maintainAspectRatio: false,
																				interaction: { mode: "index", intersect: false },
																				plugins: {
																					legend: { labels: { color: "#b0b0b0", boxWidth: 12, boxHeight: 12 } },
																					tooltip: {
																						callbacks: {
																							title: (items) => items[0] ? new Date(items[0].parsed.x).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "",
																							label: (ctx) => `${ctx.dataset.label} : ${Number(ctx.parsed.y).toFixed(1).replace(".", ",")} kg`,
																						},
																					},
																				},
																				scales: {
																					y: {
																						beginAtZero: false,
																						ticks: { color: "#b0b0b0", callback: (v) => Number(v).toFixed(0) + " kg" },
																						grid: { color: "rgba(255,255,255,0.05)" },
																					},
																					x: xScaleShared,
																				},
																			}}
																		/>
																	);
																})()}
															</div>
														</div>
													)}
												</>
											);
										})()}

								</div>
							))}
						</div>
						</div>

					</>
				)}
			</div>
			)}

			{/* ===== TAB: Vue d'ensemble ===== */}
			{activeTab === "vue-ensemble" && (
			<div className="coach-overview-tab">
				{overviewLoading ? (
					<OverviewSkeleton cards={Math.max(students.length || 6, 3)} />
				) : (
					<>
						{/* Coach alerts */}
						{alertsData && Array.isArray(alertsData) && alertsData.length > 0 && (
							<div className="overview-alerts" style={{ marginBottom: 24 }}>
								<h2 style={{ marginBottom: 12, fontSize: "1.1rem", color: "#F87B1B" }}>Alertes</h2>
								<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
									{alertsData.map((alert, i) => (
										<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, background: "rgba(248,123,27,0.08)", border: "1px solid rgba(248,123,27,0.25)", borderRadius: 10, padding: "12px 16px" }}>
											<span style={{ fontSize: "1.2rem", flexShrink: 0 }}>⚠️</span>
											<div>
												{alert.studentName && (
													<div style={{ fontWeight: 600, color: "#fff", marginBottom: 2 }}>{alert.studentName}</div>
												)}
												<div style={{ color: "#b0b0b0", fontSize: "0.9rem" }}>{alert.message || alert.description || alert.text || JSON.stringify(alert)}</div>
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Feature 6 — Broadcast button */}
						<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
							<button
								className="coach-btn-planification"
								onClick={() => setShowBroadcast(true)}
								style={{ background: "rgba(48,164,154,0.2)", border: "1px solid #30a49a", color: "#30a49a" }}
							>
								📢 Message à tous
							</button>
						</div>

						{/* Student filter */}
						<div className="overview-filter">
							<label>Filtrer par élève :</label>
							<select
								className="overview-filter-select"
								value={overviewFilterStudent || ""}
								onChange={(e) => { setOverviewFilterStudent(e.target.value ? parseInt(e.target.value) : null); setSelectedHeatmapDay(null); }}
							>
								<option value="">Tous les élèves</option>
								{user && user.id && <option key={user.id} value={user.id}>{user.prenom} {user.nom} (moi)</option>}
								{students.map(s => (
									<option key={s.id} value={s.id}>{s.prenom} {s.nom}</option>
								))}
							</select>
							<label style={{ marginLeft: 24 }}>Période :</label>
							<select
								className="overview-filter-select"
								value={overviewPeriod}
								onChange={(e) => setOverviewPeriod(parseInt(e.target.value))}
							>
								<option value={30}>30 jours</option>
								<option value={90}>90 jours</option>
							</select>
						</div>

						{/* Weekly Heatmap */}
						{heatmapData && heatmapData.students && heatmapData.students.length > 0 && (() => {
							// Compute week (Mon-Sun) with offset
							const today = new Date();
							const dayOfWeek = today.getDay(); // 0=Sun
							const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
							const monday = new Date(today);
							monday.setDate(today.getDate() + mondayOffset + (heatmapWeekOffset * 7));
							monday.setHours(0, 0, 0, 0);

							const weekDays = [];
							for (let i = 0; i < 7; i++) {
								const d = new Date(monday);
								d.setDate(monday.getDate() + i);
								weekDays.push(d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'));
							}

							const weekDayLabels = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

							// Filter by selected student if any
							const filteredStudents = overviewFilterStudent
								? heatmapData.students.filter(s => s.id === overviewFilterStudent)
								: heatmapData.students;

							// For each student, get their training count per day this week
							const weekData = filteredStudents.map(student => {
								const dayMap = {};
								(student.days || []).forEach(d => { dayMap[d.date] = d.count; });
								return {
									...student,
									weekCounts: weekDays.map(date => dayMap[date] || 0),
									totalWeek: weekDays.reduce((sum, date) => sum + (dayMap[date] || 0), 0)
								};
							});

							// For selected day, find students who trained
							const selectedDayStudents = selectedHeatmapDay !== null
								? weekData.filter(s => s.weekCounts[selectedHeatmapDay] > 0)
								: null;

							const isCurrentWeek = heatmapWeekOffset === 0;
							const sundayDate = new Date(monday);
							sundayDate.setDate(monday.getDate() + 6);
							const weekLabel = `${monday.getDate()} ${monday.toLocaleDateString('fr-FR', { month: 'short' })} — ${sundayDate.getDate()} ${sundayDate.toLocaleDateString('fr-FR', { month: 'short' })}`;

							return (
								<div className="overview-heatmap">
									<div className="week-heatmap-nav">
										<button
											className="week-nav-arrow"
											onClick={() => { setHeatmapWeekOffset(prev => Math.max(prev - 1, -Math.floor(overviewPeriod / 7))); setSelectedHeatmapDay(null); }}
											disabled={heatmapWeekOffset <= -Math.floor(overviewPeriod / 7)}
										>◀</button>
										<h2>{isCurrentWeek ? "Activité de la semaine" : weekLabel}</h2>
										<button
											className="week-nav-arrow"
											onClick={() => { setHeatmapWeekOffset(prev => Math.min(prev + 1, 0)); setSelectedHeatmapDay(null); }}
											disabled={isCurrentWeek}
										>▶</button>
									</div>
									<div className="week-heatmap">
										<div className="week-heatmap-header">
											{weekDayLabels.map((label, i) => {
												const isToday = weekDays[i] === (today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2,'0') + '-' + String(today.getDate()).padStart(2,'0'));
												const isSelected = selectedHeatmapDay === i;
												// Total trainings this day across all students
												const dayTotal = weekData.reduce((sum, s) => sum + s.weekCounts[i], 0);
												return (
													<div
														key={i}
														className={`week-day-col ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
														onClick={() => setSelectedHeatmapDay(isSelected ? null : i)}
													>
														<div className="week-day-label">{label}</div>
														<div className="week-day-date">{parseInt(weekDays[i].split("-")[2])}</div>
														<div className={`week-day-count ${dayTotal === 0 ? "zero" : ""}`}>{dayTotal}</div>
														<div className="week-day-bar">
															<div
																className="week-day-bar-fill"
																style={{ height: `${Math.min(dayTotal / Math.max(weekData.length, 1) * 100, 100)}%` }}
															/>
														</div>
													</div>
												);
											})}
										</div>

									</div>

									{/* Expanded day detail */}
									{selectedDayStudents !== null && (
										<div className="week-day-detail">
											<h3>{weekDayLabels[selectedHeatmapDay]} {parseInt(weekDays[selectedHeatmapDay].split("-")[2])} — {selectedDayStudents.length} élève(s) entraîné(s)</h3>
											{selectedDayStudents.length === 0 ? (
												<div className="stats-empty">Aucun entraînement ce jour</div>
											) : (
												<div className="week-day-detail-list">
													{selectedDayStudents.map(s => (
														<div key={s.id} className="week-day-detail-item">
															<span className="week-day-detail-name">{s.prenom} {s.nom}</span>
															<span className="week-day-detail-count">{s.weekCounts[selectedHeatmapDay]} séance(s)</span>
															<button className="alert-action-btn" onClick={() => openTrainingDetailModal(s.id, `${s.prenom} ${s.nom}`, weekDays[selectedHeatmapDay])}>Voir</button>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</div>
							);
						})()}

						{/* Student Scores List */}
						{studentScores && studentScores.length > 0 && (() => {
							const getRegularityColor = (score) => {
								if (score >= 75) return "#4ade80";
								if (score >= 60) return "#facc15";
								if (score >= 50) return "#F87B1B";
								return "#ef4444";
							};
							const getProgressionColor = (score) => {
								if (score < 0) return "rgba(255,255,255,0.3)";
								if (score >= 50) return "#4ade80";
								if (score >= 35) return "#facc15";
								if (score >= 20) return "#F87B1B";
								return "#ef4444";
							};
							const getRegressionColor = (score) => {
								if (score < 0) return "rgba(255,255,255,0.3)";
								if (score <= 10) return "#4ade80";
								if (score <= 25) return "#facc15";
								if (score <= 40) return "#F87B1B";
								return "#ef4444";
							};
							const getSessionsColor = (avg) => {
								if (avg >= 3) return "#4ade80";
								if (avg >= 2) return "#facc15";
								if (avg >= 1) return "#F87B1B";
								return "#ef4444";
							};
							const getDaysColor = (days) => {
								if (days <= 3) return "#27ae60";
								if (days <= 6) return "#f39c12";
								return "#e74c3c";
							};
							const calculateStreak = (weeklySessions) => {
								if (!weeklySessions || weeklySessions.length === 0) return 0;
								let streak = 0;
								for (let i = weeklySessions.length - 1; i >= 0; i--) {
									if (weeklySessions[i] > 0) streak++;
									else break;
								}
								return streak;
							};
							const getRegularityTrend = (weeklySessions) => {
								if (!weeklySessions || weeklySessions.length < 12) return null;
								const first6 = weeklySessions.slice(0, 6);
								const last6 = weeklySessions.slice(-6);
								const avg6first = first6.reduce((a, b) => a + b, 0) / 6;
								const avg6last = last6.reduce((a, b) => a + b, 0) / 6;
								if (avg6last > avg6first + 0.2) return { label: "En hausse", arrow: "↑", color: "#4ade80" };
								if (avg6last < avg6first - 0.2) return { label: "En baisse", arrow: "↓", color: "#ef4444" };
								return { label: "Stable", arrow: "→", color: "rgba(255,255,255,0.45)" };
							};

							const dir = scoresSortAsc ? 1 : -1;
							const baseScores = overviewFilterStudent
								? studentScores.filter(s => s.id === overviewFilterStudent)
								: studentScores;
							const sorted = [...baseScores].sort((a, b) => {
								if (scoresSortBy === "name") return dir * (a.prenom + a.nom).localeCompare(b.prenom + b.nom);
								if (scoresSortBy === "regularity") return dir * (a.regularityScore - b.regularityScore);
								if (scoresSortBy === "progression") return dir * ((a.progressionScore < 0 ? -999 : a.progressionScore) - (b.progressionScore < 0 ? -999 : b.progressionScore));
								if (scoresSortBy === "regression") return dir * ((a.regressionScore < 0 ? -999 : a.regressionScore) - (b.regressionScore < 0 ? -999 : b.regressionScore));
								if (scoresSortBy === "lastTraining") return dir * ((b.daysSinceLastTraining ?? 999) - (a.daysSinceLastTraining ?? 999));
								if (scoresSortBy === "sessions") return dir * ((a.avgSessions ?? 0) - (b.avgSessions ?? 0));
								if (scoresSortBy === "trainings") return dir * ((a.totalTrainings ?? 0) - (b.totalTrainings ?? 0));
								return 0;
							});

							return (
								<div className="scores-section">
									<div className="scores-header">
										<h2>Suivi des élèves</h2>
										<div className="scores-sort">
											<span>Trier :</span>
											{[
												{ key: "name", label: "Nom" },
												{ key: "trainings", label: "Entraînements" },
												{ key: "sessions", label: "Séances/sem" },
												{ key: "regularity", label: "Régularité" },
												{ key: "progression", label: "Progression" },
												{ key: "regression", label: "Régression" },
												{ key: "lastTraining", label: "Dernier entr." }
											].map(opt => (
												<button
													key={opt.key}
													className={`scores-sort-btn ${scoresSortBy === opt.key ? "active" : ""}`}
													onClick={() => { if (scoresSortBy === opt.key) { setScoresSortAsc(!scoresSortAsc); } else { setScoresSortBy(opt.key); setScoresSortAsc(true); } }}
												>{opt.label}{scoresSortBy === opt.key ? (scoresSortAsc ? " ▲" : " ▼") : ""}</button>
											))}
										</div>
									</div>
									<div className="student-cards-grid">
										{sorted.map(s => {
											const streak = calculateStreak(s.weeklySessions);
											const trend = getRegularityTrend(s.weeklySessions);
											const weekly = s.weeklySessions || [];
											const maxW = Math.max(...weekly, 1);
											const photoProfil = (students.find(st => st.id === s.id) || {}).photoProfil;
											const isInactive = (s.daysSinceLastTraining ?? 999) > 7;
											return (
												<div
													key={s.id}
													className={`student-card ${isInactive ? "student-card--inactive" : ""}`}
													onClick={() => {
														const student =
															students.find(st => st.id === s.id) ||
															(user && user.id === s.id ? { id: user.id, prenom: user.prenom, nom: user.nom } : null);
														if (student) { setSelectedStudent(student); setActiveTab("statistiques"); }
													}}
												>
													{/* Header: avatar + name */}
													<div className="student-card-header">
														<Avatar prenom={s.prenom} nom={s.nom} photo={photoProfil} size={40} className="student-card-avatar" />
														<div className="student-card-identity">
															<div className="student-card-name">{s.prenom} {s.nom}</div>
															<div className="student-card-sub">{s.totalTrainings || 0} entraînement{(s.totalTrainings || 0) !== 1 ? "s" : ""}</div>
														</div>
													</div>

													{/* Streak */}
													<div className="student-card-streak">
														<span className="student-card-streak-icon">🔥</span>
														<span className="student-card-streak-value">{streak}</span>
														<span className="student-card-streak-label">sem. consécutive{streak !== 1 ? "s" : ""}</span>
													</div>

													{/* Badge dernier entraînement — clamp à 0 (jamais négatif si séance future planifiée) */}
													<div className="student-card-last">
														{s.daysSinceLastTraining != null ? (() => {
															const days = Math.max(0, s.daysSinceLastTraining);
															return (
																<span className="student-card-last-badge" style={{ background: getDaysColor(days) }}>
																	{days === 0 ? "Aujourd'hui" : `Il y a ${days} j`}
																</span>
															);
														})() : (
															<span className="student-card-last-badge" style={{ background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.45)" }}>Aucune séance</span>
														)}
													</div>

													{/* Sparkline */}
													{weekly.length > 0 && (
														<div className="student-card-sparkline-wrap">
															<div className="student-card-sparkline-label">13 dernières semaines</div>
															<div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 24 }}>
																{weekly.map((v, i) => (
																	<div key={i} style={{
																		width: 6,
																		height: Math.max(2, (v / maxW) * 24),
																		backgroundColor: v > 0 ? "#30a49a" : "rgba(255,255,255,0.1)",
																		borderRadius: 2,
																		flexShrink: 0
																	}} />
																))}
															</div>
														</div>
													)}

													{/* Régularité */}
													<div className="student-card-metric-row">
														<span className="student-card-metric-label">Régularité</span>
														<div className="student-card-regularity">
															<div className="student-card-regularity-bar">
																<div className="student-card-regularity-fill" style={{ width: `${s.regularityScore}%`, background: getRegularityColor(s.regularityScore) }} />
															</div>
															<span className="student-card-regularity-score" style={{ color: getRegularityColor(s.regularityScore) }}>{s.regularityScore}</span>
															{trend && (
																<span className="student-card-trend" style={{ color: trend.color }}>{trend.arrow} {trend.label}</span>
															)}
														</div>
													</div>

													{/* Séances/sem */}
													<div className="student-card-metric-row">
														<span className="student-card-metric-label">Séances/sem</span>
														<span className="student-card-metric-value" style={{ color: getSessionsColor(s.avgSessions ?? 0) }}>{(s.avgSessions ?? 0).toFixed(1)}</span>
													</div>

													{/* Progression */}
													<div className="student-card-metric-row">
														<span className="student-card-metric-label">Progression</span>
														<div className="student-card-regularity">
															<div className="student-card-regularity-bar">
																{s.progressionScore >= 0 && (
																	<div className="student-card-regularity-fill" style={{ width: `${s.progressionScore}%`, background: getProgressionColor(s.progressionScore) }} />
																)}
															</div>
															<span className="student-card-regularity-score" style={{ color: getProgressionColor(s.progressionScore) }}>
																{s.progressionScore >= 0 ? s.progressionScore + "%" : "—"}
															</span>
															{s.progressionScore >= 0 && (
																<span className="student-card-metric-detail">{s.progressedSets}/{s.setsAnalyzed}</span>
															)}
														</div>
													</div>

													{/* Régression */}
													<div className="student-card-metric-row">
														<span className="student-card-metric-label">Régression</span>
														<div className="student-card-regularity">
															<div className="student-card-regularity-bar">
																{s.regressionScore >= 0 && (
																	<div className="student-card-regularity-fill" style={{ width: `${s.regressionScore}%`, background: getRegressionColor(s.regressionScore) }} />
																)}
															</div>
															<span className="student-card-regularity-score" style={{ color: getRegressionColor(s.regressionScore) }}>
																{s.regressionScore >= 0 ? s.regressionScore + "%" : "—"}
															</span>
															{s.regressionScore >= 0 && (
																<span className="student-card-metric-detail">{s.regressedSets}/{s.regressionSetsAnalyzed}</span>
															)}
														</div>
													</div>

												</div>
											);
										})}
									</div>
								</div>
							);
						})()}
						{studentScores && studentScores.length === 0 && (
							<div className="stats-empty">Aucun élève</div>
						)}
					</>
				)}
			</div>
			)}

			{/* ===== TAB: Rapport hebdo ===== */}
			{activeTab === "rapport-hebdo" && (
			<div className="rapport-hebdo-content" style={{ padding: "24px" }}>
				{/* Navigation semaines */}
				<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
					<button className="stats-period-btn" onClick={() => setWeekOffset(w => w + 1)}>← Semaine préc.</button>
					<span style={{ color: "var(--ink)", fontSize: "14px", fontWeight: 600, flex: 1, textAlign: "center" }}>
						{weeklyReport ? weeklyReport.weekLabel : weeklyReportLoading ? "Chargement…" : (() => {
							const d = new Date();
							d.setDate(d.getDate() - d.getDay() + 1 - weekOffset * 7);
							const lundi = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
							d.setDate(d.getDate() + 6);
							const dimanche = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
							return `${lundi} – ${dimanche}`;
						})()}
					</span>
					<button className="stats-period-btn" onClick={() => setWeekOffset(w => Math.max(0, w - 1))} disabled={weekOffset === 0}>
						Semaine suiv. →
					</button>
				</div>

				{weeklyReportLoading ? (
					<WeeklyReportSkeleton />
				) : !weeklyReport ? (
					<div style={{ color: "var(--ink-soft)", textAlign: "center", padding: "40px" }}>
						<div>Impossible de charger le rapport hebdo.</div>
						{weeklyReportError && (
							<div style={{ marginTop: 8, fontSize: "0.8rem", color: "var(--ink-faint)" }}>
								Erreur : {weeklyReportError}
							</div>
						)}
						<button
							className="stats-period-btn"
							onClick={() => {
								setWeeklyReportLoading(true);
								setWeeklyReportError(null);
								getWeeklyReport(weekOffset).then(res => {
									if (res.success) { setWeeklyReport(res.data); setWeeklyReportError(null); }
									else { setWeeklyReport(null); setWeeklyReportError(res.error || "Échec de chargement"); }
									setWeeklyReportLoading(false);
								});
							}}
							style={{ marginTop: 14 }}
						>
							Réessayer
						</button>
					</div>
				) : (
					<>
						{/* 4 métriques (avec tendance vs semaine précédente) */}
						<div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "24px" }}>
							{[
								{ label: "Séances totales", value: weeklyReport.totalSessions, cur: weeklyReport.totalSessions, prev: weeklyReport.prevTotalSessions, sub: `${weeklyReport.studentsActive} élève${weeklyReport.studentsActive !== 1 ? "s" : ""} actif${weeklyReport.studentsActive !== 1 ? "s" : ""}` },
								{ label: "Objectifs atteints", value: `${weeklyReport.studentsMetGoalCount ?? 0}/${weeklyReport.studentsWithGoalCount ?? 0}`, cur: weeklyReport.studentsMetGoalCount ?? 0, prev: weeklyReport.prevStudentsMetGoalCount, sub: "élèves" },
								{ label: "Nouveaux PRs", value: (weeklyReport.newPRs || []).length, cur: (weeklyReport.newPRs || []).length, prev: weeklyReport.prevNewPRsCount, sub: "records" },
								{ label: "Check-ins reçus", value: (weeklyReport.checkIns || []).length, cur: (weeklyReport.checkIns || []).length, prev: weeklyReport.prevCheckInsCount, sub: "bilans" },
							].map((m, i) => (
								<div key={i} style={{ background: "var(--dark-grad)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(236,236,239,0.08)", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
									<div style={{ color: "white", fontSize: "28px", fontWeight: "bold" }}>{m.value}</div>
									<div style={{ color: "rgba(255,255,255,0.6)", fontSize: "11px", marginTop: "4px" }}>{m.label}</div>
									<div style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}>{m.sub}</div>
									{m.prev != null && (() => {
										const d = m.cur - m.prev;
										const up = d > 0, down = d < 0;
										const color = up ? "#27ae60" : down ? "#e74c3c" : "rgba(255,255,255,0.35)";
										return (
											<div style={{ color, fontSize: "11px", fontWeight: 600, marginTop: 6 }}>
												{up ? "↑" : down ? "↓" : "→"} {d > 0 ? "+" : ""}{d}
												<span style={{ opacity: 0.55, fontWeight: 400 }}> vs S-1</span>
											</div>
										);
									})()}
								</div>
							))}
						</div>

						<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", gridAutoRows: "1fr" }}>

						{/* À surveiller — inactivité / douleur / régression, avec actions rapides */}
						{(weeklyReport.studentsToWatch || []).length > 0 && (
							<div style={{ background: "linear-gradient(155deg, #2a1818, #1c0c0c)", border: "1px solid rgba(231,76,60,0.35)", borderRadius: "12px", padding: "18px", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
								<h4 style={{ color: "#e74c3c", margin: "0 0 10px" }}>⚠️ À surveiller ({weeklyReport.studentsToWatch.length})</h4>
								{weeklyReport.studentsToWatch.map((w, i) => {
									const chip = (c) => ({ background: `${c}22`, color: c, border: `1px solid ${c}55`, borderRadius: 999, padding: "2px 9px", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap" });
									const full = students.find((s) => s.id === w.id) || { id: w.id, prenom: w.prenom, nom: w.nom };
									return (
										<div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" }}>
											<span style={{ color: "#fff", fontSize: "13px", fontWeight: 500, minWidth: 110 }}>{w.prenom} {w.nom}</span>
											<div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
												{w.inactiveDays != null && (
													<span style={chip("#e74c3c")}>Inactif · {w.inactiveDays >= 999 ? "jamais" : `${w.inactiveDays} j`}</span>
												)}
												{w.painZone && (
													<span style={chip("#f39c12")}>Douleur · {w.painZone}</span>
												)}
												{w.regressionPct != null && (
													<span style={chip("#e67e22")}>Régression · {w.regressionPct}%</span>
												)}
											</div>
											<button className="alert-action-btn" onClick={() => handleStudentMessage(full)}>Message</button>
											<button className="alert-action-btn" onClick={() => handleSelectStudentFromSidebar(full)}>Voir</button>
										</div>
									);
								})}
							</div>
						)}

						{/* Top progression */}
						{weeklyReport.topProgression && (
							<div style={{ background: "linear-gradient(155deg, #15291f, #0b1a12)", border: "1px solid rgba(39,174,96,0.35)", borderRadius: "12px", padding: "18px", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
								<h4 style={{ color: "#27ae60", margin: "0 0 6px" }}>🏆 Top progression</h4>
								<div style={{ color: "white", fontSize: "14px" }}>
									{weeklyReport.topProgression.studentName} — <strong>{weeklyReport.topProgression.progressionPct}%</strong> de séries en progression
								</div>
							</div>
						)}

						{/* Objectifs */}
						{(weeklyReport.studentsMetGoal || []).length > 0 && (
							<div style={{ background: "var(--dark-grad)", borderRadius: "12px", padding: "18px", border: "1px solid rgba(236,236,239,0.08)", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
								<h4 style={{ color: "white", margin: "0 0 10px" }}>🎯 Objectifs hebdo</h4>
								{weeklyReport.studentsMetGoal.map((s, i) => (
									<div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "13px" }}>
										<span style={{ color: "rgba(255,255,255,0.8)" }}>{s.prenom} {s.nom}</span>
										<span style={{ color: s.sessions >= s.goal ? "#27ae60" : "#e74c3c" }}>
											{s.sessions}/{s.goal} séance{s.goal > 1 ? "s" : ""} {s.sessions >= s.goal ? "✓" : "✗"}
										</span>
									</div>
								))}
							</div>
						)}

						{/* PRs */}
						{(weeklyReport.newPRs || []).length > 0 && (
							<div style={{ background: "var(--dark-grad)", borderRadius: "12px", padding: "18px", border: "1px solid rgba(236,236,239,0.08)", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
								<h4 style={{ color: "white", margin: "0 0 10px" }}>🥇 PRs de la semaine</h4>
								{weeklyReport.newPRs.map((pr, i) => (
									<div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "13px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
										<span style={{ color: "rgba(255,255,255,0.7)" }}>{pr.studentName}</span>
										<span style={{ color: "white" }}>{pr.exerciseName}</span>
										<span style={{ color: "#f39c12" }}>{pr.poids}kg × {pr.reps}</span>
									</div>
								))}
							</div>
						)}

						{/* Notes de séance */}
						{(weeklyReport.sessionRatings || []).length > 0 && (
							<div style={{ background: "var(--dark-grad)", borderRadius: "12px", padding: "18px", border: "1px solid rgba(236,236,239,0.08)", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
								<h4 style={{ color: "white", margin: "0 0 10px" }}>⭐ Notes de séance</h4>
								{weeklyReport.sessionRatings.map((r, i) => (
									<div key={i} style={{ padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
										<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
											<span style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>{r.studentName}</span>
											<span style={{ display: "flex", gap: 2 }}>
													{[1,2,3,4,5].map(n => (
														<span key={n} style={{ fontSize: "13px", color: n <= (r.note || 0) ? "#f59e0b" : "rgba(255,255,255,0.15)" }}>★</span>
													))}
												</span>
										</div>
										{r.retour && <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", fontStyle: "italic" }}>"{r.retour}"</div>}
									</div>
								))}
							</div>
						)}

						{/* Check-ins */}
						{(weeklyReport.checkIns || []).length > 0 && (
							<div style={{ background: "var(--dark-grad)", borderRadius: "12px", padding: "18px", border: "1px solid rgba(236,236,239,0.08)", boxShadow: "0 8px 20px -14px rgba(20,30,27,0.35)" }}>
								<h4 style={{ color: "white", margin: "0 0 10px" }}>📋 Check-ins reçus</h4>
								{weeklyReport.checkIns.map((c, i) => {
									const energyEmoji = ["😴","😟","😐","😊","⚡"][c.niveauEnergie - 1] || "—";
									return (
										<div key={i} style={{ padding: "10px", background: "rgba(236,236,239,0.06)", borderRadius: "8px", marginBottom: "8px" }}>
											<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
												<span style={{ color: "white", fontSize: "13px", fontWeight: "500" }}>{c.studentName}</span>
												<span style={{ fontSize: "18px" }}>{energyEmoji}</span>
											</div>
											{c.douleurs === 1 && <div style={{ color: "#e74c3c", fontSize: "12px", marginBottom: "4px" }}>⚠️ Douleur : {c.zoneDouleur || "non précisée"}</div>}
											{c.noteLibre && <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", fontStyle: "italic" }}>"{c.noteLibre}"</div>}
										</div>
									);
								})}
							</div>
						)}

						</div>
					</>
				)}
			</div>
			)}

					</div>
				</div>
			</div>

			<CoachSearchModal
				visible={showSearch}
				onClose={() => setShowSearch(false)}
				students={students}
				user={user}
				exercises={exercises}
				coachTemplates={coachTemplates}
				onSelectStudent={(s) => handleSelectStudentFromSidebar(s)}
				onSelectExercise={(ex) => { setSelectedExercise(ex); }}
				onSelectTemplate={() => setView("templates")}
			/>

			{/* Feature 6 — Broadcast Modal */}
			{showBroadcast && (
				<div className="modal-overlay" onClick={() => setShowBroadcast(false)}>
					<div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: "500px" }}>
						<button className="modal-close" onClick={() => setShowBroadcast(false)}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">📢 Message à tous les élèves</h2>
							<div className="modal-divider"></div>
							<textarea
								value={broadcastMessage}
								onChange={e => setBroadcastMessage(e.target.value)}
								placeholder="Votre message..."
								style={{
									width: "100%", minHeight: "100px", background: "rgba(255,255,255,0.05)",
									border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
									color: "white", padding: "10px", fontSize: "14px", resize: "vertical",
									boxSizing: "border-box", marginBottom: "16px"
								}}
							/>
							<div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
								<button className="modal-btn modal-btn-secondary" onClick={() => setShowBroadcast(false)}>Annuler</button>
								<button className="modal-btn" onClick={handleBroadcastSend} disabled={!broadcastMessage.trim()}>
									Envoyer à {(studentScores || []).length} élève{(studentScores || []).length > 1 ? "s" : ""}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Planification Modal */}
			{showPlanification && (
				<div
					className="plan-modal-overlay"
					onClick={closePlanification}
					onKeyDown={(e) => { if (e.key === "Escape") closePlanification(); }}
				>
					<div className="plan-modal" onClick={(e) => e.stopPropagation()}>
						<header className="plan-modal-header">
							<div className="plan-modal-header-text">
								<div className="plan-modal-eyebrow">
									<span className="plan-modal-eyebrow-dot" />
									Planification récurrente
								</div>
								<h2 className="plan-modal-title">Programmer des séances</h2>
							</div>
							<button className="plan-modal-close" onClick={closePlanification} aria-label="Fermer">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<line x1="18" y1="6" x2="6" y2="18" />
									<line x1="6" y1="6" x2="18" y2="18" />
								</svg>
							</button>
						</header>

						<div className="plan-modal-body">
							{/* Step 1 — Template */}
							<section className="plan-modal-section">
								<div className="plan-modal-section-head">
									<span className="plan-modal-step-num">1</span>
									<h3 className="plan-modal-section-title">Choisir un template</h3>
								</div>

								{!isSelf && (
									<div className="plan-modal-source">
										<button
											className={`plan-modal-source-tab ${planTemplateSource === "coach" ? "active" : ""}`}
											onClick={() => setPlanTemplateSource("coach")}
										>
											Mes templates
										</button>
										<button
											className={`plan-modal-source-tab ${planTemplateSource === "student" ? "active" : ""}`}
											onClick={() => setPlanTemplateSource("student")}
										>
											Templates élève
										</button>
									</div>
								)}

								<div className="plan-modal-template-list">
									{(() => {
										const list = planTemplateSource === "student" ? templates : coachTemplates;
										if (!list || list.length === 0) {
											return <div className="plan-modal-empty">Aucun template disponible</div>;
										}
										return list.map((t) => {
											const tplId = t.Id ?? t.id;
											const tplNom = t.Nom ?? t.nom ?? "Sans nom";
											const tplExos = t.ListExercice ?? t.listExercice ?? t.exercices ?? [];
											const selectedId = planSelectedTemplate ? (planSelectedTemplate.Id ?? planSelectedTemplate.id) : null;
											const isSelected = selectedId === tplId;
											return (
												<button
													key={tplId}
													className={`plan-modal-template-item ${isSelected ? "selected" : ""}`}
													onClick={() => setPlanSelectedTemplate(t)}
												>
													<div className="plan-modal-template-name">{tplNom}</div>
													{tplExos.length > 0 && (
														<div className="plan-modal-template-meta">
															{tplExos.length} exo{tplExos.length > 1 ? "s" : ""}
														</div>
													)}
													{isSelected && (
														<svg className="plan-modal-template-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
															<polyline points="20 6 9 17 4 12" />
														</svg>
													)}
												</button>
											);
										});
									})()}
								</div>
							</section>

							{/* Step 2 — Schedule */}
							<section className="plan-modal-section">
								<div className="plan-modal-section-head">
									<span className="plan-modal-step-num">2</span>
									<h3 className="plan-modal-section-title">Configurer la récurrence</h3>
								</div>

								<div className="plan-modal-grid">
									<label className="plan-modal-field">
										<span className="plan-modal-field-label">Date de début</span>
										<input
											type="date"
											className="plan-modal-input"
											value={planStartDate}
											onChange={(e) => setPlanStartDate(e.target.value)}
										/>
									</label>

									<label className="plan-modal-field">
										<span className="plan-modal-field-label">Nombre de semaines</span>
										<input
											type="number"
											className="plan-modal-input"
											min={1}
											max={52}
											value={planWeeks}
											onChange={(e) => setPlanWeeks(parseInt(e.target.value) || 1)}
										/>
									</label>
								</div>

								<div className="plan-modal-field">
									<span className="plan-modal-field-label">Jours de la semaine</span>
									<div className="plan-modal-days">
										{DAY_NAMES.map((name, i) => (
											<button
												key={i}
												type="button"
												className={`plan-modal-day ${planDays[i] ? "active" : ""}`}
												onClick={() => {
													const newDays = [...planDays];
													newDays[i] = !newDays[i];
													setPlanDays(newDays);
												}}
											>
												{name}
											</button>
										))}
									</div>
								</div>

								{planTemplateSource === "coach" && !isSelf && (
									<label className="plan-modal-check">
										<input
											type="checkbox"
											checked={planSaveAsTemplate}
											onChange={(e) => setPlanSaveAsTemplate(e.target.checked)}
										/>
										<span>Sauvegarder aussi comme template de l'élève</span>
									</label>
								)}
							</section>
						</div>

						<footer className="plan-modal-footer">
							<div className="plan-modal-preview">
								{(() => {
									const count = getPlanDates().length;
									if (count === 0) return <span className="plan-modal-preview-muted">Sélectionne un template et des jours</span>;
									return (
										<>
											<span className="plan-modal-preview-num">{count}</span>
											<span className="plan-modal-preview-label">séance{count > 1 ? "s" : ""} à créer</span>
										</>
									);
								})()}
							</div>
							<div className="plan-modal-actions">
								<button className="plan-modal-btn" onClick={closePlanification}>
									Annuler
								</button>
								<button
									className="plan-modal-btn primary"
									onClick={confirmPlanification}
									disabled={!planSelectedTemplate || getPlanDates().length === 0 || planLoading}
								>
									{planLoading ? "Planification…" : "Planifier"}
								</button>
							</div>
						</footer>
					</div>
				</div>
			)}

			{/* Exercise Modal */}
			{selectedExercise && (
				<div className="modal-overlay" onClick={closeExerciseModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeExerciseModal}>&times;</button>
						<div className="modal-inner">
							{exerciseEditMode ? (
								<>
									<h2 className="modal-title">Modifier l'exercice</h2>
									<div className="modal-divider"></div>
									<div className="exercise-edit-form">
										<div className="exercise-edit-field">
											<label className="exercise-edit-label">Nom</label>
											<input
												type="text"
												className="exercise-edit-input"
												value={editingExerciseName}
												onChange={(e) => setEditingExerciseName(e.target.value)}
												placeholder="Nom de l'exercice"
											/>
										</div>
										<div className="exercise-edit-field">
											<label className="exercise-edit-label">Description</label>
											<textarea
												className="exercise-edit-textarea"
												value={editingExerciseDescription}
												onChange={(e) => setEditingExerciseDescription(e.target.value)}
												placeholder="Description de l'exercice"
												rows={4}
											/>
										</div>
										<div className="exercise-edit-field">
											<label className="exercise-edit-label">Muscles</label>
											<div className="exercise-edit-muscles">
												{MUSCLE_LABELS.map((muscle, index) => (
													<div
														key={index}
														className={`exercise-edit-muscle-chip ${editingExerciseMuscles[index] === 1 ? 'selected' : ''}`}
														onClick={() => toggleMuscle(index)}
													>
														{muscle}
													</div>
												))}
											</div>
										</div>
										<RestTimeField
											value={editingExerciseTempsRepos}
											onChange={setEditingExerciseTempsRepos}
										/>
									</div>
									<div className="exercise-edit-buttons">
										<button
											className="modal-btn modal-btn-secondary"
											onClick={cancelEditExercise}
											disabled={savingExercise}
										>
											Annuler
										</button>
										<button
											className="modal-btn"
											onClick={saveExerciseChanges}
											disabled={savingExercise || !editingExerciseName.trim()}
										>
											{savingExercise ? "Enregistrement..." : "Enregistrer"}
										</button>
									</div>
								</>
							) : (
								<>
									<h2 className="modal-title">{selectedExercise.nomExercice}</h2>
									{getMuscleNames(selectedExercise.muscles).length > 0 && (
										<div className="modal-muscles">
											{getMuscleNames(selectedExercise.muscles).map((muscle, index) => (
												<span key={index} className="modal-muscle-chip">{muscle}</span>
											))}
										</div>
									)}
									<div className="modal-divider"></div>
									<div className="modal-section">
										<div className="modal-section-header">
											<span className="modal-section-title">Instructions</span>
										</div>
										{selectedExercise.comExercice ? (
											<p className="modal-description">{selectedExercise.comExercice}</p>
										) : (
											<p className="modal-description modal-no-description">Aucune description</p>
										)}
									</div>
									<div className="exercise-modal-buttons">
										<button className="modal-btn modal-btn-secondary" onClick={startEditExercise}>
											Éditer
										</button>
										<button className="modal-btn" onClick={handleAddToSession}>
											Ajouter à la séance
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Date Picker Modal */}
			{showDatePicker && (
				<div className="modal-overlay" onClick={closeDatePickerModal}>
					<div className="modal-content date-picker-modal" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeDatePickerModal}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Choisir une date</h2>
							<div className="modal-divider"></div>
							<div className="date-picker-calendar">
								<div className="date-picker-header">
									<button
										className="calendar-nav-btn"
										onClick={() => setSelectedCalendarDate(new Date(selectedCalendarDate.getFullYear(), selectedCalendarDate.getMonth() - 1, 1))}
									>
										&lt;
									</button>
									<span className="calendar-month-year">
										{MONTH_NAMES[selectedCalendarDate.getMonth()]} {selectedCalendarDate.getFullYear()}
									</span>
									<button
										className="calendar-nav-btn"
										onClick={() => setSelectedCalendarDate(new Date(selectedCalendarDate.getFullYear(), selectedCalendarDate.getMonth() + 1, 1))}
									>
										&gt;
									</button>
								</div>
								<div className="calendar-weekdays">
									{DAY_NAMES.map((day) => (
										<div key={day} className="calendar-weekday">{day}</div>
									))}
								</div>
								<div className="calendar-grid">
									{(() => {
										const year = selectedCalendarDate.getFullYear();
										const month = selectedCalendarDate.getMonth();
										const daysInMonth = getDaysInMonth(year, month);
										const firstDay = getFirstDayOfMonth(year, month);
										const days = [];

										for (let i = 1; i < firstDay; i++) {
											days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
										}

										for (let day = 1; day <= daysInMonth; day++) {
											const isSelected = selectedCalendarDate.getDate() === day;
											const isToday = new Date().getDate() === day &&
												new Date().getMonth() === month &&
												new Date().getFullYear() === year;

											days.push(
												<div
													key={day}
													className={`calendar-day ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
													onClick={() => setSelectedCalendarDate(new Date(year, month, day))}
												>
													<span className="day-number">{day}</span>
												</div>
											);
										}

										return days;
									})()}
								</div>
							</div>
							<div className="date-picker-selected">
								Date sélectionnée: {selectedCalendarDate.getDate()} {MONTH_NAMES[selectedCalendarDate.getMonth()]} {selectedCalendarDate.getFullYear()}
							</div>
							<button className="modal-btn" onClick={handleDatePickerConfirm}>
								Confirmer
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Training Detail Modal */}
			{trainingDetailModal && (
				<div className="modal-overlay" onClick={() => setTrainingDetailModal(null)}>
					<div className="modal-content training-detail-modal" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={() => setTrainingDetailModal(null)}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">{trainingDetailModal.studentName}</h2>
							<div className="modal-divider"></div>
							{trainingDetailLoading ? (
								<div className="stats-loading">Chargement...</div>
							) : trainingDetailModal.trainings && trainingDetailModal.trainings.length > 0 ? (
								<div className="training-detail-all">
									{trainingDetailModal.trainings.map((training, tIdx) => (
										<div key={tIdx} className="training-detail-block">
											{trainingDetailModal.trainings.length > 1 && (
												<h3 className="training-detail-block-title">{training.nom || `Entraînement ${tIdx + 1}`}</h3>
											)}
											<div className="training-detail-content">
												{training.exercices?.map((ex, exIdx) => {
													const prevSeries = trainingDetailModal.previousPerfs?.[ex.idExercice] || null;
													return (
														<div key={exIdx} className="training-detail-exercise">
															<div className="training-detail-exercise-header">
																<ExerciseThumb exercise={exerciseById.get(ex.idExercice) || { nomExercice: ex.nom || ex.nomExercice }} size={60} />
																<span className="training-detail-exercise-name">{ex.nom || ex.nomExercice}</span>
																{prevSeries && (() => {
																	const sets = ex.series || [];
																	// Sur un exo d'assistance : moins de poids = progression.
																	const assist = isAssistanceExercise(ex);
																	let exProgressed = 0, exRegressed = 0, exTotal = 0;
																	sets.forEach((s, i) => {
																		if (i < prevSeries.length && s.reps > 0) {
																			exTotal++;
																			const cw = effectiveWeight(s.poids, s.poidsDec);
																			const pw = prevSeries[i].poids;
																			const pr = prevSeries[i].reps;
																			// Reps : plus = mieux toujours.
																			// Poids : plus = mieux, SAUF assistance où c'est l'inverse.
																			const wBetter = assist ? cw < pw : cw > pw;
																			const wEqual = cw === pw;
																			const wWorse = assist ? cw > pw : cw < pw;
																			if ((wBetter && s.reps >= pr) || ((wBetter || wEqual) && s.reps > pr)) exProgressed++;
																			else if ((wWorse && s.reps <= pr) || ((wWorse || wEqual) && s.reps < pr)) exRegressed++;
																		}
																	});
																	if (exTotal === 0) return null;
																	const progPct = Math.round(exProgressed / exTotal * 100);
																	const regrPct = Math.round(exRegressed / exTotal * 100);
																	const progColor = progPct >= 50 ? "#4ade80" : progPct >= 35 ? "#facc15" : progPct >= 20 ? "#F87B1B" : "#ef4444";
																	const regrColor = regrPct <= 10 ? "#4ade80" : regrPct <= 25 ? "#facc15" : regrPct <= 40 ? "#F87B1B" : "#ef4444";
																	return (
																		<span className="training-detail-exercise-prog" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
																			{assist && (
																				<span
																					className="assist-badge"
																					title="Exercice d'assistance : machine ou dispositif qui allège la charge (contrepoids, machine assistée…). Progresser = utiliser MOINS de poids, donc la comparaison est inversée."
																				>ASSIST</span>
																			)}
																			<span style={{ color: progColor, background: "rgba(0,0,0,0.8)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, padding: "2px 9px", fontSize: "12px", fontWeight: 700, lineHeight: 1.4 }}>Prog ↑ {progPct}%</span>
																			<span style={{ color: regrColor, background: "rgba(0,0,0,0.8)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, padding: "2px 9px", fontSize: "12px", fontWeight: 700, lineHeight: 1.4 }}>Rég ↓ {regrPct}%</span>
																		</span>
																	);
																})()}
																{!prevSeries && <span className="training-detail-exercise-prog" style={{ color: "rgba(255,255,255,0.3)" }}>—</span>}
															</div>
															<div className="training-detail-sets">
																{(() => {
																	const assist = isAssistanceExercise(ex);
																	return (ex.series || []).map((s, sIdx) => {
																	const prevSet = prevSeries?.[sIdx];
																	const comparable = prevSet && s.reps > 0;
																	const curW = effectiveWeight(s.poids, s.poidsDec);
																	const prevW = prevSet?.poids;
																	const prevR = prevSet?.reps;
																	// Assistance : moins de poids = mieux → inversion des comparaisons.
																	const wBetter = assist ? curW < prevW : curW > prevW;
																	const wEqual = curW === prevW;
																	const wWorse = assist ? curW > prevW : curW < prevW;
																	const improved = comparable && ((wBetter && s.reps >= prevR) || ((wBetter || wEqual) && s.reps > prevR));
																	const regressed = comparable && ((wWorse && s.reps <= prevR) || ((wWorse || wEqual) && s.reps < prevR));
																	return (
																		<div key={sIdx} className={`training-detail-set ${improved ? "improved" : ""} ${regressed ? "regressed" : ""}`}>
																			<span className="training-detail-set-num">S{sIdx + 1}</span>
																			<span className="training-detail-set-value">{s.reps} × {formatWeight(s.poids, s.poidsDec)}kg</span>
																			{s.rpe > 0 && <span className="training-detail-set-rpe">RPE {s.rpe}</span>}
																			{prevSet && (
																				<span className="training-detail-set-prev">
																					(préc: {prevSet.reps} × {prevSet.poids}kg)
																				</span>
																			)}
																		</div>
																	);
																});
															})()}
															</div>
														</div>
													);
												})}
												{training.comEntrainement && (
													<div className="training-detail-comment">
														<strong>Notes :</strong> {training.comEntrainement}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="stats-empty">Entraînement introuvable</div>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Template Name Modal */}
			{showTemplateNameModal && (
				<div
					className="tpl-modal-overlay"
					onClick={closeTemplateNameModal}
					onKeyDown={(e) => { if (e.key === "Escape") closeTemplateNameModal(); }}
				>
					<div
						className="tpl-modal"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => {
							if (e.key === "Escape") closeTemplateNameModal();
							if (e.key === "Enter" && templateName.trim()) handleSaveAsTemplate();
						}}
					>
						<header className="tpl-modal-header">
							<div className="tpl-modal-header-text">
								<div className="tpl-modal-eyebrow">
									<span className="tpl-modal-eyebrow-dot" />
									Sauvegarde
								</div>
								<h2 className="tpl-modal-title">Enregistrer comme template</h2>
							</div>
							<button className="tpl-modal-close" onClick={closeTemplateNameModal} aria-label="Fermer">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<line x1="18" y1="6" x2="6" y2="18" />
									<line x1="6" y1="6" x2="18" y2="18" />
								</svg>
							</button>
						</header>

						<p className="tpl-modal-hint">
							Réutilise cette séance comme modèle pour planifier d'autres séances rapidement.
						</p>

						{!isSelf && (
							<div className="tpl-modal-target">
								<div className="tpl-modal-target-label">Bibliothèque cible</div>
								<div className="tpl-modal-target-tabs">
									<button
										className={`tpl-modal-target-tab ${templateSaveTarget === "coach" ? "active" : ""}`}
										onClick={() => setTemplateSaveTarget("coach")}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
											<circle cx="12" cy="7" r="4" />
										</svg>
										<div className="tpl-modal-target-tab-text">
											<span className="tpl-modal-target-tab-name">Mes templates</span>
											<span className="tpl-modal-target-tab-meta">Réutilisable sur tous tes élèves</span>
										</div>
									</button>
									<button
										className={`tpl-modal-target-tab ${templateSaveTarget === "student" ? "active" : ""}`}
										onClick={() => setTemplateSaveTarget("student")}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
										</svg>
										<div className="tpl-modal-target-tab-text">
											<span className="tpl-modal-target-tab-name">Template de {selectedStudent?.prenom}</span>
											<span className="tpl-modal-target-tab-meta">Uniquement dans sa bibliothèque</span>
										</div>
									</button>
								</div>
							</div>
						)}

						<label className="tpl-modal-field">
							<span className="tpl-modal-field-label">Nom du template</span>
							<input
								type="text"
								className="tpl-modal-input"
								placeholder="Ex : Push — heavy"
								value={templateName}
								onChange={(e) => setTemplateName(e.target.value)}
								autoFocus
							/>
						</label>

						<footer className="tpl-modal-footer">
							<span className="tpl-modal-meta">
								<kbd>↵</kbd> pour enregistrer · <kbd>Esc</kbd> pour annuler
							</span>
							<div className="tpl-modal-actions">
								<button className="tpl-modal-btn" onClick={closeTemplateNameModal}>
									Annuler
								</button>
								<button
									className="tpl-modal-btn primary"
									onClick={handleSaveAsTemplate}
									disabled={!templateName.trim()}
								>
									Enregistrer
								</button>
							</div>
						</footer>
					</div>
				</div>
			)}

			{/* Create Exercise Modal */}
			{showCreateExerciseModal && (
				<div className="modal-overlay" onClick={closeCreateExerciseModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeCreateExerciseModal}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Nouvel exercice</h2>
							<div className="modal-divider"></div>
							<div className="exercise-edit-form">
								<div className="exercise-edit-field">
									<label className="exercise-edit-label">Nom</label>
									<input
										type="text"
										className="exercise-edit-input"
										value={newExerciseName}
										onChange={(e) => setNewExerciseName(e.target.value)}
										placeholder="Nom de l'exercice"
										autoFocus
									/>
								</div>
								<div className="exercise-edit-field">
									<label className="exercise-edit-label">Description</label>
									<textarea
										className="exercise-edit-textarea"
										value={newExerciseDescription}
										onChange={(e) => setNewExerciseDescription(e.target.value)}
										placeholder="Description de l'exercice"
										rows={4}
									/>
								</div>
								<div className="exercise-edit-field">
									<label className="exercise-edit-label">Muscles</label>
									<div className="exercise-edit-muscles">
										{MUSCLE_LABELS.map((muscle, index) => (
											<div
												key={index}
												className={`exercise-edit-muscle-chip ${newExerciseMuscles[index] === 1 ? 'selected' : ''}`}
												onClick={() => toggleNewExerciseMuscle(index)}
											>
												{muscle}
											</div>
										))}
									</div>
								</div>
								<RestTimeField
									value={newExerciseTempsRepos}
									onChange={setNewExerciseTempsRepos}
								/>
							</div>
							<div className="exercise-edit-buttons">
								<button
									className="modal-btn modal-btn-secondary"
									onClick={closeCreateExerciseModal}
									disabled={creatingExercise}
								>
									Annuler
								</button>
								<button
									className="modal-btn"
									onClick={handleCreateExercise}
									disabled={creatingExercise || !newExerciseName.trim()}
								>
									{creatingExercise ? "Création..." : "Créer"}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Training Comment Modal */}
			{showCommentModal && (
				<div
					className="note-modal-overlay"
					onClick={closeCommentModal}
					onKeyDown={(e) => {
						if (e.key === "Escape") closeCommentModal();
					}}
				>
					<div
						className="note-modal"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => {
							if (e.key === "Escape") closeCommentModal();
							if ((e.metaKey || e.ctrlKey) && e.key === "Enter") saveComment();
						}}
					>
						<header className="note-modal-header">
							<div className="note-modal-header-text">
								<div className="note-modal-eyebrow">
									<span className="note-modal-eyebrow-dot" />
									Séance
								</div>
								<h2 className="note-modal-title">Note de séance</h2>
							</div>
							<button
								className="note-modal-close"
								onClick={closeCommentModal}
								aria-label="Fermer"
							>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<line x1="18" y1="6" x2="6" y2="18" />
									<line x1="6" y1="6" x2="18" y2="18" />
								</svg>
							</button>
						</header>

						<p className="note-modal-hint">
							Cette note apparaît dans le détail de la séance et reste visible par l'élève.
						</p>

						<textarea
							className="note-modal-textarea"
							value={editingComment}
							onChange={(e) => setEditingComment(e.target.value)}
							placeholder="Observations, consignes, ressenti…"
							rows={8}
							autoFocus
						/>

						<footer className="note-modal-footer">
							<span className="note-modal-meta">
								{(editingComment || "").length} caractère{(editingComment || "").length > 1 ? "s" : ""}
								<span className="note-modal-meta-sep">·</span>
								<kbd>⌘</kbd><kbd>↵</kbd> pour enregistrer
							</span>
							<div className="note-modal-actions">
								<button className="note-modal-btn" onClick={closeCommentModal}>
									Annuler
								</button>
								<button className="note-modal-btn primary" onClick={saveComment}>
									Enregistrer la note
								</button>
							</div>
						</footer>
					</div>
				</div>
			)}
		</div>
	);
}

export default CoachMode;
